import React, { useState, useEffect, createContext, useContext, useRef, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import "./App.css";
import axios from "axios";
import Webcam from 'react-webcam';
import Quagga from 'quagga';
import { IoCodeDownloadOutline } from "react-icons/io5";
import { BrowserMultiFormatReader } from '@zxing/library';
import { TbFileTypeXml } from "react-icons/tb";
import { LuPillBottle, LuWarehouse, LuPill, LuPackageSearch, LuUpload, LuUsers, LuCircleCheckBig, LuSettings } from "react-icons/lu";
import { 
  User, LogOut, ShoppingCart, Package, Upload, Download, Edit3, X, Check, 
  Search, Plus, MoreVertical, MoreHorizontal, Eye, Trash2, Archive, Camera, Type, 
  AlertCircle, CheckCircle, Info, AlertTriangle, Clock, RefreshCw,
  ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  Home, Settings, Users, FileText, Box, Truck, Activity, BarChart3,
  Tag, Calendar, MapPin, Phone, Mail, Building, Briefcase
} from 'react-feather';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext();

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Custom hook for modal close functionality (click outside + escape key)
// Apple-style PO Completion Effect Component
const POCompletionEffect = ({ isVisible, poNumber, onComplete }) => {
  const [animationPhase, setAnimationPhase] = useState(0);
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (!isVisible) return;

    console.log('🎯 Starting completion effect animation for PO:', poNumber);

    // Generate confetti particles
    const newParticles = Array.from({ length: 60 }, (_, i) => ({
      id: i,
      x: Math.random() * window.innerWidth,
      y: -10,
      rotation: Math.random() * 360,
      color: ['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444'][Math.floor(Math.random() * 5)],
      size: Math.random() * 8 + 4,
      velocity: Math.random() * 2 + 1,
      opacity: 1
    }));
    setParticles(newParticles);

    // Animation sequence
    const timeouts = [
      setTimeout(() => setAnimationPhase(1), 100),   // Scale in background
      setTimeout(() => setAnimationPhase(2), 300),   // Show checkmark
      setTimeout(() => setAnimationPhase(3), 800),   // Show text
      setTimeout(() => setAnimationPhase(4), 1200),  // Start confetti
      setTimeout(() => setAnimationPhase(5), 4000),  // Start fade out
      setTimeout(() => {
        setAnimationPhase(0);
        onComplete();
      }, 4800) // Complete and dismiss
    ];

    return () => timeouts.forEach(clearTimeout);
  }, [isVisible, onComplete]);

  // Animate particles
  useEffect(() => {
    if (animationPhase < 4) return;

    const interval = setInterval(() => {
      setParticles(prev => prev.map(particle => ({
        ...particle,
        y: particle.y + particle.velocity * 2,
        x: particle.x + Math.sin(particle.y * 0.01) * 0.5,
        rotation: particle.rotation + 3,
        opacity: particle.y > window.innerHeight ? 0 : Math.max(0, 1 - (particle.y / window.innerHeight) * 0.8)
      })).filter(p => p.opacity > 0));
    }, 16);

    return () => clearInterval(interval);
  }, [animationPhase]);

  if (!isVisible) return null;

  // Use portal to render directly to document.body for full mobile coverage
  return createPortal(
    <div className={`fixed inset-0 z-[9999] flex items-center justify-center transition-all duration-700 ${
      animationPhase >= 5 ? 'opacity-0' : 'opacity-100'
    }`}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 9999
      }}
    >
      
      {/* Background Overlay with Blur Effect - Full Mobile Coverage */}
      <div className={`absolute inset-0 bg-black transition-all duration-900 ${
        animationPhase >= 1 ? 'bg-opacity-30 backdrop-blur-md' : 'bg-opacity-0'
      }`}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh'
        }}
      />
      
      {/* Confetti Particles */}
      {particles.map(particle => (
        <div
          key={particle.id}
          className="absolute pointer-events-none"
          style={{
            left: particle.x,
            top: particle.y,
            transform: `rotate(${particle.rotation}deg)`,
            backgroundColor: particle.color,
            width: particle.size,
            height: particle.size,
            borderRadius: '2px',
            opacity: particle.opacity,
            transition: 'opacity 0.1s'
          }}
        />
      ))}
      
      {/* Main Content Container */}
      <div className={`relative bg-white rounded-3xl shadow-2xl overflow-hidden transition-all duration-400 ${
        animationPhase >= 1 ? 'scale-100 opacity-100' : 'scale-75 opacity-0'
      } ${animationPhase >= 5 ? 'scale-110' : ''}`}
        style={{
          width: '400px',
          height: '400px'
        }}
      >
        
        {/* Radial Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-green-50 via-blue-50 to-purple-50" />
        
        {/* Success Checkmark */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className={`relative transition-all duration-800 ease-out ${
            animationPhase >= 2 ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
          }`}>
            
            {/* Pulsing Ring */}
            <div className={`absolute inset-0 rounded-full bg-green-500 transition-all duration-1000 ${
              animationPhase >= 2 ? 'scale-100 opacity-20' : 'scale-50 opacity-0'
            }`}
              style={{
                width: '120px',
                height: '120px',
                animation: animationPhase >= 2 ? 'pulse 2s infinite' : 'none'
              }}
            />
            
            {/* Main Circle */}
            <div className="relative w-24 h-24 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
              <div className={`transition-all duration-500 delay-300 ${
                animationPhase >= 2 ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
              }`}>
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={3} 
                    d="M5 13l4 4L19 7"
                    style={{
                      pathLength: 1,
                      strokeDasharray: 1,
                      strokeDashoffset: animationPhase >= 2 ? 0 : 1,
                      animation: animationPhase >= 2 ? 'drawCheckmark 0.6s ease-out 0.3s forwards' : 'none'
                    }}
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        {/* Text Content */}
        <div className={`absolute bottom-16 left-0 right-0 text-center px-8 transition-all duration-700 delay-500 ${
          animationPhase >= 3 ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
        }`}>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            PO #{poNumber} Serialized
          </h2>
          <p className="text-sm text-gray-500">
            All items successfully received
          </p>
        </div>
        
        {/* Subtle Animation Accent */}
        <div className={`absolute top-6 right-6 w-3 h-3 rounded-full bg-gradient-to-r from-green-400 to-blue-500 transition-all duration-8000 ${
          animationPhase >= 2 ? 'scale-100 opacity-60' : 'scale-0 opacity-0'
        }`}
          style={{
            animation: animationPhase >= 2 ? 'float 3s ease-in-out infinite' : 'none'
          }}
        />
        <div className={`absolute bottom-6 left-6 w-2 h-2 rounded-full bg-gradient-to-r from-purple-400 to-pink-500 transition-all duration-8000 delay-200 ${
          animationPhase >= 2 ? 'scale-100 opacity-60' : 'scale-0 opacity-0'
        }`}
          style={{
            animation: animationPhase >= 2 ? 'float 3s ease-in-out infinite 1s' : 'none'
          }}
        />
      </div>
      
      {/* Custom CSS Animations */}
      <style jsx>{`
        @keyframes drawCheckmark {
          from {
            stroke-dashoffset: 1;
          }
          to {
            stroke-dashoffset: 0;
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 0.2;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.1;
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }
      `}</style>
    </div>,
    document.body  // Render portal directly to document.body for full mobile coverage
  );
};

const useModalClose = (isOpen, onClose) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [isOpen, onClose]);

  return modalRef;
};

// Global Toast Context System
const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = (message, type = 'info', duration = 5000) => {
    // Parse error messages to prevent [object Object]
    let parsedMessage = message;
    if (typeof message === 'object') {
      if (message.detail) {
        if (Array.isArray(message.detail)) {
          parsedMessage = message.detail.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ');
        } else {
          parsedMessage = message.detail;
        }
      } else if (message.message) {
        parsedMessage = message.message;
      } else {
        parsedMessage = JSON.stringify(message);
      }
    }

    const id = Date.now() + Math.random(); // Ensure unique IDs
    const newToast = { id, message: parsedMessage, type, duration, removing: false };
    
    setToasts(prev => [...prev, newToast]);
    
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }
  };

  const removeToast = (id) => {
    // First mark toast as removing to trigger exit animation
    setToasts(prev => prev.map(toast => 
      toast.id === id ? { ...toast, removing: true } : toast
    ));
    
    // Then actually remove it after animation completes
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 300); // Match the animation duration
  };

  const clearAllToasts = () => {
    setToasts([]);
  };

  return (
    <ToastContext.Provider value={{ showToast, removeToast, clearAllToasts }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

// Toast Container Component
const ToastContainer = ({ toasts, removeToast }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[9999] space-y-3 pointer-events-none">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`toastify w-full mx-0 sm:max-w-md sm:w-auto p-4 rounded-xl shadow-xl border pointer-events-auto transform transition-all duration-500 ease-out ${
            toast.removing ? 'animate-toast-out' : 'animate-toast-in'
          } ${
            toast.type === 'success' ? 'bg-green-50 text-green-800 border-green-200' :
            toast.type === 'error' ? 'bg-red-50 text-red-800 border-red-200' :
            toast.type === 'warning' ? 'bg-yellow-50 text-yellow-800 border-yellow-200' :
            'bg-blue-50 text-blue-800 border-blue-200'
          }`}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center min-w-0 flex-1">
              {toast.type === 'success' && <CheckCircle size={20} className="mr-3 text-green-600 flex-shrink-0" />}
              {toast.type === 'error' && <AlertCircle size={20} className="mr-3 text-red-600 flex-shrink-0" />}
              {toast.type === 'warning' && <AlertTriangle size={20} className="mr-3 text-yellow-600 flex-shrink-0" />}
              {toast.type === 'info' && <Info size={20} className="mr-3 text-blue-600 flex-shrink-0" />}
              <span className="text-sm font-medium leading-5 break-words">{toast.message}</span>
            </div>
            <button
              onClick={() => removeToast(toast.id)}
              className="ml-3 flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors duration-200 p-1 -mr-1 -mt-1"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    const token = localStorage.getItem('session_token');
    const userData = localStorage.getItem('user');
    const businessData = localStorage.getItem('business');
    
    if (token && userData) {
      setUser(JSON.parse(userData));
      if (businessData) {
        setBusiness(JSON.parse(businessData));
      }
    }
    setLoading(false);
  };

  const login = (sessionToken, userData, businessData) => {
    localStorage.setItem('session_token', sessionToken);
    localStorage.setItem('user', JSON.stringify(userData));
    if (businessData) {
      localStorage.setItem('business', JSON.stringify(businessData));
      setBusiness(businessData);
    }
    setUser(userData);
  };

  const logout = async () => {
    const token = localStorage.getItem('session_token');
    if (token) {
      try {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    
    localStorage.removeItem('session_token');
    localStorage.removeItem('user');
    localStorage.removeItem('business');
    setUser(null);
    setBusiness(null);
  };

  return (
    <AuthContext.Provider value={{ user, business, login, logout, loading, setUser, setBusiness }}>
      {children}
    </AuthContext.Provider>
  );
};

// Components
const Header = () => {
  const { user, business, logout } = useAuth();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showManageUsers, setShowManageUsers] = useState(false);
  const [showManageVendors, setShowManageVendors] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <header className="bg-blue-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex flex-col items-left">
              <div className="text-md font-medium tracking-tight flex gap-1 items-center">
                <img className="mt-1" src="https://rxerp.com/wp-content/uploads/2025/03/rxerp-logo-hero-white-e1736423722482.png" width="60px" /> 
                PharmaTrace
                <label className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-white bg-opacity-50 text-slate-700 not-italic">BETA</label>
              </div>
              {business && (
                <div className="text-sm opacity-90">
                  {business.pharmacy_name} • NPI: {business.npi_number}
                </div>
              )}
            </div>
            {user && (
              <UserDropdown
                user={user}
                showUserDropdown={showUserDropdown}
                setShowUserDropdown={setShowUserDropdown}
                dropdownRef={dropdownRef}
                setShowAccountSettings={setShowAccountSettings}
                setShowManageUsers={setShowManageUsers}
                setShowManageVendors={setShowManageVendors}
                logout={logout}
                currentPage="dashboard"
              />
            )}
          </div>
        </div>
      </header>

      {/* Manage Users Modal */}
      {showManageUsers && (
        <ManageUsersModal 
          onClose={() => setShowManageUsers(false)} 
        />
      )}

      {/* Manage Vendors Modal */}
      {showManageVendors && (
        <ManageVendorsModal 
          onClose={() => setShowManageVendors(false)} 
        />
      )}

      {/* Account Settings Modal */}
      {showAccountSettings && (
        <AccountSettingsModal
          onClose={() => setShowAccountSettings(false)}
          user={user}
          business={business}
        />
      )}
    </>
  );
};

const BusinessSignup = () => {
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    pharmacy_name: '',
    npi_number: '',
    contact_email: '',
    contact_name: '',
    username: '',
    password: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [npiValidation, setNpiValidation] = useState(null);
  const [showNPIModal, setShowNPIModal] = useState(false);
  const [npiSearchResults, setNpiSearchResults] = useState([]);
  const [npiSearchQuery, setNpiSearchQuery] = useState('');
  const [npiSearchZipCode, setNpiSearchZipCode] = useState('');
  const [npiSearchLoading, setNpiSearchLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/business/signup`, formData);
      showToast('Business registration submitted for approval!', 'success');
      setNpiValidation(response.data.npi_validation);
      
      // Clear form
      setFormData({
        pharmacy_name: '',
        npi_number: '',
        contact_email: '',
        contact_name: '',
        username: '',
        password: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        zip_code: '',
        phone: ''
      });
      
    } catch (error) {
      let errorMsg = 'Registration failed';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMsg = error.response.data;
        } else if (error.response.data.detail) {
          if (Array.isArray(error.response.data.detail)) {
            const validationErrors = error.response.data.detail.map(err => 
              `${err.loc?.join('.')}: ${err.msg}`
            ).join(', ');
            errorMsg = `Validation errors: ${validationErrors}`;
          } else {
            errorMsg = error.response.data.detail;
          }
        } else if (error.response.data.message) {
          errorMsg = error.response.data.message;
        } else {
          errorMsg = JSON.stringify(error.response.data);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      showToast(errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleNPISearch = async (query, zipCode = '') => {
    if (!query || query.length < 3) {
      setNpiSearchResults([]);
      return;
    }

    setNpiSearchLoading(true);
    try {
      console.log('Searching for:', query, 'ZIP:', zipCode); // Debug log
      const params = { query: query, limit: 10 };
      if (zipCode && zipCode.length >= 5) {
        params.zip_code = zipCode;
      }
      
      const response = await axios.get(`${API}/npi/search`, { params });
      console.log('Search response:', response.data); // Debug log
      
      if (response.data && response.data.results && Array.isArray(response.data.results)) {
        console.log('Number of results:', response.data.results.length); // Debug log
        setNpiSearchResults(response.data.results);
      } else {
        console.log('Invalid response format:', response.data); // Debug log
        setNpiSearchResults([]);
      }
    } catch (error) {
      console.error('NPI search error:', error);
      console.error('Error response:', error.response?.data); // Debug log
      showToast('Failed to search NPI database', 'error');
      setNpiSearchResults([]);
    } finally {
      setNpiSearchLoading(false);
    }
  };

  const handleNPILookupClick = () => {
    setNpiSearchQuery('');
    setNpiSearchZipCode('');
    setNpiSearchResults([]);
    setShowNPIModal(true);
  };

  // Helper function to generate username suggestions from full name
  const generateUsernameFromName = (fullName) => {
    if (!fullName) return '';
    
    // Convert to lowercase and remove special characters except spaces
    const cleanName = fullName.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '')  // Remove all spaces
      .substring(0, 20);    // Limit to 20 characters
    
    return cleanName;
  };

  const handleNPISelect = async (npiData) => {
    // Auto-fill form with selected NPI data
    // Extract only first 5 digits from ZIP code (remove +4 extension)
    const zipCode = npiData.zip_code || '';
    const shortZipCode = zipCode.length >= 5 ? zipCode.substring(0, 5) : zipCode;
    
    // Get suggested username from the backend
    let suggestedUsername = generateUsernameFromName(npiData.authorized_official);
    
    try {
      const response = await axios.get(`${API}/username/suggest`, {
        params: { full_name: npiData.authorized_official }
      });
      if (response.data.suggested_username) {
        suggestedUsername = response.data.suggested_username;
      }
    } catch (error) {
      console.log('Username suggestion failed, using local generation:', error);
      // Fallback to local generation if API fails
    }
    
    setFormData({
      ...formData,
      npi_number: npiData.number,
      pharmacy_name: npiData.organization_name,
      contact_name: npiData.authorized_official,
      address_line1: npiData.address_line1 || '',
      address_line2: npiData.address_line2 || '',
      city: npiData.city || '',
      state: npiData.state || '',
      zip_code: shortZipCode,
      phone: npiData.phone && npiData.phone !== 'Not provided' ? npiData.phone : '',
      // Use backend-generated username suggestion 
      username: suggestedUsername,
    });
    setShowNPIModal(false);
    showToast('Form auto-filled with NPI data', 'success');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-8 sm:px-6 lg:px-8">
        <a href="/" className="text-md font-medium tracking-tight flex gap-1 items-center mx-auto px-6 py-3 bg-slate-900 rounded-full text-white">
          <img className="mt-1" src="https://rxerp.com/wp-content/uploads/2025/03/rxerp-logo-hero-white-e1736423722482.png" width="60px" />
          PharmaTrace
          <label className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-white bg-opacity-50 text-slate-700 not-italic">BETA</label>
        </a>
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Register Your Pharmacy
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Once you’ve registered, your account will need to be approved. We’ll notify you as soon as you’re approved.
        </p>
      </div>
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-2xl">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* NPI Lookup Button at Top */}
          <div className="mb-6 text-center">
            <p className="mb-4"><a href="/">Already have an account? Log in</a></p>
            <button
              type="button"
              onClick={handleNPILookupClick}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center space-x-2"
            >
              <Search />
              <span>Search NPI Registry</span>
            </button>
            <p className="mt-2 text-xs text-gray-500">
              Find your pharmacy in the national NPI database
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* NPI and Pharmacy Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  NPI Number
                </label>
                <input
                  type="text"
                  required
                  value={formData.npi_number}
                  onChange={(e) => setFormData({...formData, npi_number: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter 10-digit NPI number"
                  maxLength="10"
                  pattern="[0-9]{10}"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Pharmacy Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.pharmacy_name}
                  onChange={(e) => setFormData({...formData, pharmacy_name: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Address Section */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Pharmacy Address</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Address Line 1
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.address_line1}
                    onChange={(e) => setFormData({...formData, address_line1: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Street address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Address Line 2 (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.address_line2}
                    onChange={(e) => setFormData({...formData, address_line2: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Apartment, suite, etc."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      City
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      State
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.state}
                      onChange={(e) => setFormData({...formData, state: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="CA"
                      maxLength="2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      ZIP Code
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.zip_code}
                      onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="12345"
                      pattern="[0-9]{5}(-[0-9]{4})?"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Contact Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.contact_name}
                    onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Phone Number (Optional)
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700">
                  Contact Email
                </label>
                <input
                  type="email"
                  required
                  value={formData.contact_email}
                  onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Login Credentials */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Login Credentials</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Username (unique, for login)
                  </label>
                  <input
                    type="text"
                    required
                    minLength="3"
                    maxLength="50"
                    pattern="[a-zA-Z0-9_-]+"
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Letters, numbers, underscore, hyphen only"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Password (minimum 6 characters)
                  </label>
                  <input
                    type="password"
                    required
                    minLength="6"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 mt-8"
            >
              {loading ? 'Submitting...' : 'Register Business'}
            </button>
          </form>

          {npiValidation && (
            <div className="mt-4 p-3 bg-green-50 rounded">
              <div className="flex items-center gap-2">
                <LuCircleCheckBig className="text-green-900 w-5 h-5" />
                <h4 className="font-medium text-green-900">Registraton Successful!</h4>
              </div>
              <p className="text-green-900">You will receive an email once your account has been approved. After that, you can log in using the credentials you provided above.</p>
              
              {/* <p className="text-sm text-green-700">Organization: {npiValidation.organization_name}</p>
              <p className="text-sm text-green-700">Authorized Official: {npiValidation.authorized_official}</p> */}
            </div>
          )}
        </div>
      </div>

      {/* NPI Search Modal */}
      {showNPIModal && (
        <NPISearchModal
          searchQuery={npiSearchQuery}
          setSearchQuery={setNpiSearchQuery}
          searchZipCode={npiSearchZipCode}
          setSearchZipCode={setNpiSearchZipCode}
          searchResults={npiSearchResults}
          searchLoading={npiSearchLoading}
          onSearch={handleNPISearch}
          onSelect={handleNPISelect}
          onClose={() => setShowNPIModal(false)}
        />
      )}
    </div>
  );
};

const NPISearchModal = ({ searchQuery, setSearchQuery, searchZipCode, setSearchZipCode, searchResults, searchLoading, onSearch, onSelect, onClose }) => {
  const modalRef = useModalClose(true, onClose);
  
  const handleSearchInput = (value) => {
    setSearchQuery(value);
  };

  const handleZipCodeInput = (value) => {
    setSearchZipCode(value);
  };

  const handleSearchButton = () => {
    if (searchQuery && searchQuery.length >= 3) {
      onSearch(searchQuery, searchZipCode);
    }
  };

  const formatTaxonomies = (taxonomies) => {
    if (!taxonomies || taxonomies.length === 0) return 'General';
    return taxonomies.map(tax => tax.desc || tax.code).join(', ');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div ref={modalRef} className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-semibold text-white">Search NPI Registry</h3>
              <p className="text-blue-100 text-sm mt-1">Find pharmacy organizations by name or NPI number</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-200 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-blue-700 transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Enhanced Search Input Section */}
          <div className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
              <div className="md:col-span-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Search size={16} className="inline mr-1" />
                  Pharmacy Name or NPI Number
                </label>
                <input
                  type="text"
                  placeholder="e.g., Wellness Pharma or 1234567890"
                  value={searchQuery}
                  onChange={(e) => handleSearchInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearchButton()}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  autoFocus
                />
              </div>
              
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ZIP Code (Optional)
                </label>
                <input
                  type="text"
                  placeholder="12345"
                  value={searchZipCode}
                  onChange={(e) => handleZipCodeInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearchButton()}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  maxLength="10"
                  pattern="[0-9]{5}(-[0-9]{4})?"
                />
              </div>
              
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">&nbsp;</label>
                <button
                  onClick={handleSearchButton}
                  disabled={!searchQuery || searchQuery.length < 3 || searchLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium shadow-sm transition-colors flex items-center justify-center"
                >
                  {searchLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search size={16} className="mr-2" />
                      Search
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Enhanced Help Text */}
            {/* <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start">
                <div className="text-blue-600 mr-2 mt-0.5">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Search Tips:</p>
                  <ul className="mt-1 space-y-1 text-blue-700">
                    <li>• <strong>Partial names work:</strong> "Center" finds "Centerfield Healthcare"</li>
                    <li>• <strong>NPI numbers:</strong> Enter full 10-digit NPI or partial number</li>
                    <li>• <strong>ZIP filtering:</strong> Add ZIP code to narrow results by location</li>
                  </ul>
                </div>
              </div>
            </div> */}
          </div>

          {/* Loading State */}
          {searchLoading && (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600 font-medium">Searching NPI registry...</p>
              <p className="text-sm text-gray-500 mt-1">This may take a few seconds</p>
            </div>
          )}

          {/* Enhanced Search Results */}
          {!searchLoading && searchQuery.length >= 3 && searchResults.length > 0 && (
            <div className="space-y-1">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-medium text-gray-900">
                  Search Results ({searchResults.length} found)
                </h4>
                <div className="text-sm text-gray-500">
                  Click any result to auto-fill your registration form
                </div>
              </div>
              
              <div className="max-h-96 overflow-y-auto space-y-3 border border-gray-200 rounded-lg">
                {searchResults.map((result, index) => (
                  <div
                    key={index}
                    className="border-b border-gray-100 last:border-b-0 p-4 hover:bg-blue-50 cursor-pointer transition-all duration-200 hover:shadow-sm"
                    onClick={() => onSelect(result)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <h4 className="font-semibold text-gray-900 text-lg">{result.organization_name}</h4>
                          <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                            NPI: {result.number}
                          </span>
                        </div>
                        
                        <div className="mt-2 space-y-1">
                          <p className="text-sm text-gray-700 flex items-center">
                            <User size={14} className="mr-2 text-gray-400" />
                            <strong>Authorized Official:</strong>&nbsp;{result.authorized_official}
                          </p>
                          
                          {result.address && result.address.trim() !== "," && (
                            <p className="text-sm text-gray-700 flex items-start">
                              <MapPin size={14} className="mr-2 text-gray-400 mt-0.5 flex-shrink-0" />
                              {result.address}
                            </p>
                          )}
                          
                          {result.phone && result.phone !== "Not provided" && (
                            <p className="text-sm text-gray-700 flex items-center">
                              <Phone size={14} className="mr-2 text-gray-400" />
                              {result.phone}
                            </p>
                          )}
                          
                          <div className="flex items-center text-xs">
                            <div className="bg-green-100 text-green-800 px-2 py-1 rounded-full mr-2">
                              {formatTaxonomies(result.taxonomies)}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelect(result);
                        }}
                        className="ml-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-sm transition-colors flex items-center"
                      >
                        <Check size={14} className="mr-1" />
                        Select
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Enhanced No Results State */}
          {!searchLoading && searchQuery.length >= 3 && searchResults.length === 0 && (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <div className="text-gray-400 mb-4">
                <Search size={48} className="mx-auto" />
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">No Results Found</h4>
              <p className="text-gray-600 mb-1">No matches for "<strong>{searchQuery}</strong>"</p>
              {searchZipCode && <p className="text-gray-600">in ZIP code <strong>{searchZipCode}</strong></p>}
              
              <div className="mt-4 text-sm text-gray-500 rounded-lg p-4">
                <p className="font-medium text-gray-700 mb-2">Try these suggestions:</p>
                <ul className="text-left space-y-1 p-2">
                  <li>• Check spelling and try shorter search terms</li>
                  <li>• Remove ZIP code filter to search nationwide</li>
                  <li>• Use partial names (e.g., "Center" instead of "Centerfield")</li>
                  <li>• Search by NPI number if you have it</li>
                </ul>
              </div>
            </div>
          )}

          {/* Enhanced Instructions */}
          {searchQuery.length < 3 && (
            <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg border border-gray-200">
              <div className="text-blue-600 mb-4">
                <Search size={48} className="mx-auto" />
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">Ready to Search</h4>
              <p className="text-gray-600 mb-4">Enter at least 3 characters to find pharmacy organizations</p>
              
              {/* <div className="bg-white rounded-lg p-4 border text-left max-w-md mx-auto">
                <p className="font-medium text-gray-700 mb-2">Example searches:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• <code className="bg-gray-100 px-2 py-1 rounded text-blue-600">Centerfield</code> - Partial pharmacy name</li>
                  <li>• <code className="bg-gray-100 px-2 py-1 rounded text-blue-600">CVS</code> - Chain pharmacy</li>
                  <li>• <code className="bg-gray-100 px-2 py-1 rounded text-blue-600">1234567890</code> - Full NPI number</li>
                </ul>
              </div> */}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const LoginPage = () => {
  const { login } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [showSignup, setShowSignup] = useState(false);
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, credentials);
      login(response.data.session_token, response.data.user, response.data.business);
      showToast('Login successful!', 'success');
    } catch (error) {
      let errorMsg = 'Login failed';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMsg = error.response.data;
        } else if (error.response.data.detail) {
          errorMsg = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMsg = error.response.data.message;
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      showToast(errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (showSignup) {
    return <BusinessSignup />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">RxERP PharmaTrace</h1>
          <p className="text-blue-100 text-lg mb-8">DSCSA Compliance Management System</p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-2xl sm:rounded-lg sm:px-10">
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Welcome Back</h2>

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 text-left">
                    Username
                  </label>
                  <input
                    type="text"
                    required
                    value={credentials.username}
                    onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your username"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 text-left">
                    Password
                  </label>
                  <input
                    type="password"
                    required
                    value={credentials.password}
                    onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your password"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200 disabled:opacity-50"
                >
                  {loading ? 'Logging in...' : 'Login'}
                </button>
              </form>

              <div className="mt-6 text-center">
                <span className="text-gray-600">New pharmacy? </span>
                <button
                  onClick={() => navigate('/registration')}
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Register here
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 text-center text-blue-100 text-sm">
        <p>Streamline your Drug Supply Chain Security Act compliance</p>
        <p>Real-time NPI validation • EPCIS file processing • SSCC tracking</p>
      </div>
    </div>
  );
};


// Shared UserDropdown Component
const UserDropdown = ({ 
  user, 
  showUserDropdown, 
  setShowUserDropdown, 
  dropdownRef,
  setShowAccountSettings,
  setShowManageUsers,
  setShowManageVendors,
  logout,
  currentPage // 'dashboard' or 'epcis-filter'
}) => {
  const getUserInitials = (name) => {
    if (!name) return 'U';
    const words = name.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowUserDropdown(!showUserDropdown)}
        className="flex items-center space-x-3 bg-blue-800 hover:bg-blue-700 rounded-full p-2 transition-colors"
      >
        <div className="w-8 h-8 bg-white text-blue-900 rounded-full flex items-center justify-center font-semibold text-sm">
          {getUserInitials(user.name)}
        </div>
        <div className="hidden md:block text-sm pr-2">
          <div className="font-medium">{user.name}</div>
          <div className="text-xs opacity-80 text-left">{user.role}</div>
        </div>
        <ChevronDown size={16} className="mr-4" />
      </button>

      {/* Dropdown Menu */}
      {showUserDropdown && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
          <button
            onClick={() => {
              if (currentPage !== 'epcis-filter') {
                window.location.href = '/tools/epcis-filter';
              }
              setShowUserDropdown(false);
            }}
            className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left ${
              currentPage === 'epcis-filter' ? 'bg-gray-50' : ''
            }`}
          >
            <div className="flex items-center">
              <LuPackageSearch size={16} className="mr-3" />
              EPCIS Filter Tool
            </div>
          </button>
          {user.role === 'admin' && (
            <button
              onClick={() => {
                setShowManageUsers(true);
                setShowUserDropdown(false);
              }}
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
            >
              <div className="flex items-center">
                <LuUsers size={16} className="mr-3" />
                Manage Users
              </div>
            </button>
          )}
          <button
            onClick={() => {
              setShowManageVendors(true);
              setShowUserDropdown(false);
            }}
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
          >
            <div className="flex items-center">
              <LuWarehouse size={16} className="mr-3" />
              Manage Vendors
            </div>
          </button>
          <button
            onClick={() => {
              setShowAccountSettings(true);
              setShowUserDropdown(false);
            }}
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
          >
            <div className="flex items-center">
              <LuSettings size={16} className="mr-3" />
              Account Settings
            </div>
          </button>
          <div className="border-t border-gray-100"></div>
          <button
            onClick={() => {
              logout();
              setShowUserDropdown(false);
            }}
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
          >
            <div className="flex items-center">
              <LogOut size={16} className="mr-3" />
              Sign Out
            </div>
          </button>
        </div>
      )}
    </div>
  );
};

const Dashboard = () => {
  const { user, business } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [pos, setPOs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePO, setShowCreatePO] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);

  const [uploadingEPCIS, setUploadingEPCIS] = useState(false);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedPOs, setSelectedPOs] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);

  // Receiving workflow states
  const [showReceiving, setShowReceiving] = useState(false);
  const [receivingSession, setReceivingSession] = useState(null);
  const [receivingStatus, setReceivingStatus] = useState(null);
  const [receivingPOId, setReceivingPOId] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const [scanSuccess, setScanSuccess] = useState('');
  const [webcamKey, setWebcamKey] = useState(Date.now()); // Force webcam remount
  const webcamRef = useRef(null);

  // Helper function to get packaging summary - will use backend pre-calculated data
  const getPackagingSummary = (po) => {
    // If backend has pre-calculated summary, use it
    if (po.packaging_summary) {
      const summary = [];
      if (po.packaging_summary.sscc) summary.push({ label: 'SSCC', count: po.packaging_summary.sscc });
      if (po.packaging_summary.case) summary.push({ label: 'CASE', count: po.packaging_summary.case });
      if (po.packaging_summary.inner_case) summary.push({ label: 'INNER', count: po.packaging_summary.inner_case });
      if (po.packaging_summary.each) summary.push({ label: 'EACH', count: po.packaging_summary.each });
      
      return summary.length > 0 ? { simple: false, items: summary } : { simple: true, count: po.packaging_summary.total || 0 };
    }
    
    // Fallback for POs without pre-calculated summary (legacy)
    if (!po.epcis_parsed_data?.packaging_hierarchy) {
      return { simple: true, count: po.sscc_numbers?.length || 0 };
    }
    
    const hierarchy = po.epcis_parsed_data.packaging_hierarchy;
    const counts = {};
    
    // Count NUMBER of items at each level (not their contents)
    hierarchy.forEach(item => {
      const level = item.level || 'UNKNOWN';
      counts[level] = (counts[level] || 0) + 1;
    });
    
    // Build structured summary
    const summary = [];
    if (counts.SSCC) summary.push({ label: 'SSCC', count: counts.SSCC });
    if (counts.CASE) summary.push({ label: 'CASE', count: counts.CASE });
    if (counts.INNER_CASE) summary.push({ label: 'INNER', count: counts.INNER_CASE });
    if (counts.EACH) summary.push({ label: 'EACH', count: counts.EACH });
    
    return summary.length > 0 ? { simple: false, items: summary } : { simple: true, count: po.sscc_numbers?.length || 0 };
  };

  // Receiving workflow functions
  const startReceivingSession = async (po) => {
    try {
      console.log('Starting receiving session for PO:', po.po_number);
      
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/pos/${po.id}/start-receiving`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReceivingSession(response.data.session);
      setReceivingPOId(po.id);
      
      // Reset webcam for fresh start
      setWebcamKey(Date.now());
      
      setShowReceiving(true);
      await getReceivingStatus(po.id);
      // Remove duplicate toast - PODetailsPage will handle this
    } catch (error) {
      console.error('Error starting receiving session:', error);
      showToast(error.response?.data?.detail || 'Failed to start receiving session', 'error');
    }
  };

  const getReceivingStatus = async (poId) => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/pos/${poId}/receiving-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReceivingStatus(response.data);
    } catch (error) {
      console.error('Error getting receiving status:', error);
    }
  };

  const scanSSCC = useCallback(async () => {
    if (!webcamRef.current || !receivingSession) return;
    
    try {
      setIsScanning(true);
      setScanError('');
      setScanSuccess('');
      
      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) {
        setScanError('Failed to capture image from camera');
        setIsScanning(false);
        return;
      }
      
      console.log('Image captured successfully, trying mobile-friendly barcode detection...');
      
      // Method 1: Try ZXing library (excellent mobile support)
      try {
        const codeReader = new BrowserMultiFormatReader();
        const img = new Image();
        
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = imageSrc;
        });

        // Create canvas to process image
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const result = await codeReader.decodeFromImageData(imageData);
        
        if (result) {
          const detectedCode = result.getText();
          console.log('✅ ZXing detected barcode:', detectedCode);
          processScannedSSCC(detectedCode);
          return;
        }
      } catch (zxingError) {
        console.log('ZXing detection failed, trying Quagga...', zxingError.message);
      }

      // Method 2: Try Quagga (good mobile support)
      try {
        await new Promise((resolve, reject) => {
          Quagga.decodeSingle({
            decoder: {
              readers: ['code_128_reader', 'code_39_reader', 'ean_reader', 'ean_8_reader']
            },
            locate: true,
            src: imageSrc
          }, (result) => {
            if (result && result.codeResult) {
              console.log('✅ Quagga detected barcode:', result.codeResult.code);
              processScannedSSCC(result.codeResult.code);
              resolve(true);
            } else {
              reject(new Error('No barcode detected by Quagga'));
            }
          });
        });
        return;
      } catch (quaggaError) {
        console.log('Quagga detection failed:', quaggaError.message);
      }

      // Method 3: Native BarcodeDetector (fallback for supported browsers)
      if ('BarcodeDetector' in window) {
        try {
          console.log('Trying native BarcodeDetector API as fallback...');
          const barcodeDetector = new window.BarcodeDetector({
            formats: ['code_128', 'ean_13', 'ean_8', 'code_39']
          });
          
          const img = new Image();
          img.onload = async () => {
            try {
              const barcodes = await barcodeDetector.detect(img);
              console.log('BarcodeDetector results:', barcodes);
              
              if (barcodes.length > 0) {
                const barcode = barcodes[0];
                console.log('✅ Native BarcodeDetector detected:', barcode.rawValue, 'Format:', barcode.format);
                processScannedSSCC(barcode.rawValue);
              } else {
                console.log('No barcodes detected with BarcodeDetector');
                setScanError('Could not detect barcode. Try better lighting, closer distance, or use manual entry.');
                setIsScanning(false);
              }
            } catch (detectError) {
              console.error('BarcodeDetector detection error:', detectError);
              setScanError('Barcode detection failed: ' + detectError.message);
              setIsScanning(false);
            }
          };
          
          img.onerror = () => {
            console.error('Image load failed for BarcodeDetector');
            setScanError('Image processing failed. Try again or use manual entry.');
            setIsScanning(false);
          };
          
          img.src = imageSrc;
          return;
          
        } catch (barcodeError) {
          console.error('BarcodeDetector setup error:', barcodeError);
        }
      }
      
      // If all methods fail, show helpful error
      console.log('All barcode detection methods failed');
      setScanError('Could not detect barcode. Try better lighting, closer distance, or use manual entry below.');
      setIsScanning(false);
      
    } catch (error) {
      console.error('Main scan error:', error);
      setScanError('Scanning error: ' + error.message);
      setIsScanning(false);
    }
  }, [receivingSession]);

  const processScannedSSCC = async (scannedCode) => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/pos/${receivingPOId}/scan-sscc`, {
        scanned_code: scannedCode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Only set scan success if PO is not completing
        if (!response.data.all_complete && !response.data.session_complete) {
          setScanSuccess(response.data.message);
        }
        // Let ReceivingModal handle toast via useEffect
        
        // Refresh receiving status
        await getReceivingStatus(receivingPOId);
        
        // If all complete, refresh PO list and close modal
        if (response.data.session_complete) {
          await fetchPOs();
          setTimeout(() => {
            setShowReceiving(false);
            setReceivingSession(null);
            setReceivingStatus(null);
          }, 1000);
        }
      } else {
        setScanError(response.data.error);
        // Let ReceivingModal handle toast via useEffect
      }
    } catch (error) {
      console.error('Error processing scanned SSCC:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to process scanned SSCC';
      setScanError(errorMsg);
      // Let ReceivingModal handle toast via useEffect
    } finally {
      setIsScanning(false);
    }
  };

  const closeReceiving = () => {
    console.log('Closing receiving modal - COMPLETE RESET approach');
    
    // Stop any ongoing scanning
    setIsScanning(false);
    
    // Immediate state reset
    setShowReceiving(false);
    setReceivingSession(null);
    setReceivingStatus(null);
    setReceivingPOId(null);
    setScanError('');
    setScanSuccess('');
    
    // Force complete component remount by changing key immediately
    setWebcamKey(Date.now());
    
    console.log('Modal closed with immediate webcam key reset');
  };

  // Sorting functions
  const handleSort = (field) => {
    let newDirection = 'desc';
    if (sortField === field) {
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      newDirection = 'asc';
    }
    
    setSortField(field);
    setSortDirection(newDirection);
    setCurrentPage(1);
    
    // Use the main fetchPOs function with new parameters
    fetchPOs(1, itemsPerPage, searchQuery, field, newDirection);
  };

  // Search function - only triggers on Enter key or search button click
  const performSearch = () => {
    setSearchLoading(true);
    setCurrentPage(1);
    fetchPOs(1, itemsPerPage, searchQuery);
    setSelectedPOs([]); // Clear selections
    setShowBulkActions(false);
    setSearchLoading(false);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      performSearch();
    }
  };

  const handleSearchInputChange = (query) => {
    setSearchQuery(query);
    // Don't trigger search automatically - wait for Enter key or button click
  };

  const goToPage = (page) => {
    setCurrentPage(page);
    fetchPOs(page, itemsPerPage, searchQuery);
    setSelectedPOs([]); // Clear selections when changing pages
    setShowBulkActions(false);
  };

  const handleItemsPerPageChange = (newLimit) => {
    setItemsPerPage(newLimit);
    setCurrentPage(1);
    fetchPOs(1, newLimit, searchQuery);
    setSelectedPOs([]);
    setShowBulkActions(false);
  };

  // Selection functions
  const handleSelectPO = (poId) => {
    setSelectedPOs(prev => {
      const newSelection = prev.includes(poId) 
        ? prev.filter(id => id !== poId)
        : [...prev, poId];
      setShowBulkActions(newSelection.length > 0);
      return newSelection;
    });
  };

  const handleSelectAll = () => {
    if (selectedPOs.length === pos.length) {
      setSelectedPOs([]);
      setShowBulkActions(false);
    } else {
      setSelectedPOs(pos.map(po => po.id));
      setShowBulkActions(true);
    }
  };

  // Delete functions
  const handleDeletePO = async (poId) => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.delete(`${API}/pos/${poId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.status === 200) {
        showToast('Purchase order deleted successfully', 'success');
        fetchPOs(); // Refresh the list

      }
    } catch (error) {
      console.error('Delete PO error:', error);
      showToast(`Failed to delete purchase order: ${error.response?.data?.detail || error.message}`, 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedPOs.length === 0) return;
    
    const confirmed = window.confirm(`Are you sure you want to delete ${selectedPOs.length} purchase order(s)? This action cannot be undone.`);
    if (!confirmed) return;
    
    try {
      const token = localStorage.getItem('session_token');
      const deletePromises = selectedPOs.map(poId => 
        axios.delete(`${API}/pos/${poId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );
      
      const results = await Promise.allSettled(deletePromises);
      const successful = results.filter(result => result.status === 'fulfilled').length;
      const failed = results.length - successful;
      
      if (successful > 0) {
        showToast(`Successfully deleted ${successful} purchase order(s)${failed > 0 ? `, ${failed} failed` : ''}`, 
                  failed > 0 ? 'warning' : 'success');
        fetchPOs(); // Refresh the list
        setSelectedPOs([]);
        setShowBulkActions(false);
      } else {
        showToast('Failed to delete purchase orders', 'error');
      }
    } catch (error) {
      console.error('Bulk delete error:', error);
      showToast('Failed to delete purchase orders', 'error');
    }
  };

  const fetchPOs = async (page = currentPage, limit = itemsPerPage, search = searchQuery, sortFieldParam = sortField, sortDirectionParam = sortDirection) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('session_token');
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sort_field: sortFieldParam,
        sort_direction: sortDirectionParam
      });
      
      if (search && search.trim()) {
        params.append('search', search.trim());
      }
      
      const response = await axios.get(`${API}/pos?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPOs(response.data.items || []);
      setTotalPages(response.data.total_pages || 1);
      setTotalItems(response.data.total || 0);
      
    } catch (error) {
      console.error('Error fetching POs:', error);
      setPOs([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  // Add ref to prevent duplicate calls during component mount
  const fetchPOsCalledRef = useRef(false);

  useEffect(() => {
    if (user && !fetchPOsCalledRef.current) {
      fetchPOsCalledRef.current = true;
      fetchPOs();
    }
  }, [user]);

  const handlePOClick = (po) => {
    navigate(`/po/${po.id}`);
  };



  const handleDownloadEPCIS = async (poId) => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API}/pos/${poId}/download-epcis`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        // Extract filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        
        let filename = `PO_${poId}_EPCIS.xml`; // fallback filename
        
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, ''); // Remove quotes if present
          }
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = filename; // Use original filename
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        showToast('EPCIS file downloaded successfully', 'success');
      } else {
        showToast('Failed to download EPCIS file', 'error');
      }
    } catch (error) {
      console.error('Download error:', error);
      showToast(`Failed to download EPCIS file: ${error.message}`, 'error');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'awaiting_epcis': return 'bg-yellow-100 text-yellow-800';
      case 'ready_to_receive': return 'bg-blue-100 text-blue-800';
      case 'complete': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatStatus = (status) => {
    return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-wrap gap-6 justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Purchase Orders</h1>
          <p className="text-gray-600">Manage your DSCSA compliance workflow</p>
        </div>
        <div className="flex space-x-3">
          {user?.role === 'admin' && (
            <button
              onClick={() => setShowCreatePO(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium flex items-center space-x-2 shadow-md hover:shadow-lg"
            >
              <Plus size={16} />
              <span>Create PO</span>
            </button>
          )}
        </div>
      </div>

      {business?.status === 'pending' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle size={20} className="text-yellow-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Business Approval Pending
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>Your pharmacy registration is pending approval. You'll be notified once approved.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow-lg rounded-lg overflow-y-scroll">
        {/* Search and Filters */}
        <div className="px-6 py-4 border-b border-gray-200 bg-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1 max-w-lg">
              <label htmlFor="search" className="sr-only">Search purchase orders</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search size={20} className="text-gray-400" />
                </div>
                <input
                  id="search"
                  name="search"
                  className="block w-full pl-10 pr-12 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Search by PO number, vendor, status..."
                  // type="search"
                  value={searchQuery}
                  onChange={(e) => handleSearchInputChange(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    onClick={performSearch}
                    disabled={searchLoading}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                    title="Search"
                  >
                    <ArrowRight size={18} />
                  </button>
                </div>
                {searchQuery && (
                  <div className="absolute inset-y-0 right-8 pr-3 flex items-center">
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setCurrentPage(1);
                        fetchPOs(1, itemsPerPage, '');
                      }}
                      className="text-gray-400 hover:text-gray-600"
                      title="Clear search"
                    >
                      <span className="sr-only">Clear search</span>
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {searchQuery && (
                <span className="text-sm text-gray-600">
                  {totalItems} result{totalItems !== 1 ? 's' : ''} found
                </span>
              )}
              {/* <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Sort by:</span>
                <select
                  value={sortField}
                  onChange={(e) => handleSort(e.target.value)}
                  className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="created_at">Date Created</option>
                  <option value="po_number">PO Number</option>
                  <option value="supplier_name">Vendor</option>
                  <option value="status">Status</option>
                </select>
                <button
                  onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                  className="p-1 text-gray-400 hover:text-gray-600"
                  title={`Sort ${sortDirection === 'asc' ? 'descending' : 'ascending'}`}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {sortDirection === 'asc' ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
                    )}
                  </svg>
                </button>
              </div> */}
            </div>
          </div>
        </div>
        
        {pos.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
              <path d="M34 40h10v-4a6 6 0 00-10.712-3.714M34 40H14m20 0v-4a9.971 9.971 0 00-.712-3.714M14 40H4v-4a6 6 0 0110.713-3.714M14 40v-4c0-1.313.253-2.566.713-3.714m0 0A10.003 10.003 0 0124 26c4.21 0 7.813 2.602 9.288 6.286" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No purchase orders</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating a new purchase order.</p>
          </div>
        ) : pos.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No results found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search terms or filters.
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
                setCurrentPage(1);
                fetchPOs(1, itemsPerPage, '');
              }}
              className="mt-3 text-sm text-blue-600 hover:text-blue-500"
            >
              Clear search
            </button>
          </div>
        ) : (
          <div className="overflow-y-scroll shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            {/* Bulk Actions Bar */}
            {showBulkActions && (
              <div className="bg-blue-50 px-6 py-3 border-b border-blue-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-blue-800">
                      {selectedPOs.length} purchase order(s) selected
                    </span>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={handleBulkDelete}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H8a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete Selected
                    </button>
                    <button
                      onClick={() => {
                        setSelectedPOs([]);
                        setShowBulkActions(false);
                      }}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="relative px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      checked={selectedPOs.length === pos.length && pos.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('po_number')}
                  >
                    <div className="flex items-center">
                      PO Number
                      <svg className={`ml-1 h-4 w-4 ${sortField === 'po_number' ? 'text-gray-900' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {sortField === 'po_number' && sortDirection === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                        ) : sortField === 'po_number' && sortDirection === 'desc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                        )}
                      </svg>
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('supplier_name')}
                  >
                    <div className="flex items-center">
                      Vendor
                      <svg className={`ml-1 h-4 w-4 ${sortField === 'supplier_name' ? 'text-gray-900' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {sortField === 'supplier_name' && sortDirection === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                        ) : sortField === 'supplier_name' && sortDirection === 'desc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                        )}
                      </svg>
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      Status
                      <svg className={`ml-1 h-4 w-4 ${sortField === 'status' ? 'text-gray-900' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {sortField === 'status' && sortDirection === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                        ) : sortField === 'status' && sortDirection === 'desc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                        )}
                      </svg>
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center">
                      Created
                      <svg className={`ml-1 h-4 w-4 ${sortField === 'created_at' ? 'text-gray-900' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {sortField === 'created_at' && sortDirection === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                        ) : sortField === 'created_at' && sortDirection === 'desc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                        )}
                      </svg>
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Packaging Summary
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pos.map((po) => (
                  <tr 
                    key={po.id} 
                    className={`hover:bg-gray-50 cursor-pointer ${selectedPOs.includes(po.id) ? 'bg-blue-50' : ''}`}
                  >
                    <td className="relative px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        checked={selectedPOs.includes(po.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleSelectPO(po.id);
                        }}
                      />
                    </td>
                    <td 
                      className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                      onClick={() => handlePOClick(po)}
                    >
                      {po.po_number}
                    </td>
                    <td 
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                      onClick={() => handlePOClick(po)}
                    >
                      {po.supplier_name}
                    </td>
                    <td 
                      className="px-6 py-4 whitespace-nowrap"
                      onClick={() => handlePOClick(po)}
                    >
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(po.status)}`}>
                        {po.status === 'awaiting_epcis' ? 'Awaiting EPCIS' :
                     po.status === 'ready_to_receive' ? 'Ready to Receive' :
                     po.status === 'complete' ? 'Complete' : po.status}
                      </span>
                    </td>
                    <td 
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                      onClick={() => handlePOClick(po)}
                    >
                      {new Date(po.created_at).toLocaleDateString()}
                    </td>
                    <td 
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                      onClick={() => handlePOClick(po)}
                    >
                      <div className="flex items-center">
                        {(() => {
                          const summary = getPackagingSummary(po);
                          
                          if (summary.simple) {
                            return (
                              <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                                {summary.count.toLocaleString()}
                              </span>
                            );
                          } else {
                            return (
                              <div className="flex items-center space-x-1">
                                {summary.items.map((item, index) => (
                                  <div key={item.label} className="flex items-center">
                                    <div className="flex items-center">
                                      <span className="text-xs font-semibold px-1.5 py-0.5 rounded text-gray-600 bg-gray-100">
                                        {item.count.toLocaleString()} {item.label}
                                      </span>
                                    </div>
                                    {index < summary.items.length - 1 && (
                                      <svg className="w-3 h-3 text-gray-400 mx-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                      </svg>
                                    )}
                                  </div>
                                ))}
                              </div>
                            );
                          }
                        })()}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination Controls */}
            {pos.length > 0 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                      currentPage === 1
                        ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                        : 'text-gray-700 bg-white hover:bg-gray-50'
                    }`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                      currentPage === totalPages
                        ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                        : 'text-gray-700 bg-white hover:bg-gray-50'
                    }`}
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div className="flex items-center space-x-2">
                    <p className="text-sm text-gray-700">
                      Showing{' '}
                      <span className="font-medium">{Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}</span>
                      {' '}to{' '}
                      <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span>
                      {' '}of{' '}
                      <span className="font-medium">{totalItems}</span>
                      {' '}result{totalItems !== 1 ? 's' : ''}
                      {searchQuery && (
                        <span className="text-gray-500"> (filtered)</span>
                      )}
                    </p>
                    <div className="flex items-center space-x-2 ml-4">
                      <label htmlFor="itemsPerPage" className="text-sm text-gray-700">
                        Show:
                      </label>
                      <select
                        id="itemsPerPage"
                        value={itemsPerPage}
                        onChange={(e) => {
                          const newItemsPerPage = parseInt(e.target.value);
                          handleItemsPerPageChange(newItemsPerPage);
                        }}
                        className="w-fit block px-3 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span className="text-sm text-gray-700">per page</span>
                    </div>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${
                          currentPage === 1
                            ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                            : 'text-gray-500 bg-white hover:bg-gray-50'
                        }`}
                      >
                        <span className="sr-only">Previous</span>
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      
                      {/* Page Numbers */}
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                        // Show first page, last page, current page, and 2 pages around current
                        if (
                          pageNum === 1 ||
                          pageNum === totalPages ||
                          (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                        ) {
                          return (
                            <button
                              key={pageNum}
                              onClick={() => goToPage(pageNum)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                pageNum === currentPage
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        } else if (
                          pageNum === currentPage - 3 ||
                          pageNum === currentPage + 3
                        ) {
                          return (
                            <span key={pageNum} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                              ...
                            </span>
                          );
                        }
                        return null;
                      })}
                      
                      <button
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${
                          currentPage === totalPages
                            ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                            : 'text-gray-500 bg-white hover:bg-gray-50'
                        }`}
                      >
                        <span className="sr-only">Next</span>
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {showCreatePO && (
        <CreatePOModal 
          onClose={() => setShowCreatePO(false)} 
          onSuccess={(poId) => {
            setShowCreatePO(false);
            // Navigate directly to the created PO details page
            navigate(`/po/${poId}`);
          }}
        />
      )}

      {showAddUser && (
        <AddUserModal 
          onClose={() => setShowAddUser(false)} 
          onSuccess={() => {
            setShowAddUser(false);
          }}
        />
      )}

      {/* Receiving Modal */}
      {showReceiving && receivingPOId && (
        <ReceivingModal
          po={pos.find(p => p.id === receivingPOId)}
          receivingSession={receivingSession}
          receivingStatus={receivingStatus}
          isScanning={isScanning}
          scanError={scanError}
          scanSuccess={scanSuccess}
          webcamRef={webcamRef}
          webcamKey={webcamKey}
          onClose={closeReceiving}
          onScan={scanSSCC}
          onStartReceiving={startReceivingSession}
        />
      )}
    </div>
  );
};

const AddUserModal = ({ onClose, onSuccess }) => {
  const { showToast } = useToast();
  const modalRef = useModalClose(true, onClose);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    username: '',
    password: '',
    role: 'staff'
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/users/invite`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onSuccess();
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to add user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div ref={modalRef} className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Add Team Member</h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Full Name</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Username</label>
              <input
                type="text"
                required
                minLength="3"
                maxLength="50"
                pattern="[a-zA-Z0-9_-]+"
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Unique username for login"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <input
                type="password"
                required
                minLength="6"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="staff">Staff</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {message && (
              <div className="p-3 bg-red-100 text-red-700 rounded">
                {message}
              </div>
            )}

            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm font-medium disabled:opacity-50"
              >
                {loading ? 'Adding...' : 'Add User'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-md text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const AccountSettingsModal = ({ onClose, user, business }) => {
  const { showToast } = useToast();
  const modalRef = useModalClose(true, onClose);
  const { setUser, setBusiness } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [currentBusiness, setCurrentBusiness] = useState(business);  // Local business state
  const [fetchingBusiness, setFetchingBusiness] = useState(false);
  
  // Fetch business data for admin and staff users if not already available or incomplete
  useEffect(() => {
    const fetchBusinessData = async () => {
      // Check if business data is incomplete (missing address fields)
      const hasCompleteAddressData = currentBusiness && 
        currentBusiness.address_line1 !== undefined && 
        currentBusiness.city !== undefined && 
        currentBusiness.state !== undefined;
      
      // For admin and staff users, fetch business data if:
      // 1. No business data at all, OR
      // 2. Business data exists but is incomplete (missing address fields)
      const shouldFetch = (user?.role === 'admin' || user?.role === 'staff') && (!currentBusiness || !hasCompleteAddressData);
      
      if (shouldFetch) {
        setFetchingBusiness(true);
        try {
          const response = await axios.get(`${API}/business/profile`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('session_token')}`
            }
          });
          setCurrentBusiness(response.data.business);
        } catch (error) {
          console.error('Error fetching business data:', error);
          showToast(`Failed to load business information: ${error.response?.data?.detail || error.message}`, 'error');
        } finally {
          setFetchingBusiness(false);
        }
      }
    };

    if (user) {
      fetchBusinessData();
    }
  }, [user, currentBusiness, showToast]);
  
  // User profile form data
  const [userProfileData, setUserProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    username: user?.username || ''
  });
  
  // Business information form data - matching backend field names
  const [businessData, setBusinessData] = useState({
    business_name: '',
    npi_number: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'United States',
    phone: ''
  });

  // Update business form data when currentBusiness changes
  useEffect(() => {
    if (currentBusiness) {
      setBusinessData({
        business_name: currentBusiness.pharmacy_name || '',
        npi_number: currentBusiness.npi_number || '',
        address_line_1: currentBusiness.address_line1 || '',
        address_line_2: currentBusiness.address_line2 || '',
        city: currentBusiness.city || '',
        state: currentBusiness.state || '',
        zip_code: currentBusiness.zip_code || '',
        country: currentBusiness.country || 'United States',
        phone: currentBusiness.phone || ''
      });
    }
  }, [currentBusiness]);

  // Password form data
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const handleUserProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('session_token');
      
      // Send original email for proper comparison
      const requestData = {
        ...userProfileData,
        original_email: user?.email || '',
        original_username: user?.username || ''
      };
      
      const response = await axios.put(`${API}/user/profile`, requestData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showToast('Profile updated successfully!', 'success');
      
      // Update localStorage with new user data
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setUser(response.data.user); // Also update the current state
      }
      
      // Close modal - no need to reload since we updated the data
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to update profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBusinessSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.put(`${API}/business/profile`, businessData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showToast('Business information updated successfully!', 'success');
      
      // Update localStorage and state with new business data
      if (response.data.business) {
        const updatedBusiness = response.data.business;
        setCurrentBusiness(updatedBusiness);  // Update local business state
        localStorage.setItem('business', JSON.stringify(updatedBusiness));
        setBusiness(updatedBusiness);
      }
      
      // Close modal - no need to reload since we updated the data
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to update business information');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage('New passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('session_token');
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage('Password updated successfully!');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div ref={modalRef} className="relative top-10 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <LuSettings size={18} className="mr-2 sm:mr-3" />
            <h3 className="text-xl font-bold text-gray-900">Account Settings</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('profile')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'profile'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              User Profile
            </button>
            <button
              onClick={() => setActiveTab('business')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'business'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Business Information
            </button>
            <button
              onClick={() => setActiveTab('password')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'password'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Change Password
            </button>
          </nav>
        </div>

        {message && (
          <div className={`mb-4 p-3 rounded-md text-sm ${
            message.includes('successfully') 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message}
          </div>
        )}

        {/* User Profile Tab */}
        {activeTab === 'profile' && (
          <form onSubmit={handleUserProfileSubmit} className="space-y-6">
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h4>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={userProfileData.name}
                    onChange={(e) => setUserProfileData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                    Username
                  </label>
                  <input
                    type="text"
                    id="username"
                    value={userProfileData.username}
                    onChange={(e) => setUserProfileData(prev => ({ ...prev, username: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    pattern="^[a-zA-Z0-9_\-]+$"
                    title="Username can only contain letters, numbers, underscores, and hyphens"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Only letters, numbers, underscores, and hyphens allowed
                  </p>
                </div>
              </div>
              <div className="mt-6">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={userProfileData.email}
                  onChange={(e) => setUserProfileData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Updating...' : 'Update Profile'}
              </button>
            </div>
          </form>
        )}

        {/* Business Information Tab */}
        {activeTab === 'business' && (
          <form onSubmit={handleBusinessSubmit} className="space-y-8">
            {user.role !== 'admin' && user.role !== 'owner' && (  // Allow both admin and owner
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      Only business owners and administrators can modify business information. Contact your administrator to make changes.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Loading Business Data for Admin Users */}
            {fetchingBusiness && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
                  <p className="text-sm text-blue-700">Loading business information...</p>
                </div>
              </div>
            )}
            
            {/* Business Details Section */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Business Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Business Name *</label>
                  <input
                    type="text"
                    required
                    disabled={user.role !== 'admin' && user.role !== 'owner'}  // Allow both admin and owner
                    value={businessData.business_name}
                    onChange={(e) => setBusinessData({...businessData, business_name: e.target.value})}
                    className={`w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      user.role !== 'admin' && user.role !== 'owner' ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">NPI Number *</label>
                  <input
                    type="text"
                    required
                    disabled={user.role !== 'admin' && user.role !== 'owner'}  // Allow both admin and owner
                    value={businessData.npi_number}  // Fixed: Use npi_number consistently
                    onChange={(e) => setBusinessData({...businessData, npi_number: e.target.value})}
                    className={`w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      user.role !== 'admin' && user.role !== 'owner' ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                    }`}
                  />
                </div>
              </div>
            </div>

            {/* Primary Practice Address Section */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Primary Practice Address</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Address Line 1 *</label>
                  <input
                    type="text"
                    required
                    disabled={user.role !== 'admin' && user.role !== 'owner'}  // Allow both admin and owner
                    value={businessData.address_line_1}
                    onChange={(e) => setBusinessData({...businessData, address_line_1: e.target.value})}
                    className={`w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      user.role !== 'admin' && user.role !== 'owner' ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Address Line 2</label>
                  <input
                    type="text"
                    disabled={user.role !== 'admin' && user.role !== 'owner'}  // Allow both admin and owner
                    value={businessData.address_line_2}
                    onChange={(e) => setBusinessData({...businessData, address_line_2: e.target.value})}
                    className={`w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      user.role !== 'admin' && user.role !== 'owner' ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                    }`}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
                    <input
                      type="text"
                      required
                      disabled={user.role !== 'admin' && user.role !== 'owner'}
                      value={businessData.city}
                      onChange={(e) => setBusinessData({...businessData, city: e.target.value})}
                      className={`w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        user.role !== 'admin' && user.role !== 'owner' ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">State *</label>
                    <input
                      type="text"
                      required
                      disabled={user.role !== 'admin' && user.role !== 'owner'}
                      value={businessData.state}
                      onChange={(e) => setBusinessData({...businessData, state: e.target.value})}
                      className={`w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        user.role !== 'admin' && user.role !== 'owner' ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                      }`}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code *</label>
                    <input
                      type="text"
                      required
                      disabled={user.role !== 'admin' && user.role !== 'owner'}
                      value={businessData.zip_code}
                      onChange={(e) => setBusinessData({...businessData, zip_code: e.target.value})}
                      className={`w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        user.role !== 'admin' && user.role !== 'owner' ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Country *</label>
                    <input
                      type="text"
                      required
                      disabled={user.role !== 'admin' && user.role !== 'owner'}
                      value={businessData.country}
                      onChange={(e) => setBusinessData({...businessData, country: e.target.value})}
                      className={`w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        user.role !== 'admin' && user.role !== 'owner' ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                      }`}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                  <input
                    type="text"
                    disabled={user.role !== 'admin' && user.role !== 'owner'}
                    value={businessData.phone}
                    onChange={(e) => setBusinessData({...businessData, phone: e.target.value})}
                    className={`w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      user.role !== 'admin' && user.role !== 'owner' ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                    }`}
                  />
                </div>
              </div>
            </div>

            {(user.role === 'admin' || user.role === 'owner') && (
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {loading ? 'Updating...' : 'Update Business Information'}
                </button>
              </div>
            )}
          </form>
        )}

        {/* Password Tab */}
        {activeTab === 'password' && (
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
              <input
                type="password"
                required
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
              <input
                type="password"
                required
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
              <input
                type="password"
                required
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm font-medium disabled:opacity-50"
              >
                {loading ? 'Updating...' : 'Update Password'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-md text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const ManageUsersModal = ({ onClose }) => {
  const { showToast } = useToast();
  const modalRef = useModalClose(true, onClose);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);
  
  // Add user form data
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    username: '',
    password: '',
    role: 'staff'
  });
  const [addUserLoading, setAddUserLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setMessage('Failed to load users: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddUserLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/users/invite`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage('User added successfully!');
      setFormData({
        email: '',
        name: '',
        username: '',
        password: '',
        role: 'staff'
      });
      setShowAddUser(false);
      
      // Refresh user list
      await fetchUsers();
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Add user error:', error);
      
      // Handle different types of errors
      let errorMessage = 'Failed to add user';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle FastAPI validation errors
        if (errorData.detail && Array.isArray(errorData.detail)) {
          // Multiple validation errors
          errorMessage = errorData.detail.map(err => `${err.loc?.join(' ')}: ${err.msg}`).join(', ');
        } else if (errorData.detail && typeof errorData.detail === 'string') {
          // Simple string error
          errorMessage = errorData.detail;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setMessage(errorMessage);
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.put(`${API}/users/${userId}/role`, {
        role: newRole
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setUsers(users.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ));
      setMessage('User role updated successfully!');
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to update user role');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to remove ${userName}? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Remove from local state
      setUsers(users.filter(user => user.id !== userId));
      setMessage('User removed successfully!');
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to remove user');
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div ref={modalRef} className="relative top-10 mx-auto p-5 border w-full max-w-5xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <LuUsers size={20} className="mr-3" />
            <h3 className="text-xl font-bold text-gray-900">Manage Users</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {message && (
          <div className={`mb-4 p-3 rounded-md text-sm ${
            message.includes('successfully') 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message}
          </div>
        )}

        {/* Add User Section */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-medium text-gray-900">Team Members</h4>
            <button
              onClick={() => setShowAddUser(!showAddUser)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              {showAddUser ? 'Cancel' : 'Add User'}
            </button>
          </div>

          {/* Add User Form */}
          {showAddUser && (
            <form onSubmit={handleAddUser} className="bg-gray-50 p-4 rounded-lg mb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Full Name *</label>
                  <input
                    type="text"
                    required
                    minLength="1"
                    maxLength="100"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Username *</label>
                  <input
                    type="text"
                    required
                    minLength="3"
                    maxLength="50"
                    pattern="[a-zA-Z0-9_-]+"
                    title="Username can only contain letters, numbers, underscores, and hyphens"
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value.toLowerCase()})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">3-50 characters, letters, numbers, underscores, hyphens only</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Password *</label>
                  <input
                    type="password"
                    required
                    minLength="6"
                    maxLength="128"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role *</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="staff">Staff</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
              
              <div className="flex space-x-3 mt-4">
                <button
                  type="submit"
                  disabled={addUserLoading}
                  className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md text-sm font-medium disabled:opacity-50"
                >
                  {addUserLoading ? 'Adding...' : 'Add User'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddUser(false)}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-md text-sm font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Users Table */}
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Joined
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold text-sm mr-3">
                          {user.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">@{user.username}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={user.role}
                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                        className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="staff">Staff</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleDeleteUser(user.id, user.name)}
                        className="text-red-600 hover:text-red-900"
                        title="Remove user"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {users.length === 0 && !loading && (
              <div className="text-center py-8 text-gray-500">
                No users found. Click "Add User" to invite team members.
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end pt-4">
          <button
            onClick={onClose}
            className="bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-md text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ManageVendorsModal = ({ onClose }) => {
  const { showToast } = useToast();
  const modalRef = useModalClose(true, onClose);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  
  // Pagination and search state
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 10;
  
  // Add/Edit vendor form data
  const [formData, setFormData] = useState({
    company_name: '',
    glns: ['']
  });
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    fetchVendors();
  }, []);

  // Filter vendors based on search term
  const filteredVendors = vendors.filter(vendor =>
    vendor.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.glns.some(gln => gln.includes(searchTerm))
  );

  // Calculate pagination
  const totalPages = Math.ceil(filteredVendors.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedVendors = filteredVendors.slice(startIndex, startIndex + itemsPerPage);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/vendors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendors(response.data.vendors || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      showToast('Failed to load vendors: ' + (error.response?.data?.detail || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const validateGLN = (gln) => {
    // Remove spaces and hyphens
    const glnClean = gln.replace(/[\s-]/g, '');
    
    // Check if it's exactly 13 digits
    if (!/^\d{13}$/.test(glnClean)) {
      return false;
    }
    
    // Validate check digit using GS1 algorithm
    let total = 0;
    for (let i = 0; i < 12; i++) {
      const digit = parseInt(glnClean[i]);
      const weight = i % 2 === 1 ? 3 : 1; // Alternate between 1 and 3, starting with 1
      total += digit * weight;
    }
    
    const calculatedCheckDigit = (10 - (total % 10)) % 10;
    return calculatedCheckDigit === parseInt(glnClean[12]);
  };

  const handleAddGLNField = () => {
    setFormData(prev => ({
      ...prev,
      glns: [...prev.glns, '']
    }));
  };

  const handleRemoveGLNField = (index) => {
    setFormData(prev => ({
      ...prev,
      glns: prev.glns.filter((_, i) => i !== index)
    }));
  };

  const handleGLNChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      glns: prev.glns.map((gln, i) => i === index ? value : gln)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);

    try {
      // Validate form data
      if (!formData.company_name.trim()) {
        showToast('Company name is required', 'error');
        setSubmitLoading(false);
        return;
      }

      // Filter out empty GLNs and validate
      const validGLNs = formData.glns.filter(gln => gln.trim() !== '');
      if (validGLNs.length === 0) {
        showToast('At least one GLN is required', 'error');
        setSubmitLoading(false);
        return;
      }

      // Validate each GLN
      for (const gln of validGLNs) {
        if (!validateGLN(gln)) {
          showToast(`Invalid GLN: ${gln}. Must be 13 digits with valid check digit.`, 'error');
          setSubmitLoading(false);
          return;
        }
      }

      const token = localStorage.getItem('session_token');
      const vendorData = {
        company_name: formData.company_name.trim(),
        glns: validGLNs
      };

      if (editingVendor) {
        // Update existing vendor
        await axios.put(`${API}/vendors/${editingVendor.id}`, vendorData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showToast('Vendor updated successfully!', 'success');
      } else {
        // Create new vendor
        await axios.post(`${API}/vendors`, vendorData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showToast('Vendor created successfully!', 'success');
      }

      // Reset form and refresh list
      setFormData({ company_name: '', glns: [''] });
      setShowAddVendor(false);
      setEditingVendor(null);
      await fetchVendors();

    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to save vendor';
      showToast(errorMsg, 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (vendor) => {
    setEditingVendor(vendor);
    setFormData({
      company_name: vendor.company_name,
      glns: [...vendor.glns]
    });
    setShowAddVendor(true);
  };

  const handleDelete = async (vendorId) => {
    if (!confirm('Are you sure you want to delete this vendor? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/vendors/${vendorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showToast('Vendor deleted successfully!', 'success');
      await fetchVendors();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to delete vendor';
      showToast(errorMsg, 'error');
    }
  };

  const resetForm = () => {
    setFormData({ company_name: '', glns: [''] });
    setShowAddVendor(false);
    setEditingVendor(null);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-4 sm:p-6 border w-full max-w-4xl shadow-lg rounded-md bg-white min-h-[calc(100vh-2rem)]">
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <div className="flex items-center">
            <LuWarehouse size={18} className="mr-2 sm:mr-3" />
            <h3 className="text-lg sm:text-xl font-bold text-gray-900">Manage Vendors</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <X size={20} />
          </button>
        </div>

        {/* Add Vendor Section */}
        {!showAddVendor ? (
          <div className="mb-4 sm:mb-6">
            <button
              onClick={() => setShowAddVendor(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium flex items-center text-sm sm:text-base"
            >
              <Plus size={16} className="mr-2" />
              Add Vendor
            </button>
          </div>
        ) : (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 border border-gray-200 rounded-lg bg-gray-50">
            <div className="flex justify-between items-center mb-3 sm:mb-4">
              <h4 className="text-base sm:text-lg font-medium">
                {editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
              </h4>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X size={18} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter company name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GLNs (Global Location Numbers) *
                </label>
                <div className="text-xs text-gray-500 mb-2">
                  Each GLN must be exactly 13 digits with a valid check digit according to GS1 standards
                </div>
                {formData.glns.map((gln, index) => (
                  <div key={index} className="flex items-center space-x-2 mb-2">
                    <input
                      type="text"
                      value={gln}
                      onChange={(e) => handleGLNChange(index, e.target.value)}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter 13-digit GLN (e.g., 1234567890123)"
                      pattern="[0-9]{13}"
                      maxLength="13"
                    />
                    {formData.glns.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveGLNField(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={handleAddGLNField}
                  className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                >
                  <Plus size={16} className="mr-1" />
                  Add another GLN
                </button>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={submitLoading}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-md font-medium"
                >
                  {submitLoading ? 'Saving...' : (editingVendor ? 'Update Vendor' : 'Create Vendor')}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Vendors List */}
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <h4 className="text-base sm:text-lg font-medium">
              Vendors ({filteredVendors.length})
            </h4>
            
            {/* Search Bar */}
            <div className="relative flex-1 max-w-xs">
              <input
                type="text"
                placeholder="Search vendors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            </div>
          </div>
          
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600 text-sm">Loading vendors...</p>
            </div>
          ) : filteredVendors.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm sm:text-base">
                {searchTerm ? `No vendors found matching "${searchTerm}".` : 'No vendors found.'}
              </p>
              {!searchTerm && (
                <p className="text-xs sm:text-sm mt-1">Add your first vendor to get started.</p>
              )}
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Company Name
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        GLNs
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedVendors.map((vendor) => (
                      <tr key={vendor.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2">
                          <div className="text-sm font-medium text-gray-900">{vendor.company_name}</div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {vendor.glns.map((gln, index) => (
                              <span
                                key={index}
                                className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded font-mono"
                                title={`GLN: ${gln}`}
                              >
                                {gln}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-sm text-gray-500">
                            {new Date(vendor.created_at).toLocaleDateString()}
                            {vendor.updated_at !== vendor.created_at && (
                              <div className="text-xs text-gray-400">
                                Updated: {new Date(vendor.updated_at).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="flex space-x-1">
                            <button
                              onClick={() => handleEdit(vendor)}
                              className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-2 rounded transition-colors"
                              title="Edit vendor"
                            >
                              <Edit3 size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(vendor.id)}
                              className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded transition-colors"
                              title="Delete vendor"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="sm:hidden space-y-3">
                {paginatedVendors.map((vendor) => (
                  <div key={vendor.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex justify-between items-start mb-3">
                      <h5 className="font-medium text-gray-900">{vendor.company_name}</h5>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => handleEdit(vendor)}
                          className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-2 rounded transition-colors"
                          title="Edit vendor"
                        >
                          <Edit3 size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(vendor.id)}
                          className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded transition-colors"
                          title="Delete vendor"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="mb-2">
                      <div className="text-xs text-gray-500 mb-1">GLNs:</div>
                      <div className="flex flex-wrap gap-1">
                        {vendor.glns.map((gln, index) => (
                          <span
                            key={index}
                            className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded font-mono"
                            title={`GLN: ${gln}`}
                          >
                            {gln}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      Created: {new Date(vendor.created_at).toLocaleDateString()}
                      {vendor.updated_at !== vendor.created_at && (
                        <span> • Updated: {new Date(vendor.updated_at).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 border-t border-gray-200 pt-4">
                  <div className="text-sm text-gray-500">
                    Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredVendors.length)} of {filteredVendors.length} vendors
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    
                    <div className="flex space-x-1">
                      {[...Array(Math.min(5, totalPages))].map((_, i) => {
                        const pageNumber = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                        if (pageNumber > totalPages) return null;
                        
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => setCurrentPage(pageNumber)}
                            className={`px-3 py-1 text-sm border rounded-md ${
                              currentPage === pageNumber
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end pt-4 sm:pt-6 border-t border-gray-200 mt-4 sm:mt-6">
          <button
            onClick={onClose}
            className="bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-md text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const CreatePOModal = ({ onClose, onSuccess }) => {
  const { showToast } = useToast();
  const modalRef = useModalClose(true, onClose);
  const [formData, setFormData] = useState({
    po_number: '',
    supplier_name: '',
    products: []
  });
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(true);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    // Filter vendors based on search query
    const filtered = vendors.filter(vendor =>
      vendor.company_name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredVendors(filtered);
  }, [searchQuery, vendors]);

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/vendors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendors(response.data.vendors || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      showToast('Failed to load vendors. Please add vendors in Manage Vendors first.', 'error');
    } finally {
      setVendorsLoading(false);
    }
  };

  const handleVendorSelect = (vendor) => {
    setFormData({...formData, supplier_name: vendor.company_name});
    setSearchQuery(vendor.company_name);
    setShowDropdown(false);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setFormData({...formData, supplier_name: value});
    setShowDropdown(value.length > 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate that vendor name matches an existing vendor
      const selectedVendor = vendors.find(vendor => vendor.company_name === formData.supplier_name);
      if (!selectedVendor) {
        showToast('Please select a valid vendor from the list', 'error');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/pos`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Get the created PO ID from the response
      const createdPO = response.data;
      showToast(`Purchase Order ${formData.po_number} created successfully!`, 'success');
      
      // Pass the created PO ID to the success callback
      onSuccess(createdPO.id);
    } catch (error) {
      let errorMsg = 'Failed to create PO';
      
      if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      showToast(errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div ref={modalRef} className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Create Purchase Order</h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">PO Number</label>
              <input
                type="text"
                required
                value={formData.po_number}
                onChange={(e) => setFormData({...formData, po_number: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-gray-700">Vendor</label>
              <input
                type="text"
                required
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => setShowDropdown(searchQuery.length > 0)}
                onBlur={(e) => {
                  // Delay closing to allow for clicks
                  const currentTarget = e.currentTarget;
                  setTimeout(() => {
                    if (currentTarget && document.activeElement && !currentTarget.contains(document.activeElement)) {
                      setShowDropdown(false);
                    }
                  }, 150);
                }}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder={vendorsLoading ? "Loading vendors..." : vendors.length === 0 ? "No vendors available - add vendors first" : "Type to search vendors..."}
                disabled={vendorsLoading || vendors.length === 0}
              />
              
              {/* Vendor Dropdown */}
              {showDropdown && filteredVendors.length > 0 && (
                <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto mt-1">
                  {filteredVendors.map((vendor) => (
                    <div
                      key={vendor.id}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      onMouseDown={(e) => e.preventDefault()} // Prevent blur on click
                      onClick={() => handleVendorSelect(vendor)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{vendor.company_name}</div>
                          <div className="text-sm text-gray-500">
                            GLNs: {vendor.glns.join(', ')}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Helper text */}
              {vendors.length === 0 && !vendorsLoading && (
                <p className="mt-1 text-sm text-red-600">
                  No vendors available. Please add vendors in "Manage Vendors" first.
                </p>
              )}
              
              {searchQuery && !filteredVendors.length && vendors.length > 0 && (
                <p className="mt-1 text-sm text-gray-500">
                  No vendors match your search. Try a different term.
                </p>
              )}
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-md text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm font-medium disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create PO'}
              </button>
              
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const SuperAdminPanel = () => {
  const { showToast } = useToast();
  const [businesses, setBusinesses] = useState([]);
  const [superAdmins, setSuperAdmins] = useState([]);
  const [adminToken, setAdminToken] = useState(localStorage.getItem('admin_token'));
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('businesses');
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [businessUsers, setBusinessUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userActionModal, setUserActionModal] = useState({ show: false, user: null, action: null });
  const [showBusinessPasswordModal, setShowBusinessPasswordModal] = useState({ show: false, business: null });
  const [openDropdownId, setOpenDropdownId] = useState(null);
  
  // Database management state
  const [databaseStats, setDatabaseStats] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [collectionData, setCollectionData] = useState(null);
  const [collectionPage, setCollectionPage] = useState(1);
  const [collectionSearch, setCollectionSearch] = useState('');
  const [loadingDatabase, setLoadingDatabase] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [editDocumentData, setEditDocumentData] = useState('');
  
  // Business Applications pagination and search state
  const [businessPage, setBusinessPage] = useState(1);
  const [businessSearch, setBusinessSearch] = useState('');
  const [businessTotal, setBusinessTotal] = useState(0);
  const [businessTotalPages, setBusinessTotalPages] = useState(0);
  const [loadingBusinesses, setLoadingBusinesses] = useState(false);
  
  // Super Admin password reset state
  const [showPasswordResetModal, setShowPasswordResetModal] = useState({ show: false, admin: null });
  const [resetPasswordData, setResetPasswordData] = useState({ new_password: '', confirm_password: '' });

  useEffect(() => {
    if (adminToken) {
      // Initial data fetch for tab counts - only fetch once when admin token is available
      fetchBusinesses();
      fetchSuperAdmins();
    }
  }, [adminToken]);

  useEffect(() => {
    if (adminToken && activeTab === 'database') {
      // Only fetch database stats when on database tab
      fetchDatabaseStats();
    }
  }, [adminToken, activeTab]);

  const fetchDatabaseStats = async () => {
    try {
      setLoadingDatabase(true);
      const response = await axios.get(`${API}/admin/database-stats?admin_token=${adminToken}`);
      setDatabaseStats(response.data);
    } catch (error) {
      console.error('Error fetching database stats:', error);
      showToast('Failed to fetch database statistics', 'error');
    } finally {
      setLoadingDatabase(false);
    }
  };

  const fetchCollectionData = async (collectionName, page = 1, search = '') => {
    try {
      setLoadingDatabase(true);
      const params = new URLSearchParams({
        admin_token: adminToken,
        page: page.toString(),
        limit: '20'
      });
      
      if (search.trim()) {
        params.append('search', search.trim());
      }
      
      const response = await axios.get(`${API}/admin/collection/${collectionName}?${params}`);
      setCollectionData(response.data);
      setSelectedCollection(collectionName);
      setCollectionPage(page);
    } catch (error) {
      console.error('Error fetching collection data:', error);
      showToast('Failed to fetch collection data', 'error');
    } finally {
      setLoadingDatabase(false);
    }
  };

  const deleteDocument = async (collectionName, documentId) => {
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/collection/${collectionName}/document/${documentId}?admin_token=${adminToken}`);
      showToast('Document deleted successfully', 'success');
      // Refresh collection data
      fetchCollectionData(collectionName, collectionPage, collectionSearch);
    } catch (error) {
      console.error('Error deleting document:', error);
      showToast('Failed to delete document', 'error');
    }
  };

  const startEditDocument = (document) => {
    setEditingDocument(document);
    setEditDocumentData(JSON.stringify(document, null, 2));
  };

  const saveDocumentEdit = async () => {
    try {
      const parsedData = JSON.parse(editDocumentData);
      await axios.put(
        `${API}/admin/collection/${selectedCollection}/document/${editingDocument._id || editingDocument.id}?admin_token=${adminToken}`,
        parsedData
      );
      showToast('Document updated successfully', 'success');
      setEditingDocument(null);
      setEditDocumentData('');
      // Refresh collection data
      fetchCollectionData(selectedCollection, collectionPage, collectionSearch);
    } catch (error) {
      console.error('Error updating document:', error);
      if (error.message.includes('JSON')) {
        showToast('Invalid JSON format', 'error');
      } else {
        showToast('Failed to update document', 'error');
      }
    }
  };

  const cancelEditDocument = () => {
    setEditingDocument(null);
    setEditDocumentData('');
  };

  const downloadBackup = async () => {
    try {
      setLoadingDatabase(true);
      showToast('Creating database backup...', 'info');
      const response = await axios.post(`${API}/admin/database-backup?admin_token=${adminToken}`);
      
      // Create and download JSON file
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `database_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      showToast('Database backup downloaded successfully', 'success');
    } catch (error) {
      console.error('Error creating backup:', error);
      showToast('Failed to create database backup', 'error');
    } finally {
      setLoadingDatabase(false);
    }
  };

  const downloadCollection = async (collectionName) => {
    try {
      setLoadingDatabase(true);
      showToast(`Exporting ${collectionName} collection...`, 'info');
      const response = await axios.get(`${API}/admin/collection/${collectionName}/export?admin_token=${adminToken}`);
      
      // Create and download JSON file
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${collectionName}_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      showToast(`${collectionName} collection exported successfully`, 'success');
    } catch (error) {
      console.error('Error exporting collection:', error);
      showToast('Failed to export collection', 'error');
    } finally {
      setLoadingDatabase(false);
    }
  };

  const downloadEPCISFile = async (poId, fileName) => {
    try {
      showToast('Downloading EPCIS file...', 'info');
      
      // Create a direct download URL
      const downloadUrl = `${API}/admin/collection/purchase_orders/${poId}/epcis-file?admin_token=${adminToken}`;
      
      // Create hidden link and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName || 'epcis_file.xml';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showToast('EPCIS file download started', 'success');
    } catch (error) {
      console.error('Error downloading EPCIS file:', error);
      showToast('Failed to download EPCIS file', 'error');
    }
  };

  const handleResetSuperAdminPassword = async () => {
    if (resetPasswordData.new_password !== resetPasswordData.confirm_password) {
      showToast('Passwords do not match', 'error');
      return;
    }
    
    if (resetPasswordData.new_password.length < 6) {
      showToast('Password must be at least 6 characters', 'error');
      return;
    }
    
    try {
      await axios.put(
        `${API}/admin/super-admin/${showPasswordResetModal.admin.id}/reset-password?admin_token=${adminToken}`,
        { new_password: resetPasswordData.new_password }
      );
      
      showToast('Password reset successfully', 'success');
      setShowPasswordResetModal({ show: false, admin: null });
      setResetPasswordData({ new_password: '', confirm_password: '' });
    } catch (error) {
      console.error('Error resetting password:', error);
      showToast(error.response?.data?.detail || 'Failed to reset password', 'error');
    }
  };

  const fetchBusinesses = async (page = 1, search = '') => {
    try {
      setLoadingBusinesses(true);
      const params = new URLSearchParams({
        admin_token: adminToken,
        page: page.toString(),
        limit: '10'  // Changed from 20 to 10 records per page
      });
      
      if (search.trim()) {
        params.append('search', search.trim());
      }
      
      const response = await axios.get(`${API}/admin/businesses?${params}`);
      setBusinesses(response.data.businesses);
      setBusinessTotal(response.data.total);
      setBusinessTotalPages(response.data.total_pages);
      setBusinessPage(page);
    } catch (error) {
      console.error('Error fetching businesses:', error);
      if (error.response?.status === 401) {
        setAdminToken(null);
        localStorage.removeItem('admin_token');
      }
    } finally {
      setLoadingBusinesses(false);
    }
  };

  const fetchSuperAdmins = async () => {
    try {
      const response = await axios.get(`${API}/admin/super-admins?admin_token=${adminToken}`);
      setSuperAdmins(response.data);
    } catch (error) {
      console.error('Error fetching super admins:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      const token = response.data.admin_token;
      setAdminToken(token);
      localStorage.setItem('admin_token', token);
      setCredentials({ username: '', password: '' });
      showToast('Logged in successfully', 'success');
    } catch (error) {
      showToast('Invalid credentials', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (businessId) => {
    try {
      await axios.post(`${API}/admin/businesses/${businessId}/approve?admin_token=${adminToken}`);
      showToast('Business approved successfully', 'success');
      fetchBusinesses();
    } catch (error) {
      showToast('Failed to approve business', 'error');
    }
  };

  const handleSuspend = async (businessId) => {
    if (window.confirm('Are you sure you want to suspend this business?')) {
      try {
        await axios.post(`${API}/admin/businesses/${businessId}/suspend?admin_token=${adminToken}`);
        showToast('Business suspended successfully', 'success');
        fetchBusinesses();
      } catch (error) {
        showToast('Failed to suspend business', 'error');
      }
    }
  };

  const handleUnsuspend = async (businessId) => {
    if (window.confirm('Are you sure you want to unsuspend this business?')) {
      try {
        await axios.post(`${API}/admin/businesses/${businessId}/unsuspend?admin_token=${adminToken}`);
        showToast('Business unsuspended successfully', 'success');
        fetchBusinesses();
      } catch (error) {
        showToast('Failed to unsuspend business', 'error');
      }
    }
  };

  const handleDeleteBusiness = async (businessId) => {
    if (window.confirm('Are you sure you want to DELETE this business and ALL associated data? This action cannot be undone.')) {
      try {
        await axios.delete(`${API}/admin/businesses/${businessId}?admin_token=${adminToken}`);
        showToast('Business deleted successfully', 'success');
        fetchBusinesses();
      } catch (error) {
        showToast('Failed to delete business', 'error');
      }
    }
  };

  const handleViewUsers = async (business) => {
    setSelectedBusiness(business);
    setLoadingUsers(true);
    setShowUserManagement(true);
    
    try {
      const response = await axios.get(`${API}/admin/businesses/${business.id}/users?admin_token=${adminToken}`);
      setBusinessUsers(response.data);
    } catch (error) {
      showToast('Failed to load users', 'error');
      setBusinessUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role?admin_token=${adminToken}`, 
        { role: newRole },
        { headers: { 'Content-Type': 'application/json' } }
      );
      showToast(`User role updated to ${newRole}`, 'success');
      handleViewUsers(selectedBusiness); // Refresh user list
    } catch (error) {
      showToast('Failed to update user role', 'error');
    }
  };

  const handleResetPassword = async (userId, newPassword) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/password?admin_token=${adminToken}`,
        { password: newPassword },
        { headers: { 'Content-Type': 'application/json' } }
      );
      showToast('User password reset successfully', 'success');
    } catch (error) {
      showToast('Failed to reset password', 'error');
    }
  };

  const handleRemoveUser = async (userId) => {
    if (window.confirm('Are you sure you want to remove this user?')) {
      try {
        await axios.delete(`${API}/admin/users/${userId}?admin_token=${adminToken}`);
        showToast('User removed successfully', 'success');
        handleViewUsers(selectedBusiness); // Refresh user list
      } catch (error) {
        showToast('Failed to remove user', 'error');
      }
    }
  };

  const handleResetBusinessOwnerPassword = async (businessId, newPassword) => {
    try {
      await axios.put(`${API}/admin/businesses/${businessId}/reset-password?admin_token=${adminToken}`,
        { password: newPassword },
        { headers: { 'Content-Type': 'application/json' } }
      );
      showToast('Business owner password reset successfully', 'success');
    } catch (error) {
      showToast('Failed to reset business owner password', 'error');
    }
  };

  const toggleDropdown = (businessId) => {
    setOpenDropdownId(openDropdownId === businessId ? null : businessId);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleDeleteSuperAdmin = async (adminId) => {
    if (window.confirm('Are you sure you want to delete this super admin?')) {
      try {
        await axios.delete(`${API}/admin/super-admins/${adminId}?admin_token=${adminToken}`);
        showToast('Super admin deleted successfully', 'success');
        fetchSuperAdmins();
      } catch (error) {
        showToast(error.response?.data?.detail || 'Failed to delete super admin', 'error');
      }
    }
  };

  if (!adminToken) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Super Admin Login
          </h2>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Username</label>
                <input
                  type="text"
                  required
                  value={credentials.username}
                  onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Password</label>
                <input
                  type="password"
                  required
                  value={credentials.password}
                  onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Super Admin Panel</h1>
        <button
          onClick={() => {
            setAdminToken(null);
            localStorage.removeItem('admin_token');
          }}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
        >
          Logout
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('businesses')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'businesses'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Business Applications ({businessTotal})
          </button>
          <button
            onClick={() => setActiveTab('admins')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'admins'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Super Admins ({superAdmins.length})
          </button>
          <button
            onClick={() => setActiveTab('database')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'database'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Database Management
          </button>
        </nav>
      </div>

      {activeTab === 'businesses' && (
        <div className="bg-white shadow-lg rounded-lg overflow-visible">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">
                Business Applications ({businessTotal.toLocaleString()})
              </h2>
              <div className="flex items-center space-x-3">
                <input
                  type="text"
                  placeholder="Search businesses..."
                  value={businessSearch}
                  onChange={(e) => setBusinessSearch(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      fetchBusinesses(1, e.target.value);
                    }
                  }}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm w-64"
                />
                <button
                  onClick={() => fetchBusinesses(1, businessSearch)}
                  disabled={loadingBusinesses}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50"
                >
                  Search
                </button>
              </div>
            </div>
          </div>
          
          {loadingBusinesses ? (
            <div className="p-8 text-center">
              <div className="text-gray-500">Loading businesses...</div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto overflow-y-visible">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Pharmacy Name
                      </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Username
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    NPI Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {businesses.map((business, index) => (
                  <tr key={business.id}>
                    <td className="px-6 py-4 max-w-fit whitespace-normal break-words text-sm font-medium text-gray-900">
                      {business.pharmacy_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {business.username || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {business.npi_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>
                        <div>{business.contact_name}</div>
                        <div className="text-gray-400">{business.contact_email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        business.status === 'approved' ? 'bg-green-100 text-green-800' :
                        business.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {business.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="relative dropdown-container">
                        <button
                          onClick={() => toggleDropdown(business.id)}
                          className="p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <MoreHorizontal size={16} className="text-gray-500" />
                        </button>
                        
                        {openDropdownId === business.id && (
                          <div className={`absolute right-0 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-[60] ${
                            // Smart positioning: show dropdown above for last 2 rows
                            index >= businesses.length - 2 
                              ? 'bottom-full mb-2' 
                              : 'top-full mt-2'
                          }`}>
                            <div className="py-1">
                              {business.status === 'pending' && (
                                <button
                                  onClick={() => {
                                    handleApprove(business.id);
                                    setOpenDropdownId(null);
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-green-700 hover:bg-green-50"
                                >
                                  <Check size={16} className="mr-3" />
                                  Approve Business
                                </button>
                              )}
                              
                              {business.status === 'approved' && (
                                <button
                                  onClick={() => {
                                    handleSuspend(business.id);
                                    setOpenDropdownId(null);
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                                >
                                  <Archive size={16} className="mr-3" />
                                  Suspend Business
                                </button>
                              )}
                              
                              {business.status === 'suspended' && (
                                <button
                                  onClick={() => {
                                    handleUnsuspend(business.id);
                                    setOpenDropdownId(null);
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-blue-700 hover:bg-blue-50"
                                >
                                  <RefreshCw size={16} className="mr-3" />
                                  Unsuspend Business
                                </button>
                              )}
                              
                              <button
                                onClick={() => {
                                  handleViewUsers(business);
                                  setOpenDropdownId(null);
                                }}
                                className="flex items-center w-full px-4 py-2 text-sm text-blue-700 hover:bg-blue-50"
                              >
                                <Users size={16} className="mr-3" />
                                Manage Users
                              </button>
                              
                              <button
                                onClick={() => {
                                  setShowBusinessPasswordModal({ show: true, business });
                                  setOpenDropdownId(null);
                                }}
                                className="flex items-center w-full px-4 py-2 text-sm text-purple-700 hover:bg-purple-50"
                              >
                                <Edit3 size={16} className="mr-3" />
                                Reset Owner Password
                              </button>
                              
                              <div className="border-t border-gray-100"></div>
                              
                              <button
                                onClick={() => {
                                  handleDeleteBusiness(business.id);
                                  setOpenDropdownId(null);
                                }}
                                className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                              >
                                <Trash2 size={16} className="mr-3" />
                                Delete Business
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination for Business Applications */}
          {businessTotalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-500">
                  Showing page {businessPage} of {businessTotalPages} ({businessTotal.toLocaleString()} total)
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => fetchBusinesses(businessPage - 1, businessSearch)}
                    disabled={businessPage <= 1 || loadingBusinesses}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-3 py-2 rounded text-sm disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => fetchBusinesses(businessPage + 1, businessSearch)}
                    disabled={businessPage >= businessTotalPages || loadingBusinesses}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-3 py-2 rounded text-sm disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
            </>
          )}
        </div>
      )}

      {activeTab === 'admins' && (
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Super Admins</h2>
            <button
              onClick={() => setShowCreateAdmin(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              Create Super Admin
            </button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Username
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {superAdmins.map((admin) => (
                  <tr key={admin.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {admin.username}
                      {admin.username === 'superadmin' && (
                        <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Default</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(admin.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {admin.created_by || 'System'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setShowPasswordResetModal({ show: true, admin: admin })}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Reset Password
                        </button>
                        {admin.username !== 'superadmin' && (
                          <button
                            onClick={() => handleDeleteSuperAdmin(admin.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'database' && (
        <div className="space-y-6">
          {/* Database Statistics */}
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">Database Overview</h2>
              <div className="space-x-3">
                <button
                  onClick={fetchDatabaseStats}
                  disabled={loadingDatabase}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                >
                  {loadingDatabase ? 'Loading...' : 'Refresh'}
                </button>
                <button
                  onClick={downloadBackup}
                  disabled={loadingDatabase}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                >
                  Download Backup
                </button>
              </div>
            </div>
            
            {databaseStats ? (
              <div className="p-6">
                {/* Database Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      {databaseStats.database_stats.collections}
                    </div>
                    <div className="text-sm text-blue-600">Collections</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {databaseStats.database_stats.objects.toLocaleString()}
                    </div>
                    <div className="text-sm text-green-600">Documents</div>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-600">
                      {(databaseStats.database_stats.data_size / (1024 * 1024)).toFixed(1)}MB
                    </div>
                    <div className="text-sm text-yellow-600">Data Size</div>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">
                      {(databaseStats.database_stats.storage_size / (1024 * 1024)).toFixed(1)}MB
                    </div>
                    <div className="text-sm text-purple-600">Storage Size</div>
                  </div>
                </div>

                {/* Collections Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Collection Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Document Count
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Sample Schema
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {databaseStats.collections.map((collection) => (
                        <tr key={collection.name}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {collection.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {collection.count.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            <div className="flex flex-wrap gap-1">
                              {Object.keys(collection.schema_preview).slice(0, 4).map(field => (
                                <span key={field} className="text-xs bg-gray-100 px-2 py-1 rounded">
                                  {field}
                                </span>
                              ))}
                              {Object.keys(collection.schema_preview).length > 4 && (
                                <span className="text-xs text-gray-400">
                                  +{Object.keys(collection.schema_preview).length - 4} more
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => fetchCollectionData(collection.name)}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                View All
                              </button>
                              <button
                                onClick={() => downloadCollection(collection.name)}
                                disabled={loadingDatabase}
                                className="text-green-600 hover:text-green-900 disabled:opacity-50"
                              >
                                Download
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="p-6 text-center">
                <div className="text-gray-500">
                  {loadingDatabase ? 'Loading database statistics...' : 'Click Refresh to load database statistics'}
                </div>
              </div>
            )}
          </div>

          {/* Collection Browser */}
          {selectedCollection && collectionData && (
            <div className="bg-white shadow-lg rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Collection: {selectedCollection}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {collectionData.total.toLocaleString()} total documents
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <input
                      type="text"
                      placeholder="Search..."
                      value={collectionSearch}
                      onChange={(e) => setCollectionSearch(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          fetchCollectionData(selectedCollection, 1, e.target.value);
                        }
                      }}
                      className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                    <button
                      onClick={() => fetchCollectionData(selectedCollection, 1, collectionSearch)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm"
                    >
                      Search
                    </button>
                    <button
                      onClick={() => {
                        setSelectedCollection(null);
                        setCollectionData(null);
                        setCollectionSearch('');
                      }}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                {/* Documents Display */}
                <div className="space-y-4">
                  {collectionData.documents.map((doc, index) => (
                    <div key={doc._id || doc.id || index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1 space-y-2">
                          <div>
                            <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                              ID: {doc._id || doc.id || 'No ID'}
                            </span>
                          </div>
                          {/* Show EPCIS file info for purchase orders */}
                          {selectedCollection === 'purchase_orders' && doc.epcis_file_name && (
                            <div>
                              <span className="text-xs font-mono bg-green-100 text-green-800 px-2 py-1 rounded">
                                📁 EPCIS: {doc.epcis_file_name}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => startEditDocument(doc)}
                            className="text-blue-600 hover:text-blue-900 text-sm"
                          >
                            Edit
                          </button>
                          {/* Show EPCIS download button for purchase orders with EPCIS files */}
                          {selectedCollection === 'purchase_orders' && doc.epcis_file_name && (
                            <button
                              onClick={() => downloadEPCISFile(doc.id, doc.epcis_file_name)}
                              className="text-green-600 hover:text-green-900 text-sm"
                            >
                              Download EPCIS
                            </button>
                          )}
                          <button
                            onClick={() => deleteDocument(selectedCollection, doc._id || doc.id)}
                            className="text-red-600 hover:text-red-900 text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      <pre className="text-xs bg-gray-50 p-3 rounded overflow-x-auto">
                        {JSON.stringify(doc, null, 2)}
                      </pre>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {collectionData.total_pages > 1 && (
                  <div className="flex justify-between items-center mt-6">
                    <div className="text-sm text-gray-500">
                      Page {collectionData.page} of {collectionData.total_pages}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => fetchCollectionData(selectedCollection, collectionPage - 1, collectionSearch)}
                        disabled={collectionPage <= 1}
                        className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-3 py-2 rounded text-sm disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => fetchCollectionData(selectedCollection, collectionPage + 1, collectionSearch)}
                        disabled={collectionPage >= collectionData.total_pages}
                        className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-3 py-2 rounded text-sm disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit Document Modal */}
      {editingDocument && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Edit Document - {selectedCollection}
              </h3>
              <button
                onClick={cancelEditDocument}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Document ID: <span className="font-mono bg-gray-100 px-2 py-1 rounded">{editingDocument._id || editingDocument.id}</span>
              </p>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document JSON (edit the content below):
              </label>
              <textarea
                value={editDocumentData}
                onChange={(e) => setEditDocumentData(e.target.value)}
                className="w-full h-96 p-3 border border-gray-300 rounded-md text-sm font-mono"
                placeholder="Enter valid JSON..."
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelEditDocument}
                className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={saveDocumentEdit}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateAdmin && (
        <CreateSuperAdminModal 
          adminToken={adminToken}
          onClose={() => setShowCreateAdmin(false)} 
          onSuccess={() => {
            setShowCreateAdmin(false);
            fetchSuperAdmins();
          }}
        />
      )}

      {showUserManagement && (
        <UserManagementModal 
          business={selectedBusiness}
          users={businessUsers}
          loading={loadingUsers}
          adminToken={adminToken}
          onClose={() => setShowUserManagement(false)}
          onUpdateRole={handleUpdateUserRole}
          onResetPassword={handleResetPassword}
          onRemoveUser={handleRemoveUser}
        />
      )}

      {showBusinessPasswordModal.show && (
        <BusinessOwnerPasswordModal 
          business={showBusinessPasswordModal.business}
          onClose={() => setShowBusinessPasswordModal({ show: false, business: null })}
          onResetPassword={handleResetBusinessOwnerPassword}
        />
      )}

      {showPasswordResetModal.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Reset Password - {showPasswordResetModal.admin.username}
              </h3>
              <button
                onClick={() => {
                  setShowPasswordResetModal({ show: false, admin: null });
                  setResetPasswordData({ new_password: '', confirm_password: '' });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  value={resetPasswordData.new_password}
                  onChange={(e) => setResetPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  placeholder="Enter new password (min 6 characters)"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={resetPasswordData.confirm_password}
                  onChange={(e) => setResetPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  placeholder="Confirm new password"
                />
              </div>
              
              {showPasswordResetModal.admin.username === 'superadmin' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <div className="text-sm text-yellow-800">
                    ⚠️ You are resetting the password for the default system admin account.
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowPasswordResetModal({ show: false, admin: null });
                  setResetPasswordData({ new_password: '', confirm_password: '' });
                }}
                className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleResetSuperAdminPassword}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
              >
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CreateSuperAdminModal = ({ adminToken, onClose, onSuccess }) => {
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/admin/super-admins`, formData, {
        params: { admin_token: adminToken }
      });
      showToast('Super admin created successfully', 'success');
      onSuccess();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to create super admin', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Create Super Admin</h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Username</label>
              <input
                type="text"
                required
                minLength="3"
                maxLength="50"
                pattern="[a-zA-Z0-9_-]+"
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Letters, numbers, underscore, hyphen only"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <input
                type="password"
                required
                minLength="6"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Minimum 6 characters"
              />
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm font-medium disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Super Admin'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-md text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const UserManagementModal = ({ business, users, loading, adminToken, onClose, onUpdateRole, onResetPassword, onRemoveUser }) => {
  const { showToast } = useToast();
  const [showPasswordModal, setShowPasswordModal] = useState({ show: false, user: null });
  const [newPassword, setNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  const handlePasswordReset = async () => {
    if (!newPassword || newPassword.length < 6) {
      showToast('Password must be at least 6 characters', 'error');
      return;
    }

    setPasswordLoading(true);
    try {
      await onResetPassword(showPasswordModal.user.id, newPassword);
      setShowPasswordModal({ show: false, user: null });
      setNewPassword('');
    } catch (error) {
      // Error handled by parent component
    } finally {
      setPasswordLoading(false);
    }
  };

  if (!business) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              User Management - {business.pharmacy_name}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading users...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Username
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {user.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.username}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={user.role}
                          onChange={(e) => onUpdateRole(user.id, e.target.value)}
                          className="text-sm border border-gray-300 rounded px-2 py-1"
                        >
                          <option value="staff">Staff</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex gap-2">
                          <button
                            onClick={() => setShowPasswordModal({ show: true, user })}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Reset Password
                          </button>
                          <button
                            onClick={() => onRemoveUser(user.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {users.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No users found for this business.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Password Reset Modal */}
      {showPasswordModal.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-60">
          <div className="relative top-1/2 transform -translate-y-1/2 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Reset Password for {showPasswordModal.user?.name}
              </h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={handlePasswordReset}
                  disabled={passwordLoading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm font-medium disabled:opacity-50"
                >
                  {passwordLoading ? 'Resetting...' : 'Reset Password'}
                </button>
                <button
                  onClick={() => {
                    setShowPasswordModal({ show: false, user: null });
                    setNewPassword('');
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-md text-sm font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BusinessOwnerPasswordModal = ({ business, onClose, onResetPassword }) => {
  const { showToast } = useToast();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newPassword || newPassword.length < 6) {
      showToast('Password must be at least 6 characters', 'error');
      return;
    }

    if (newPassword !== confirmPassword) {
      showToast('Passwords do not match', 'error');
      return;
    }

    setLoading(true);
    try {
      await onResetPassword(business.id, newPassword);
      onClose();
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      // Error handled by parent component
    } finally {
      setLoading(false);
    }
  };

  if (!business) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-1/2 transform -translate-y-1/2 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Reset Business Owner Password
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          <div className="mb-4 p-3 bg-blue-50 rounded-md">
            <p className="text-sm text-gray-700">
              <strong>Business:</strong> {business.pharmacy_name}
            </p>
            <p className="text-sm text-gray-700">
              <strong>Owner Username:</strong> {business.username}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            
            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-md text-sm font-medium disabled:opacity-50"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-md text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// EPCIS Filter Tool Component
const EPCISFilterTool = () => {
  const { user, business, logout, loading } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showManageUsers, setShowManageUsers] = useState(false);
  const [showManageVendors, setShowManageVendors] = useState(false);
  const dropdownRef = useRef(null);

  // Helper function to get user initials

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [selectedSerials, setSelectedSerials] = useState(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const [showTree, setShowTree] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [isCollapsedAll, setIsCollapsedAll] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [glnInfo, setGlnInfo] = useState(null);

  // Redirect if not logged in
  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const processFile = async (file) => {
    if (!file.name.toLowerCase().endsWith('.xml')) {
      showToast('Please select an XML file', 'error');
      return;
    }

    setSelectedFile(file);
    setIsProcessing(true);
    setShowTree(false);
    
    try {
      const fileContent = await file.text();
      const parsed = parseEPCISForFilter(fileContent);
      
      if (!parsed || !parsed.hierarchy) {
        showToast('No packaging hierarchy found in EPCIS file', 'error');
        return;
      }
      
      if (parsed.hierarchy.length === 0 && parsed.serialCount === 0) {
        showToast('No serial numbers found in EPCIS file', 'error');
        return;
      }
      
      setParsedData(parsed);
      setGlnInfo(parsed.glnInfo);
      
      // Initialize with no serial numbers selected by default
      setSelectedSerials(new Set());
      
      // Initialize expanded state - expand SSCC nodes by default
      const ssccNodes = new Set();
      parsed.hierarchy.forEach(node => {
        if (node.level === 'SSCC' || node.level === 'NESTED_SSCC') {
          ssccNodes.add(node.identifier);
        }
      });
      setExpandedNodes(ssccNodes);
      setIsCollapsedAll(false); // SSCCs are expanded
      
      setShowTree(true);
      showToast(`Successfully parsed EPCIS file with ${parsed.allSerials.size} serial numbers`, 'success');
      
    } catch (error) {
      console.error('Error parsing EPCIS file:', error);
      showToast('Error parsing EPCIS file. Please ensure it\'s a valid GS1 Rx EPCIS format.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    await processFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await processFile(files[0]);
    }
  };

  const parseEPCISForFilter = (xmlContent) => {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');
      
      console.log('🔍 Starting EPCIS parsing for filter tool...');
      
      // Step 0: Extract GLN information from SBDH (Standard Business Document Header)
      let glnInfo = { sender: 'N/A', receiver: 'N/A' };
      try {
        const senderElement = xmlDoc.querySelector('sbdh\\:Sender sbdh\\:Identifier, Sender Identifier');
        const receiverElement = xmlDoc.querySelector('sbdh\\:Receiver sbdh\\:Identifier, Receiver Identifier');
        
        if (senderElement) {
          glnInfo.sender = senderElement.textContent?.trim() || 'N/A';
        }
        
        if (receiverElement) {
          glnInfo.receiver = receiverElement.textContent?.trim() || 'N/A';
        }
        
        console.log(`GLN Info - Sender: ${glnInfo.sender}, Receiver: ${glnInfo.receiver}`);
      } catch (error) {
        console.warn('Could not extract GLN information:', error);
      }

      // Step 0.5: Parse the EPCClass Vocabulary to derive the EACH layer per product.
      // The EACH layer is the LOWEST indicator digit present for a product in the
      // Vocabulary (the indicator digit is NOT always 0). Higher indicator digits
      // within the same product group represent containers (CASE / INNER CASE).
      const eachIndicatorMap = new Map(); // baseGTIN -> each (lowest) indicator digit
      const productNdcMap = new Map(); // baseGTIN -> NDC (additionalTradeItemIdentification)
      const productNameMap = new Map(); // baseGTIN -> regulatedProductName
      try {
        const indicatorsByBase = new Map(); // baseGTIN -> Set of indicator digits
        const getAttr = (ve, attrId) => Array.from(ve.querySelectorAll('attribute, epcis\\:attribute')).find(
          a => (a.getAttribute('id') || '') === attrId
        )?.textContent?.trim();
        const epcClassVocabs = xmlDoc.querySelectorAll('Vocabulary[type="urn:epcglobal:epcis:vtype:EPCClass"], epcis\\:Vocabulary[type="urn:epcglobal:epcis:vtype:EPCClass"]');
        epcClassVocabs.forEach(vocab => {
          const vocabElements = vocab.querySelectorAll('VocabularyElement, epcis\\:VocabularyElement');
          vocabElements.forEach(ve => {
            const idAttr = ve.getAttribute('id') || '';
            const match = idAttr.match(/urn:epc:idpat:sgtin:([^.]+)\.([^.]+)\.\*/);
            if (match) {
              const companyPrefix = match[1];
              const itemRef = match[2];
              if (!itemRef || itemRef.length < 1) return;
              const indicatorDigit = itemRef.charAt(0);
              const baseGTIN = `${companyPrefix}${itemRef.substring(1)}`;
              if (!indicatorsByBase.has(baseGTIN)) indicatorsByBase.set(baseGTIN, new Set());
              indicatorsByBase.get(baseGTIN).add(indicatorDigit);

              // Capture the NDC and product name exactly as portrayed in the Vocabulary.
              if (!productNdcMap.has(baseGTIN)) {
                const ndc = getAttr(ve, 'urn:epcglobal:cbv:mda#additionalTradeItemIdentification');
                if (ndc) productNdcMap.set(baseGTIN, ndc);
              }
              if (!productNameMap.has(baseGTIN)) {
                const name = getAttr(ve, 'urn:epcglobal:cbv:mda#regulatedProductName');
                if (name) productNameMap.set(baseGTIN, name);
              }
            }
          });
        });
        indicatorsByBase.forEach((indicators, baseGTIN) => {
          const lowest = Array.from(indicators)
            .map(d => parseInt(d, 10))
            .filter(n => !isNaN(n))
            .sort((a, b) => a - b)[0];
          if (lowest !== undefined) eachIndicatorMap.set(baseGTIN, String(lowest));
        });
        console.log(`📚 Derived EACH indicator for ${eachIndicatorMap.size} products from EPCClass Vocabulary`);
      } catch (error) {
        console.warn('Could not derive EACH layer from EPCClass Vocabulary:', error);
      }

      // Step 1: Find ALL commissioned serial numbers from ObjectEvents (commissioning events)
      const allSgtinEpcs = new Map(); // epc -> serial data
      const objectEvents = xmlDoc.querySelectorAll('ObjectEvent, epcis\\:ObjectEvent');
      
      console.log(`Found ${objectEvents.length} ObjectEvents to analyze for commissioned serials`);
      
      objectEvents.forEach((event, eventIndex) => {
        const action = event.querySelector('action, epcis\\:action')?.textContent?.trim();
        
        if (action === 'ADD') { // Commissioning event
          console.log(`Processing commissioning event ${eventIndex + 1}`);
          
          const eventEpcs = event.querySelectorAll('epcList epc, epcis\\:epcList epcis\\:epc');
          console.log(`  Found ${eventEpcs.length} EPCs in this commissioning event`);
          
          eventEpcs.forEach(epcElement => {
            const epcText = epcElement.textContent?.trim();
            if (epcText && epcText.includes('sgtin')) {
              const match = epcText.match(/urn:epc:id:sgtin:([^.]+)\.([^.]+)\.(.+)/);
              if (match) {
                const companyPrefix = match[1];
                const itemReference = match[2]; 
                const serialNumber = match[3];
                // GTIN is straightforward - just company prefix + item reference
                const gtin = `${companyPrefix}${itemReference}`;
                
                console.log(`    SGTIN: ${epcText}`);
                console.log(`      Company Prefix: ${companyPrefix}`);
                console.log(`      Item Reference: ${itemReference}`);
                console.log(`      GTIN: ${gtin}`);
                
                // Extract lot and expiration from this commissioning event's extensions/ILMD
                let lot = 'N/A';
                let expiration = 'N/A';
                
                // Check extensions
                const extensions = event.querySelectorAll('extension *, epcis\\:extension *');
                extensions.forEach(ext => {
                  const tagName = ext.tagName?.toLowerCase();
                  const textContent = ext.textContent?.trim();
                  
                  if (tagName && textContent) {
                    if (tagName.includes('lot') || tagName.includes('batch')) {
                      lot = textContent;
                    } else if (tagName.includes('expir') || tagName.includes('exp')) {
                      expiration = textContent;
                    }
                  }
                });

                // Check ILMD (Instance/Lot Master Data)
                const ilmdElements = event.querySelectorAll('ilmd *, epcis\\:ilmd *');
                ilmdElements.forEach(ilmd => {
                  const tagName = ilmd.tagName?.toLowerCase();
                  const textContent = ilmd.textContent?.trim();
                  
                  if (tagName && textContent) {
                    if (tagName.includes('lot') || tagName.includes('batch')) {
                      lot = textContent;
                    } else if (tagName.includes('expir') || tagName.includes('exp')) {
                      expiration = textContent;
                    }
                  }
                });
                
                allSgtinEpcs.set(epcText, {
                  epc: epcText,
                  serialNumber,
                  gtin,
                  companyPrefix,
                  itemReference,
                  lot,
                  expiration,
                  level: 'EACH'
                });
                
                console.log(`    ✅ Added serial ${serialNumber} with GTIN ${gtin}, Lot: ${lot}, Exp: ${expiration}`);
              }
            }
          });
        }
      });
      
      console.log(`✅ Found ${allSgtinEpcs.size} total commissioned serial numbers`);

      // Step 2: Build packaging hierarchy from AggregationEvents
      console.log('🏗️ Building packaging hierarchy from AggregationEvents...');
      
      const parentChildMap = new Map(); // parent EPC -> array of child EPCs
      const childParentMap = new Map(); // child EPC -> parent EPC
      
      const aggregationEvents = xmlDoc.querySelectorAll('AggregationEvent, epcis\\:AggregationEvent');
      console.log(`Found ${aggregationEvents.length} aggregation events`);
      
      aggregationEvents.forEach((event, index) => {
        const action = event.querySelector('action, epcis\\:action')?.textContent?.trim();
        const parentEpc = event.querySelector('parentID, epcis\\:parentID')?.textContent?.trim();
        const childEpcs = Array.from(event.querySelectorAll('childEPCs epc, epcis\\:childEPCs epcis\\:epc'))
          .map(e => e.textContent?.trim())
          .filter(epc => epc);
        
        console.log(`AggregationEvent ${index + 1}: action=${action}, parent=${parentEpc}, children=${childEpcs.length}`);
        
        if (action === 'ADD' && parentEpc && childEpcs.length > 0) {
          parentChildMap.set(parentEpc, childEpcs);
          childEpcs.forEach(childEpc => {
            childParentMap.set(childEpc, parentEpc);
          });
        }
      });

      console.log(`Built parent-child relationships: ${parentChildMap.size} parents`);

      // Step 2.5: Build a comprehensive EPC type map for proper level detection
      const epcTypeMap = new Map(); // epc -> type info
      
      // First pass: Identify all EPCs and their basic types
      const allEpcs = new Set([...parentChildMap.keys(), ...Array.from(parentChildMap.values()).flat()]);
      
      allEpcs.forEach(epc => {
        let baseType = 'UNKNOWN';
        let isSerial = false;
        
        if (allSgtinEpcs.has(epc)) {
          isSerial = true;
          baseType = 'SGTIN';
        } else if (epc.includes('sscc')) {
          baseType = 'SSCC';
        } else {
          baseType = 'CONTAINER';
        }
        
        epcTypeMap.set(epc, {
          baseType,
          isSerial,
          hasChildren: parentChildMap.has(epc),
          children: parentChildMap.get(epc) || [],
          level: null, // To be determined
          depth: -1    // To be calculated
        });
      });

      // Second pass: Calculate hierarchy depth and determine proper levels
      const calculateDepthAndLevel = (epc, currentDepth = 0, visited = new Set()) => {
        if (visited.has(epc)) {
          console.warn(`Circular dependency detected for EPC: ${epc}`);
          return currentDepth;
        }
        
        visited.add(epc);
        const epcInfo = epcTypeMap.get(epc);
        if (!epcInfo) return currentDepth;
        
        // Update depth if this is deeper than previously calculated
        if (epcInfo.depth < currentDepth) {
          epcInfo.depth = currentDepth;
        }
        
        // Determine level based on type, content, and depth
        if (epcInfo.isSerial) {
          // Serial numbers (SGTINs)
          if (epcInfo.hasChildren) {
            // This SGTIN has children, so it's a container
            const hasOnlySerials = epcInfo.children.every(childEpc => allSgtinEpcs.has(childEpc));
            const hasContainers = epcInfo.children.some(childEpc => !allSgtinEpcs.has(childEpc));
            
            if (hasOnlySerials && !hasContainers) {
              // Contains only other serial numbers - it's a CASE
              epcInfo.level = 'CASE';
            } else if (hasContainers) {
              // Contains other containers - could be INNER CASE
              epcInfo.level = currentDepth <= 2 ? 'CASE' : 'INNER_CASE';
            } else {
              // Default to CASE for serial containers
              epcInfo.level = 'CASE';
            }
            
            // Update the serial data level
            if (allSgtinEpcs.has(epc)) {
              allSgtinEpcs.get(epc).level = epcInfo.level;
            }
          } else {
            // No children - it's an EACH (individual item)
            epcInfo.level = 'EACH';
            if (allSgtinEpcs.has(epc)) {
              allSgtinEpcs.get(epc).level = 'EACH';
            }
          }
        } else if (epcInfo.baseType === 'SSCC') {
          // SSCC containers
          if (currentDepth === 0) {
            epcInfo.level = 'SSCC'; // Root SSCC
          } else {
            epcInfo.level = 'NESTED_SSCC'; // SSCC inside another SSCC
          }
        } else {
          // Other containers
          const hasOnlySerials = epcInfo.children.every(childEpc => allSgtinEpcs.has(childEpc));
          const hasSSCCs = epcInfo.children.some(childEpc => epcTypeMap.get(childEpc)?.baseType === 'SSCC');
          
          if (hasSSCCs) {
            epcInfo.level = currentDepth === 0 ? 'SSCC' : 'CASE';
          } else if (hasOnlySerials) {
            epcInfo.level = 'CASE';
          } else {
            epcInfo.level = currentDepth <= 1 ? 'CASE' : 'INNER_CASE';
          }
        }
        
        console.log(`EPC: ${epc.split(':').pop()} | Type: ${epcInfo.baseType} | Level: ${epcInfo.level} | Depth: ${currentDepth} | Children: ${epcInfo.children.length}`);
        
        // Recursively process children
        let maxChildDepth = currentDepth;
        epcInfo.children.forEach(childEpc => {
          const childDepth = calculateDepthAndLevel(childEpc, currentDepth + 1, new Set(visited));
          maxChildDepth = Math.max(maxChildDepth, childDepth);
        });
        
        visited.delete(epc);
        return maxChildDepth;
      };

      // Calculate levels for all root nodes
      const allChildren = new Set(Array.from(parentChildMap.values()).flat());
      const rootEpcs = Array.from(parentChildMap.keys()).filter(epc => !allChildren.has(epc));
      
      console.log(`Processing ${rootEpcs.length} root EPCs for level determination`);
      rootEpcs.forEach(rootEpc => {
        calculateDepthAndLevel(rootEpc, 0);
      });

      console.log(`✅ Completed advanced level detection for ${epcTypeMap.size} EPCs`);

      // Step 2.6: Refine EACH vs container classification using the EPCClass Vocabulary.
      // The EACH layer is the LOWEST indicator digit for a product (not always 0).
      // Any commissioned SGTIN whose indicator digit is higher than its product's
      // each-indicator is a container (CASE), even when no AggregationEvent nests it.
      // We only promote default EACH items to containers; we never demote a
      // container that aggregation already identified.
      if (eachIndicatorMap.size > 0) {
        let reclassifiedCount = 0;
        allSgtinEpcs.forEach((serialData) => {
          const { companyPrefix, itemReference } = serialData;
          if (!companyPrefix || !itemReference || itemReference.length < 2) return;
          const indicatorDigit = itemReference.charAt(0);
          const baseGTIN = `${companyPrefix}${itemReference.substring(1)}`;
          const eachIndicator = eachIndicatorMap.get(baseGTIN);
          if (eachIndicator === undefined) return;
          if (
            serialData.level === 'EACH' &&
            parseInt(indicatorDigit, 10) > parseInt(eachIndicator, 10)
          ) {
            serialData.level = 'CASE';
            const info = epcTypeMap.get(serialData.epc);
            if (info) info.level = 'CASE';
            reclassifiedCount++;
          }
        });
        if (reclassifiedCount > 0) {
          console.log(`🔧 Reclassified ${reclassifiedCount} higher-indicator SGTINs from EACH to CASE based on EPCClass Vocabulary`);
        }
      }

      // Step 3: Build the tree structure recursively using advanced level detection
      const buildNode = (epc, depth = 0) => {
        const epcInfo = epcTypeMap.get(epc);
        const isSerial = allSgtinEpcs.has(epc);
        const children = parentChildMap.get(epc) || [];
        
        let level = epcInfo?.level || 'UNKNOWN';
        let displayName = epc;
        let serialData = null;
        
        if (isSerial) {
          serialData = allSgtinEpcs.get(epc);
          level = serialData.level; // Use the level determined by advanced detection
          
          // Create appropriate display names based on level
          switch (level) {
            case 'CASE':
              displayName = `Case: ${serialData.serialNumber}`;
              break;
            case 'INNER_CASE':
              displayName = `Inner Case: ${serialData.serialNumber}`;
              break;
            case 'EACH':
              displayName = `Serial: ${serialData.serialNumber}`;
              break;
            default:
              displayName = `Serial: ${serialData.serialNumber}`;
          }
        } else {
          // Non-serial containers
          const identifier = epc.split(':').pop() || epc.substring(epc.lastIndexOf('.') + 1) || epc.substring(epc.lastIndexOf('/') + 1) || epc;
          
          switch (level) {
            case 'SSCC':
              displayName = `SSCC: ${identifier}`;
              break;
            case 'NESTED_SSCC':
              displayName = `Nested SSCC: ${identifier}`;
              break;
            case 'CASE':
              displayName = `Case: ${identifier}`;
              break;
            case 'INNER_CASE':
              displayName = `Inner Case: ${identifier}`;
              break;
            default:
              displayName = `Container: ${identifier}`;
          }
        }

        const node = {
          identifier: epc,
          level,
          displayName,
          children: [],
          depth: epcInfo?.depth || depth,
          isExpanded: true, // Default to expanded
          ...(serialData || {
            serialNumber: null,
            gtin: 'N/A',
            lot: 'N/A',
            expiration: 'N/A'
          })
        };

        // Recursively build children
        children.forEach(childEpc => {
          node.children.push(buildNode(childEpc, depth + 1));
        });

        console.log(`Built node: ${displayName} (${level}) with ${children.length} children at depth ${depth}`);
        return node;
      };

      // Step 4: Find root nodes (containers that are not children of other containers)
      const allParents = new Set([...parentChildMap.keys()]);
      const allChildEpcs = new Set([...parentChildMap.values()].flat());
      const hierarchyRoots = [...allParents].filter(parent => !allChildEpcs.has(parent));
      
      console.log(`\n=== ROOT NODE ANALYSIS ===`);
      console.log(`Total parents in aggregation events: ${allParents.size}`);
      console.log(`Total children in aggregation events: ${allChildEpcs.size}`);
      console.log(`Root containers (parents but not children): ${hierarchyRoots.length}`);
      
      hierarchyRoots.forEach(rootEpc => {
        const epcInfo = epcTypeMap.get(rootEpc);
        const isSerial = allSgtinEpcs.has(rootEpc);
        console.log(`  Root: ${rootEpc.split(':').pop()} | Type: ${epcInfo?.baseType} | Level: ${epcInfo?.level} | Is Serial: ${isSerial}`);
      });

      const hierarchy = hierarchyRoots.map(rootEpc => buildNode(rootEpc));

      // Step 4.5: Check for missing CASE serial numbers after initial hierarchy build
      const initialSerialsInHierarchy = new Set();
      const collectInitialSerials = (nodes) => {
        nodes.forEach(node => {
          if ((node.level === 'EACH' || node.level === 'CASE' || node.level === 'INNER_CASE') && node.serialNumber) {
            initialSerialsInHierarchy.add(node.epc);
          }
          if (node.children) {
            collectInitialSerials(node.children);
          }
        });
      };
      collectInitialSerials(hierarchy);

      // Find CASE serial numbers that have children but aren't in the hierarchy
      const missingCaseSerials = [];
      allSgtinEpcs.forEach((serialData, epc) => {
        if (serialData.level === 'CASE' && !initialSerialsInHierarchy.has(epc)) {
          if (parentChildMap.has(epc)) {
            console.log(`Found missing CASE serial: ${serialData.serialNumber} (${epc.split(':').pop()}) with ${parentChildMap.get(epc).length} children`);
            missingCaseSerials.push(epc);
          }
        }
      });

      // Add missing CASE serials as additional root nodes
      if (missingCaseSerials.length > 0) {
        console.log(`Adding ${missingCaseSerials.length} missing CASE serials as additional root nodes`);
        missingCaseSerials.forEach(caseEpc => {
          hierarchy.push(buildNode(caseEpc));
        });
      }

      // Step 5: Handle orphaned serials (serials not included in hierarchy)
      const allSerialsInHierarchy = new Set();
      const collectSerials = (nodes) => {
        nodes.forEach(node => {
          // Include ALL serial numbers in hierarchy (EACH, CASE, INNER_CASE)
          if ((node.level === 'EACH' || node.level === 'CASE' || node.level === 'INNER_CASE') && node.serialNumber) {
            allSerialsInHierarchy.add(node.epc);
          }
          if (node.children) {
            collectSerials(node.children);
          }
        });
      };
      collectSerials(hierarchy);

      const orphanedSerials = [...allSgtinEpcs.keys()].filter(epc => !allSerialsInHierarchy.has(epc));
      
      console.log(`Hierarchy contains ${allSerialsInHierarchy.size} serial numbers`);
      console.log(`Found ${orphanedSerials.length} truly orphaned serials:`, orphanedSerials.map(epc => allSgtinEpcs.get(epc)?.serialNumber));
      
      if (orphanedSerials.length > 0) {
        console.log(`Found ${orphanedSerials.length} orphaned serials, adding as flat list`);
        const orphanedNode = {
          identifier: 'orphaned',
          level: 'ROOT',
          displayName: `Orphaned Serial Numbers (${orphanedSerials.length})`,
          children: orphanedSerials.map(epc => ({
            ...allSgtinEpcs.get(epc),
            identifier: epc,
            displayName: `Serial: ${allSgtinEpcs.get(epc).serialNumber}`,
            children: []
          })),
          isExpanded: true,
          serialNumber: null,
          ndc: 'N/A',
          lot: 'N/A',
          expiration: 'N/A'
        };
        hierarchy.push(orphanedNode);
      }

      // Count only EACH level items (not CASE or INNER_CASE)
      const eachLevelSerials = Array.from(allSgtinEpcs.values()).filter(serialData => serialData.level === 'EACH').length;
      console.log(`✅ Parsing complete. EACH level serials: ${eachLevelSerials}, Total items: ${allSgtinEpcs.size}, Hierarchy nodes: ${hierarchy.length}`);

      return {
        originalXml: xmlContent,
        hierarchy,
        serialCount: eachLevelSerials,
        allSerials: allSgtinEpcs,
        eachIndicatorMap,
        productNdcMap,
        productNameMap,
        glnInfo
      };
      
    } catch (error) {
      console.error('❌ Error parsing EPCIS:', error);
      throw error;
    }
  };

  const toggleSerialSelection = (serialNumber, isSelected) => {
    const newSelected = new Set(selectedSerials);
    if (isSelected) {
      newSelected.add(serialNumber);
    } else {
      newSelected.delete(serialNumber);
    }
    setSelectedSerials(newSelected);
  };

  const toggleNodeSelection = (node, isSelected) => {
    console.log(`toggleNodeSelection called for ${node.displayName} (${node.level}): ${isSelected ? 'SELECT' : 'DESELECT'}`);
    const newSelected = new Set(selectedSerials);
    
    const updateNodeSerials = (currentNode, select) => {
      // Only select/deselect EACH level items
      if (currentNode.serialNumber && currentNode.level === 'EACH') {
        if (select) {
          newSelected.add(currentNode.serialNumber);
          console.log(`  ✅ Selected serial: ${currentNode.serialNumber} (${currentNode.level})`);
        } else {
          newSelected.delete(currentNode.serialNumber);
          console.log(`  ❌ Deselected serial: ${currentNode.serialNumber} (${currentNode.level})`);
        }
      }
      
      // Recursively update all children
      if (currentNode.children && currentNode.children.length > 0) {
        console.log(`  Updating ${currentNode.children.length} children of ${currentNode.displayName}`);
        currentNode.children.forEach(child => updateNodeSerials(child, select));
      }
    };
    
    updateNodeSerials(node, isSelected);
    console.log(`Total selected serials after toggle: ${newSelected.size}`);
    setSelectedSerials(newSelected);
  };

  const selectAll = () => {
    const allSerials = new Set();
    const collectSerials = (items) => {
      items.forEach(item => {
        // Only include EACH level items
        if (item.level === 'EACH' && item.serialNumber) {
          allSerials.add(item.serialNumber);
        }
        if (item.children && item.children.length > 0) {
          collectSerials(item.children);
        }
      });
    };
    if (parsedData?.hierarchy) {
      collectSerials(parsedData.hierarchy);
    }
    setSelectedSerials(allSerials);
  };

  const selectNone = () => {
    setSelectedSerials(new Set());
  };

  // Get GTINs with indicator digit 0 (EACHES) and map them to all related packaging levels
  const getEachGTINsWithAllLevels = () => {
    if (!parsedData?.allSerials) return new Map();
    
    // First, organize all items by their base GTIN (for counting all packaging levels)
    const baseProductMapAll = new Map(); // base GTIN -> array of all items (all levels)
    const baseProductMapEach = new Map(); // base GTIN -> array of EACH level items only
    
    parsedData.allSerials.forEach((serialData, epc) => {
      const { gtin, companyPrefix, itemReference, serialNumber, level } = serialData;
      
      // Extract indicator digit (first digit of item reference)
      const indicatorDigit = itemReference && itemReference.length > 0 ? itemReference.charAt(0) : '?';
      
      // Create base GTIN (company prefix + item reference without indicator digit)
      const itemRefWithoutIndicator = itemReference && itemReference.length > 1 ? itemReference.substring(1) : itemReference;
      const baseGTIN = `${companyPrefix}${itemRefWithoutIndicator}`;
      
      // Track all items for level counting
      if (!baseProductMapAll.has(baseGTIN)) {
        baseProductMapAll.set(baseGTIN, []);
      }
      baseProductMapAll.get(baseGTIN).push({
        gtin,
        indicatorDigit,
        level
      });
      
      // Track only EACH items for selection
      if (level === 'EACH') {
        if (!baseProductMapEach.has(baseGTIN)) {
          baseProductMapEach.set(baseGTIN, []);
        }
        baseProductMapEach.get(baseGTIN).push({
          gtin,
          indicatorDigit,
          serialNumber
        });
      }
    });
    
    // Now create a map of indicator-0 GTINs with EACH serial numbers and ALL level counts
    const eachGTINsMap = new Map();
    
    baseProductMapEach.forEach((eachVariants, baseGTIN) => {
      // Determine the EACH layer: the variant with the LOWEST indicator digit.
      // The indicator digit is NOT always 0 — the lowest indicator present for a
      // product (per the EPCClass Vocabulary) represents the EACH layer.
      const vocabEachIndicator = parsedData.eachIndicatorMap?.get(baseGTIN);
      let eachVariant;
      if (vocabEachIndicator !== undefined) {
        eachVariant = eachVariants.find(v => v.indicatorDigit === vocabEachIndicator);
      }
      if (!eachVariant) {
        // Fallback: lowest indicator digit among the EACH-level variants present.
        eachVariant = [...eachVariants].sort((a, b) => {
          const ai = a.indicatorDigit === '?' ? 99 : parseInt(a.indicatorDigit, 10);
          const bi = b.indicatorDigit === '?' ? 99 : parseInt(b.indicatorDigit, 10);
          return ai - bi;
        })[0];
      }
      
      if (eachVariant) {
        // Collect ALL EACH level serial numbers
        const allSerialNumbers = eachVariants.map(v => v.serialNumber);
        
        // Count unique indicator digits across ALL levels (EACH, CASE, INNER_CASE)
        const allVariants = baseProductMapAll.get(baseGTIN) || [];
        const uniqueIndicators = new Set(allVariants.map(v => v.indicatorDigit));
        
        eachGTINsMap.set(eachVariant.gtin, {
          serialNumbers: allSerialNumbers,
          totalVariants: uniqueIndicators.size,
          ndc: parsedData.productNdcMap?.get(baseGTIN) || null,
          productName: parsedData.productNameMap?.get(baseGTIN) || null
        });
      }
    });
    
    return eachGTINsMap;
  };

  // Toggle selection for a GTIN and all its packaging level variants
  const toggleGTINSelection = (gtin, serialNumbers) => {
    const newSelected = new Set(selectedSerials);
    
    // Check if all serials for this GTIN (across all packaging levels) are currently selected
    const allSelected = serialNumbers.every(serial => newSelected.has(serial));
    
    if (allSelected) {
      // Deselect all
      serialNumbers.forEach(serial => newSelected.delete(serial));
    } else {
      // Select all
      serialNumbers.forEach(serial => newSelected.add(serial));
    }
    
    setSelectedSerials(newSelected);
  };

  // Get selection state for a GTIN
  const getGTINSelectionState = (serialNumbers) => {
    const selectedCount = serialNumbers.filter(serial => selectedSerials.has(serial)).length;
    if (selectedCount === 0) return 'none';
    if (selectedCount === serialNumbers.length) return 'all';
    return 'partial';
  };

  const toggleNodeExpansion = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    const wasExpanded = newExpanded.has(nodeId);
    
    if (wasExpanded) {
      newExpanded.delete(nodeId);
      console.log(`Collapsing node: ${nodeId}`);
    } else {
      newExpanded.add(nodeId);
      console.log(`Expanding node: ${nodeId}`);
      // If user is manually expanding a node, reset the "collapsed all" state FIRST
      if (isCollapsedAll) {
        setIsCollapsedAll(false);
      }
    }
    
    console.log('New expanded nodes:', Array.from(newExpanded));
    setExpandedNodes(newExpanded);
  };

  const expandAll = () => {
    const allNodeIds = new Set();
    const collectNodeIds = (nodes) => {
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          allNodeIds.add(node.identifier);
          collectNodeIds(node.children);
        }
      });
    };
    if (parsedData?.hierarchy) {
      collectNodeIds(parsedData.hierarchy);
    }
    setExpandedNodes(allNodeIds);
    setIsCollapsedAll(false);
  };

  const collapseAll = () => {
    setExpandedNodes(new Set());
    setIsCollapsedAll(true);
  };

  const renderTreeNode = (node, depth = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    // Simply check if the node is in expandedNodes set
    const isExpanded = expandedNodes.has(node.identifier);
    const isSerial = node.level === 'EACH';
    const isSelected = isSerial ? selectedSerials.has(node.serialNumber) : false;
    
    // Check if all children are selected
    const childSerials = [];
    const collectChildSerials = (currentNode) => {
      // Only include EACH level items in the count
      if (currentNode.level === 'EACH' && currentNode.serialNumber) {
        childSerials.push(currentNode.serialNumber);
      }
      if (currentNode.children) {
        currentNode.children.forEach(child => collectChildSerials(child));
      }
    };
    collectChildSerials(node);
    
    const allChildrenSelected = childSerials.length > 0 && childSerials.every(serial => selectedSerials.has(serial));
    const someChildrenSelected = childSerials.some(serial => selectedSerials.has(serial));

    return (
      <div key={node.identifier}>
        <div 
          className={`flex items-start sm:items-center py-2 px-2 hover:bg-gray-50 rounded`}
          style={{ paddingLeft: `${depth * 12 + 8}px` }}
        >
          {/* Expand/Collapse Button */}
          {hasChildren && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log(`Toggling expansion for ${node.identifier}: currently ${isExpanded ? 'expanded' : 'collapsed'}`);
                toggleNodeExpansion(node.identifier);
              }}
              className="mr-2 p-1 hover:bg-gray-200 rounded focus:outline-none flex-shrink-0"
            >
              {isExpanded ? (
                <ChevronDown size={16} className="text-gray-600" />
              ) : (
                <ChevronRight size={16} className="text-gray-600" />
              )}
            </button>
          )}
          {!hasChildren && <div className="w-6 mr-2 flex-shrink-0" />}
          
          {/* Checkbox */}
          <input
            type="checkbox"
            checked={isSerial ? isSelected : allChildrenSelected}
            onChange={(e) => {
              if (isSerial) {
                toggleSerialSelection(node.serialNumber, e.target.checked);
              } else {
                toggleNodeSelection(node, e.target.checked);
              }
            }}
            ref={input => {
              if (input && !isSerial && someChildrenSelected && !allChildrenSelected) {
                input.indeterminate = true;
              }
            }}
            className="mr-2 sm:mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded flex-shrink-0"
          />
          
          {/* Level Badge */}
          <span className={`inline-block px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-medium rounded mr-2 sm:mr-3 flex-shrink-0 ${
            node.level === 'SSCC' ? 'bg-purple-100 text-purple-800' :
            node.level === 'NESTED_SSCC' ? 'bg-purple-200 text-purple-900' :
            node.level === 'CASE' ? 'bg-blue-100 text-blue-800' :
            node.level === 'INNER_CASE' ? 'bg-indigo-100 text-indigo-800' :
            node.level === 'EACH' ? 'bg-green-100 text-green-800' :
            node.level === 'ROOT' ? 'bg-orange-100 text-orange-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {node.level === 'NESTED_SSCC' ? 'NESTED SSCC' : 
             node.level === 'INNER_CASE' ? 'INNER CASE' : 
             node.level}
          </span>
          
          {/* Node Info */}
          <div className="flex-1 min-w-0">
            <div className="font-medium text-gray-900 text-xs sm:text-sm break-all">
              {node.displayName || node.identifier}
              {/* Display GTIN inline for all levels that have it */}
              {node.gtin && (
                <span className="ml-2 font-normal text-gray-600">
                  | GTIN: {node.gtin}
                </span>
              )}
            </div>
            {isSerial && (
              <div className="text-xs sm:text-sm text-gray-500 mt-1 space-y-0.5">
                <div className="break-all">Lot: {node.lot} | Exp: {node.expiration}</div>
              </div>
            )}
            {hasChildren && !isSerial && (
              <div className="text-xs sm:text-sm text-gray-500 mt-1">
                {childSerials.length} serial numbers
              </div>
            )}
          </div>
        </div>
        
        {/* Children */}
        {hasChildren && isExpanded && (
          <div>
            {node.children.map(child => renderTreeNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const processAndDownload = async () => {
    if (!parsedData || !selectedFile) return;
    
    setIsProcessing(true);
    try {
      // Create filtered EPCIS XML by removing unselected serial numbers
      const filteredXml = removeUnselectedSerials(parsedData.originalXml, selectedSerials);
      
      // Create download
      const blob = new Blob([filteredXml], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Create filtered filename
      const originalName = selectedFile.name;
      const nameWithoutExt = originalName.replace(/\.xml$/i, '');
      link.download = `${nameWithoutExt}_filtered.xml`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      showToast(`Downloaded filtered EPCIS with ${selectedSerials.size} selected serial numbers`, 'success');
      
    } catch (error) {
      console.error('Error processing file:', error);
      showToast('Error processing file for download', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const removeUnselectedSerials = (originalXml, selectedSerials) => {
    console.log('🔄 Starting filtered EPCIS XML generation...');
    console.log(`Selected serials: ${selectedSerials.size}`);
    
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(originalXml, 'text/xml');
    
    // Build a map of ALL EPCs (both SGTIN and container EPCs) for reference
    const allSgtinEpcs = new Map(); // SGTIN EPCs only
    const allEpcsMap = new Map(); // ALL EPCs (SGTIN + containers)
    const allEpcs = xmlDoc.querySelectorAll('epc, epcis\\:epc');
    
    allEpcs.forEach(epcElement => {
      const epcText = epcElement.textContent?.trim();
      if (epcText) {
        // Add to comprehensive EPC map
        allEpcsMap.set(epcText, {
          epc: epcText,
          element: epcElement,
          isSgtin: epcText.includes('sgtin'),
          isContainer: !epcText.includes('sgtin')
        });
        
        // If it's an SGTIN, also add to SGTIN-specific map
        if (epcText.includes('sgtin')) {
          const match = epcText.match(/urn:epc:id:sgtin:([^.]+)\.([^.]+)\.(.+)/);
          if (match) {
            const serialNumber = match[3];
            allSgtinEpcs.set(epcText, {
              serialNumber,
              isSelected: selectedSerials.has(serialNumber),
              element: epcElement
            });
          }
        }
      }
    });

    console.log(`Total EPCs found: ${allEpcsMap.size} (${allSgtinEpcs.size} SGTINs, ${allEpcsMap.size - allSgtinEpcs.size} containers)`);

    // Build complete hierarchy relationships and identify ALL ancestors that should be preserved
    const parentChildMap = new Map(); // parent EPC -> array of child EPCs
    const childParentMap = new Map(); // child EPC -> parent EPC
    const containersToPreserve = new Set(); // All containers (CASEs, SSCCs) that need to be preserved
    
    const aggEventsForAnalysis = xmlDoc.querySelectorAll('AggregationEvent, epcis\\:AggregationEvent');
    
    // First pass: Build parent-child and child-parent maps
    aggEventsForAnalysis.forEach(event => {
      const action = event.querySelector('action, epcis\\:action')?.textContent?.trim();
      const parentEpc = event.querySelector('parentID, epcis\\:parentID')?.textContent?.trim();
      const childEpcs = Array.from(event.querySelectorAll('childEPCs epc, epcis\\:childEPCs epcis\\:epc'))
        .map(e => e.textContent?.trim())
        .filter(epc => epc);
      
      if (action === 'ADD' && parentEpc && childEpcs.length > 0) {
        parentChildMap.set(parentEpc, childEpcs);
        childEpcs.forEach(childEpc => {
          childParentMap.set(childEpc, parentEpc);
        });
      }
    });
    
    // Second pass: For each selected serial, traverse up the hierarchy and mark ALL ancestors for preservation
    const markAncestorsForPreservation = (epc) => {
      const visited = new Set();
      let currentEpc = epc;
      
      console.log(`  Starting ancestor traversal for: ${epc.split(':').pop()}`);
      
      while (currentEpc && !visited.has(currentEpc)) {
        visited.add(currentEpc);
        const parentEpc = childParentMap.get(currentEpc);
        
        if (parentEpc) {
          containersToPreserve.add(parentEpc);
          
          // Log what we're preserving and why
          const parentSgtinInfo = allSgtinEpcs.get(parentEpc);
          const parentEpcInfo = allEpcsMap.get(parentEpc);
          
          if (parentSgtinInfo) {
            console.log(`    Preserving SGTIN container serial ${parentSgtinInfo.serialNumber} (ancestor)`);
          } else if (parentEpcInfo) {
            console.log(`    Preserving container ${parentEpc.split(':').pop()} (ancestor)`);
          }
          
          currentEpc = parentEpc; // Continue traversing up
        } else {
          console.log(`    Reached top of hierarchy at ${currentEpc.split(':').pop()}`);
          break; // Reached the top of the hierarchy
        }
      }
    };
    
    // Mark ancestors for all selected serials
    console.log('🔍 Analyzing hierarchy to preserve all ancestors...');
    allSgtinEpcs.forEach((epcInfo, epcText) => {
      if (epcInfo.isSelected) {
        console.log(`Marking ancestors for selected serial: ${epcInfo.serialNumber}`);
        markAncestorsForPreservation(epcText);
      }
    });
    
    console.log(`Total containers to preserve: ${containersToPreserve.size}`);

    // Step 1: Handle ObjectEvents (Commissioning Events and other ObjectEvents)
    const objectEvents = xmlDoc.querySelectorAll('ObjectEvent, epcis\\:ObjectEvent');
    console.log(`Processing ${objectEvents.length} ObjectEvents...`);
    
    objectEvents.forEach((event, eventIndex) => {
      const action = event.querySelector('action, epcis\\:action')?.textContent?.trim();
      
      if (action === 'ADD') { // Commissioning event
        const epcList = event.querySelector('epcList, epcis\\:epcList');
        if (epcList) {
          const eventEpcs = Array.from(epcList.querySelectorAll('epc, epcis\\:epc'));
          let shouldKeepEvent = false;
          
          // Check each EPC in this commissioning event
          eventEpcs.forEach(epcElement => {
            const epcText = epcElement.textContent?.trim();
            const sgtinInfo = allSgtinEpcs.get(epcText);
            const epcInfo = allEpcsMap.get(epcText);
            
            if (epcInfo) {
              // Keep EPC if it's directly selected OR if it's a container that should be preserved
              const isDirectlySelected = sgtinInfo && sgtinInfo.isSelected;
              const shouldKeepEpc = isDirectlySelected || containersToPreserve.has(epcText);
              
              if (shouldKeepEpc) {
                shouldKeepEvent = true;
                if (isDirectlySelected) {
                  console.log(`  Keeping commissioning event for selected serial: ${sgtinInfo.serialNumber}`);
                } else if (epcInfo.isSgtin && sgtinInfo) {
                  console.log(`  Keeping commissioning event for SGTIN container with selected descendants: ${sgtinInfo.serialNumber}`);
                } else {
                  console.log(`  Keeping commissioning event for container with selected descendants: ${epcText.split(':').pop()}`);
                }
              } else if (sgtinInfo) {
                // Remove this specific SGTIN EPC from the event
                console.log(`  Removing unselected serial from commissioning event: ${sgtinInfo.serialNumber}`);
                epcElement.remove();
              } else {
                // Remove this container EPC from the event
                console.log(`  Removing unpreserved container from commissioning event: ${epcText.split(':').pop()}`);
                epcElement.remove();
              }
            }
          });
          
          // If no EPCs left or none were selected, remove entire event
          const remainingEpcs = epcList.querySelectorAll('epc, epcis\\:epc');
          if (remainingEpcs.length === 0 || !shouldKeepEvent) {
            console.log(`  Removing entire commissioning event ${eventIndex + 1} (no selected EPCs)`);
            event.remove();
          }
        }
      } else {
        // Handle other ObjectEvents (like shipping events with action="OBSERVE")
        const epcList = event.querySelector('epcList, epcis\\:epcList');
        if (epcList) {
          const eventEpcs = Array.from(epcList.querySelectorAll('epc, epcis\\:epc'));
          let shouldKeepEvent = false;
          
          // Check each EPC in this event
          eventEpcs.forEach(epcElement => {
            const epcText = epcElement.textContent?.trim();
            const epcInfo = allEpcsMap.get(epcText);
            
            // Only keep EPCs that are in containersToPreserve (have selected descendants)
            if (containersToPreserve.has(epcText)) {
              shouldKeepEvent = true;
              console.log(`  Keeping ${action} event for container with selected descendants: ${epcText.split(':').pop()}`);
            } else {
              // Remove this EPC from the event
              console.log(`  Removing container without selected descendants from ${action} event: ${epcText.split(':').pop()}`);
              epcElement.remove();
            }
          });
          
          // If no EPCs left or none were selected, remove entire event
          const remainingEpcs = epcList.querySelectorAll('epc, epcis\\:epc');
          if (remainingEpcs.length === 0 || !shouldKeepEvent) {
            console.log(`  Removing entire ${action} event ${eventIndex + 1} (no containers with selected descendants)`);
            event.remove();
          }
        }
      }
    });

    // Step 1.5: Handle TransactionEvents (files containing multiple POs / DESADVs).
    // Each TransactionEvent ties a set of EPCs to a business transaction (PO/DESADV).
    // Remove unselected EPCs; drop the whole event if nothing selected remains.
    // Track which bizTransactions still apply so aggregate shipping events can be cleaned.
    const survivingBizTransactions = new Set();
    const transactionEvents = xmlDoc.querySelectorAll('TransactionEvent, epcis\\:TransactionEvent');
    console.log(`Processing ${transactionEvents.length} TransactionEvents...`);

    transactionEvents.forEach((txEvent, eventIndex) => {
      const epcList = txEvent.querySelector('epcList, epcis\\:epcList');
      if (!epcList) return;

      const eventEpcs = Array.from(epcList.querySelectorAll('epc, epcis\\:epc'));
      let keptEpcs = 0;

      eventEpcs.forEach(epcElement => {
        const epcText = epcElement.textContent?.trim();
        const sgtinInfo = allSgtinEpcs.get(epcText);
        const isDirectlySelected = sgtinInfo && sgtinInfo.isSelected;
        const shouldKeepEpc = isDirectlySelected || containersToPreserve.has(epcText);
        if (shouldKeepEpc) {
          keptEpcs++;
        } else {
          epcElement.remove();
        }
      });

      if (keptEpcs === 0) {
        console.log(`  Removing TransactionEvent ${eventIndex + 1} (no selected EPCs)`);
        txEvent.remove();
      } else {
        // This transaction survives; remember its bizTransaction value(s)
        Array.from(txEvent.querySelectorAll('bizTransaction, epcis\\:bizTransaction')).forEach(bt => {
          const val = bt.textContent?.trim();
          if (val) survivingBizTransactions.add(val);
        });
        console.log(`  Keeping TransactionEvent ${eventIndex + 1} (${keptEpcs} selected EPCs kept)`);
      }
    });

    // Step 1.6: Clean aggregate shipping ObjectEvents (action="OBSERVE"):
    //  - drop bizTransactions that no longer tie to any surviving TransactionEvent
    //  - drop directPurchaseEPCs that reference unpreserved containers
    const shippingObjectEvents = xmlDoc.querySelectorAll('ObjectEvent, epcis\\:ObjectEvent');
    shippingObjectEvents.forEach(event => {
      const action = event.querySelector('action, epcis\\:action')?.textContent?.trim();
      if (action === 'ADD') return; // commissioning events handled in Step 1

      // Clean bizTransactionList (only when we have surviving info to compare against)
      const bizTxList = event.querySelector('bizTransactionList, epcis\\:bizTransactionList');
      if (bizTxList && survivingBizTransactions.size > 0) {
        Array.from(bizTxList.querySelectorAll('bizTransaction, epcis\\:bizTransaction')).forEach(bt => {
          const val = bt.textContent?.trim();
          if (val && !survivingBizTransactions.has(val)) {
            console.log(`  Removing bizTransaction not tied to selected items: ${val}`);
            bt.remove();
          }
        });
      }

      // Clean directPurchaseEPCs (remove containers/serials that aren't preserved)
      Array.from(event.querySelectorAll('directPurchaseEPCs epc, directPurchaseEPCs epcis\\:epc')).forEach(epcElement => {
        const epcText = epcElement.textContent?.trim();
        const sgtinInfo = allSgtinEpcs.get(epcText);
        const isDirectlySelected = sgtinInfo && sgtinInfo.isSelected;
        if (!isDirectlySelected && !containersToPreserve.has(epcText)) {
          console.log(`  Removing directPurchase EPC without selected descendants: ${epcText.split(':').pop()}`);
          epcElement.remove();
        }
      });
      // Remove a now-empty directPurchase block to keep the file schema-valid
      Array.from(event.querySelectorAll('directPurchaseEPCs')).forEach(dpe => {
        if (dpe.querySelectorAll('epc, epcis\\:epc').length === 0 && dpe.parentElement) {
          dpe.parentElement.remove();
        }
      });
    });

    // Step 2: Handle AggregationEvents - preserve events for preserved containers
    const aggregationEvents = xmlDoc.querySelectorAll('AggregationEvent, epcis\\:AggregationEvent');
    console.log(`Processing ${aggregationEvents.length} AggregationEvents...`);
    
    aggregationEvents.forEach((aggEvent, eventIndex) => {
      const action = aggEvent.querySelector('action, epcis\\:action')?.textContent?.trim();
      const parentID = aggEvent.querySelector('parentID, epcis\\:parentID')?.textContent?.trim();
      const childEPCs = aggEvent.querySelector('childEPCs, epcis\\:childEPCs');
      
      if (action === 'ADD' && childEPCs && parentID) {
        const childEpcElements = Array.from(childEPCs.querySelectorAll('epc, epcis\\:epc'));
        let keptChildren = 0;
        let totalChildren = childEpcElements.length;
        
        console.log(`  AggEvent ${eventIndex + 1}: Parent ${parentID.split(':').pop()} with ${totalChildren} children`);
        
        // Process each child EPC
        childEpcElements.forEach(childEpcElement => {
          const childEpcText = childEpcElement.textContent?.trim();
          const childSgtinInfo = allSgtinEpcs.get(childEpcText);
          const childEpcInfo = allEpcsMap.get(childEpcText);
          
          if (childEpcInfo) {
            // Keep child if it's directly selected OR if it's a container that should be preserved
            const isDirectlySelected = childSgtinInfo && childSgtinInfo.isSelected;
            const shouldKeepChild = isDirectlySelected || containersToPreserve.has(childEpcText);
            
            if (shouldKeepChild) {
              keptChildren++;
              if (isDirectlySelected) {
                console.log(`    Keeping directly selected child: ${childSgtinInfo.serialNumber}`);
              } else if (childSgtinInfo) {
                console.log(`    Keeping SGTIN container child with descendants: ${childSgtinInfo.serialNumber}`);
              } else {
                console.log(`    Keeping container child with descendants: ${childEpcText.split(':').pop()}`);
              }
            } else {
              if (childSgtinInfo) {
                console.log(`    Removing unselected child: ${childSgtinInfo.serialNumber}`);
              } else {
                console.log(`    Removing unpreserved container child: ${childEpcText.split(':').pop()}`);
              }
              childEpcElement.remove();
            }
          }
        });
        
        // Decide whether to keep the aggregation event
        if (keptChildren === 0) {
          console.log(`    Removing aggregation event (no children kept)`);
          aggEvent.remove();
        } else {
          console.log(`    Keeping aggregation event (${keptChildren}/${totalChildren} children kept)`);
        }
      }
    });

    // Step 3: Clean up empty elements and whitespace
    console.log('🧹 Cleaning up empty elements...');
    
    // Remove empty epcList elements
    const epcLists = xmlDoc.querySelectorAll('epcList, epcis\\:epcList');
    epcLists.forEach(epcList => {
      const epcs = epcList.querySelectorAll('epc, epcis\\:epc');
      if (epcs.length === 0) {
        // Remove the parent event if epcList is empty
        const parentEvent = epcList.closest('ObjectEvent, epcis\\:ObjectEvent, AggregationEvent, epcis\\:AggregationEvent');
        if (parentEvent) {
          console.log('  Removing event with empty epcList');
          parentEvent.remove();
        }
      }
    });
    
    // Remove empty childEPCs elements
    const childEPCsElements = xmlDoc.querySelectorAll('childEPCs, epcis\\:childEPCs');
    childEPCsElements.forEach(childEPCs => {
      const children = childEPCs.querySelectorAll('epc, epcis\\:epc');
      if (children.length === 0) {
        const parentEvent = childEPCs.closest('AggregationEvent, epcis\\:AggregationEvent');
        if (parentEvent) {
          console.log('  Removing aggregation event with empty childEPCs');
          parentEvent.remove();
        }
      }
    });

    // Step 4: Remove empty text nodes and normalize whitespace
    const removeEmptyTextNodes = (node) => {
      for (let i = node.childNodes.length - 1; i >= 0; i--) {
        const child = node.childNodes[i];
        if (child.nodeType === Node.TEXT_NODE) {
          if (child.textContent.trim() === '') {
            node.removeChild(child);
          }
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          removeEmptyTextNodes(child);
        }
      }
    };
    
    removeEmptyTextNodes(xmlDoc);

    // Step 5: Remove VocabularyElements for filtered-out GTINs
    console.log('🗑️ Cleaning up VocabularyElements for filtered-out GTINs...');
    const epcClassVocabularies = xmlDoc.querySelectorAll('Vocabulary[type="urn:epcglobal:epcis:vtype:EPCClass"], epcis\\:Vocabulary[type="urn:epcglobal:epcis:vtype:EPCClass"]');
    
    // Build a set of GTINs from both selected EACH items AND preserved containers (CASE/INNER_CASE)
    const selectedGTINs = new Set();
    
    // Add GTINs from selected EACH items
    allSgtinEpcs.forEach((epcInfo, epcText) => {
      if (epcInfo.isSelected) {
        // Extract GTIN from the SGTIN
        const match = epcText.match(/urn:epc:id:sgtin:([^.]+)\.([^.]+)\.(.+)/);
        if (match) {
          const companyPrefix = match[1];
          const itemReference = match[2];
          const gtin = companyPrefix + itemReference;
          selectedGTINs.add(gtin);
        }
      }
    });
    
    // Also add GTINs from preserved containers (CASE/INNER_CASE that are ancestors of selected items)
    containersToPreserve.forEach(containerEpc => {
      const containerInfo = allSgtinEpcs.get(containerEpc);
      if (containerInfo) {
        // This is an SGTIN container (CASE or INNER_CASE)
        const match = containerEpc.match(/urn:epc:id:sgtin:([^.]+)\.([^.]+)\.(.+)/);
        if (match) {
          const companyPrefix = match[1];
          const itemReference = match[2];
          const gtin = companyPrefix + itemReference;
          selectedGTINs.add(gtin);
          console.log(`  Including GTIN from preserved container: ${gtin}`);
        }
      }
    });
    
    console.log(`Total GTINs to preserve in vocabulary: ${selectedGTINs.size}`);
    
    let vocabularyElementsRemoved = 0;
    epcClassVocabularies.forEach(vocabulary => {
      const vocabularyElements = vocabulary.querySelectorAll('VocabularyElement, epcis\\:VocabularyElement');
      
      vocabularyElements.forEach(vocabElement => {
        const idAttr = vocabElement.getAttribute('id');
        if (idAttr && idAttr.includes('sgtin')) {
          // Extract GTIN from the wildcard pattern: urn:epc:idpat:sgtin:030002.0762301.*
          const match = idAttr.match(/urn:epc:idpat:sgtin:([^.]+)\.([^.]+)\.\*/);
          if (match) {
            const companyPrefix = match[1];
            const itemReference = match[2];
            const gtin = companyPrefix + itemReference;
            
            // Check if this GTIN has ANY selected serials
            if (!selectedGTINs.has(gtin)) {
              console.log(`  Removing VocabularyElement for GTIN with no selected serials: ${gtin}`);
              vocabElement.remove();
              vocabularyElementsRemoved++;
            } else {
              console.log(`  Keeping VocabularyElement for GTIN with selected serials: ${gtin}`);
            }
          }
        }
      });
    });
    
    console.log(`Removed ${vocabularyElementsRemoved} VocabularyElements for GTINs with no selected serials`);

    // Step 6: Make SSCCs unique across filtered files by appending a UTC timestamp
    // (ddmmhhmmss) to each SSCC's serial reference. The same timestamp is applied to
    // EVERY reference of a given SSCC (commissioning, aggregation parentID, shipping
    // event, directPurchase) so the packaging hierarchy links stay intact.
    const now = new Date();
    const p2 = (n) => String(n).padStart(2, '0');
    const ssccSuffix = `${p2(now.getUTCDate())}${p2(now.getUTCMonth() + 1)}${p2(now.getUTCHours())}${p2(now.getUTCMinutes())}${p2(now.getUTCSeconds())}`;
    console.log(`🔖 Appending SSCC uniqueness suffix (UTC ddmmhhmmss): ${ssccSuffix}`);

    let ssccUpdated = 0;
    xmlDoc.querySelectorAll('epc, epcis\\:epc, parentID, epcis\\:parentID').forEach(el => {
      const text = el.textContent?.trim();
      if (text && text.includes(':sscc:')) {
        el.textContent = `${text}${ssccSuffix}`;
        ssccUpdated++;
      }
    });
    console.log(`🔖 Updated ${ssccUpdated} SSCC reference(s) with uniqueness suffix`);

    console.log('✅ Filtered EPCIS XML generation complete');

    // Serialize back to XML with proper formatting
    const serializer = new XMLSerializer();
    let xmlString = serializer.serializeToString(xmlDoc);
    
    // Basic XML formatting cleanup
    xmlString = xmlString.replace(/></g, '>\n<');
    
    return xmlString;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Exact copy from main app */}
      <header className="bg-blue-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex flex-col items-left">
              <div className="text-md font-medium tracking-tight flex gap-1 items-center">
                <img className="mt-1" src="https://rxerp.com/wp-content/uploads/2025/03/rxerp-logo-hero-white-e1736423722482.png" width="60px" /> 
                PharmaTrace
                <label className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-white bg-opacity-50 text-slate-700 not-italic">BETA</label>
              </div>
              {business && (
                <div className="text-sm opacity-90">
                  {business.pharmacy_name} • NPI: {business.npi_number}
                </div>
              )}
            </div>
            {user && (
              <UserDropdown
                user={user}
                showUserDropdown={showUserDropdown}
                setShowUserDropdown={setShowUserDropdown}
                dropdownRef={dropdownRef}
                setShowAccountSettings={setShowAccountSettings}
                setShowManageUsers={setShowManageUsers}
                setShowManageVendors={setShowManageVendors}
                logout={logout}
                currentPage="epcis-filter"
              />
            )}
          </div>
        </div>
      </header>

      {/* Page Title Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0">
              <button
                onClick={() => navigate('/')}
                className="flex items-center text-gray-600 hover:text-gray-900 sm:mr-6 w-fit"
              >
                <ArrowLeft size={18} className="mr-2" />
                <span className="text-sm sm:text-base">Back to Dashboard</span>
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
                  <LuPackageSearch size={24} className="mr-2 sm:mr-3 text-blue-600" />
                  <span className="leading-tight">EPCIS Serial Number Filter Tool</span>
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  Upload, filter, and download customized EPCIS files
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* File Upload Section */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 mb-6">
          <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-4">Upload EPCIS File</h2>
          <div 
            className={`border-2 border-dashed rounded-lg p-4 sm:p-6 transition-colors ${
              isDragOver 
                ? 'border-blue-400 bg-blue-50' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="text-center">
              <LuUpload className={`mx-auto h-10 w-10 sm:h-12 sm:w-12 mb-3 sm:mb-4 ${
                isDragOver ? 'text-blue-500' : 'text-gray-400'
              }`} />
              <div className="text-xs sm:text-sm text-gray-600">
                <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                  <span>Upload an EPCIS XML file</span>
                  <input
                    type="file"
                    accept=".xml"
                    onChange={handleFileUpload}
                    className="sr-only"
                    disabled={isProcessing}
                  />
                </label>
                <span className="ml-1">or drag and drop</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">XML files only (GS1 Rx EPCIS format)</p>
              {selectedFile && (
                <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-center">
                    <FileText className="text-green-600 mr-2 flex-shrink-0" size={18} />
                    <span className="text-sm sm:text-lg font-semibold text-green-800 break-all">{selectedFile.name}</span>
                  </div>
                </div>
              )}
              {isDragOver && (
                <p className="text-xs sm:text-sm text-blue-600 mt-2 font-medium">Drop file here to upload</p>
              )}
            </div>
          </div>
          {isProcessing && (
            <div className="mt-4 flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
              <span className="text-xs sm:text-sm text-gray-600">Processing EPCIS file...</span>
            </div>
          )}
        </div>

        {/* EPCIS Document Information Section */}
        {glnInfo && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-6">
            <h3 className="text-xs sm:text-sm font-medium text-blue-900 mb-2">EPCIS Document Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm">
              <div className="flex flex-col sm:flex-row sm:items-center">
                <span className="font-medium text-blue-800 sm:mr-2 mb-1 sm:mb-0">Sender GLN:</span>
                <span className="text-blue-700 font-mono break-all">{glnInfo.sender}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center">
                <span className="font-medium text-blue-800 sm:mr-2 mb-1 sm:mb-0">Receiver GLN:</span>
                <span className="text-blue-700 font-mono break-all">{glnInfo.receiver}</span>
              </div>
            </div>
          </div>
        )}

        {/* Tree View Section */}
        {showTree && parsedData && (
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:justify-between sm:items-center mb-6">
              <div>
                <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                  Select the serial numbers you want to keep
                </h2>
                <p className="text-xs font-bold sm:text-sm text-gray-600 mt-1">
                  {selectedSerials.size} of {parsedData.serialCount} serial numbers selected
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                {/* View Controls */}
                <div className="flex items-center space-x-1 border border-gray-300 rounded-md">
                  <button
                    onClick={expandAll}
                    className="flex-1 sm:flex-none px-3 py-1.5 text-xs sm:text-sm text-gray-700 hover:bg-gray-50 rounded-l-md border-r border-gray-300 flex items-center justify-center"
                    title="Expand all containers"
                  >
                    <ChevronDown size={14} className="mr-1" />
                    Expand
                  </button>
                  <button
                    onClick={collapseAll}
                    className="flex-1 sm:flex-none px-3 py-1.5 text-xs sm:text-sm text-gray-700 hover:bg-gray-50 rounded-r-md flex items-center justify-center"
                    title="Collapse all containers"
                  >
                    <ChevronRight size={14} className="mr-1" />
                    Collapse
                  </button>
                </div>

                {/* Selection Controls */}
                <div className="flex items-center space-x-1 border border-gray-300 rounded-md">
                  <button
                    onClick={selectAll}
                    className="flex-1 sm:flex-none px-3 py-1.5 text-xs sm:text-sm text-gray-700 hover:bg-gray-50 rounded-l-md border-r border-gray-300"
                    title="Select all serial numbers"
                  >
                    Select All
                  </button>
                  <button
                    onClick={selectNone}
                    className="flex-1 sm:flex-none px-3 py-1.5 text-xs sm:text-sm text-gray-700 hover:bg-gray-50 rounded-r-md"
                    title="Deselect all serial numbers"
                  >
                    Clear All
                  </button>
                </div>

                {/* Download Button */}
                <button
                  onClick={processAndDownload}
                  disabled={selectedSerials.size === 0 || isProcessing}
                  className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium text-xs sm:text-sm"
                  title="Download filtered EPCIS file with selected serial numbers"
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <Download size={16} className="mr-2" />
                      Download Filtered File
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Select by GTIN Subsection - Showing EACH layer (lowest indicator per product) */}
            {(() => {
              const eachGTINs = getEachGTINsWithAllLevels();
              
              return eachGTINs.size > 0 ? (
                <div className="mb-6 p-6 border-b border-t border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm font-bold sm:text-base font-medium text-gray-900">Select by Product (NDC)</p>
                      <p className="text-sm text-gray-600 mt-1">
                        Quick selection: Click a product to select all its serial numbers across all packaging levels
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {Array.from(eachGTINs.entries()).map(([gtin, data]) => {
                      const selectionState = getGTINSelectionState(data.serialNumbers);
                      const selectedCount = data.serialNumbers.filter(s => selectedSerials.has(s)).length;
                      
                      return (
                        <button
                          key={gtin}
                          onClick={() => toggleGTINSelection(gtin, data.serialNumbers)}
                          className={`px-3 py-2 rounded-lg border-2 transition-all text-xs sm:text-sm ${
                            selectionState === 'all'
                              ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                              : selectionState === 'partial'
                              ? 'bg-blue-100 text-blue-800 border-blue-400 hover:bg-blue-200'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                          title={`Includes all packaging levels (${data.totalVariants} ${data.totalVariants === 1 ? 'level' : 'levels'})`}
                        >
                          <div className="flex flex-col items-start space-y-1">
                            {data.ndc && (
                              <div className="font-mono text-sm sm:text-base font-bold" data-testid={`product-ndc-${gtin}`}>
                                {data.ndc}
                              </div>
                            )}
                            {data.productName && (
                              <div className="text-xs opacity-90 text-left max-w-[14rem] leading-snug" data-testid={`product-name-${gtin}`}>
                                {data.productName}
                              </div>
                            )}
                            <div className="flex items-center space-x-2 text-xs">
                              <span className="opacity-75">
                                {data.totalVariants} {data.totalVariants === 1 ? 'level' : 'levels'}
                              </span>
                              <span className={`px-1.5 py-0.5 rounded font-semibold ${
                                selectionState === 'all'
                                  ? 'bg-blue-500 text-white'
                                  : selectionState === 'partial'
                                  ? 'bg-blue-200 text-blue-900'
                                  : 'bg-gray-200 text-gray-700'
                              }`}>
                                {selectedCount}/{data.serialNumbers.length}
                              </span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null;
            })()}

            {/* Tree - Full height, no scrollable container */}
            <div className="border border-gray-200 rounded-lg p-2 sm:p-4 overflow-x-auto">
              {parsedData.hierarchy.map(node => renderTreeNode(node))}
            </div>
          </div>
        )}
      </div>

      {/* Account Settings Modal */}
      {showAccountSettings && (
        <AccountSettingsModal
          onClose={() => setShowAccountSettings(false)}
          user={user}
          business={business}
        />
      )}

      {/* Manage Users Modal */}
      {showManageUsers && (
        <ManageUsersModal 
          onClose={() => setShowManageUsers(false)} 
        />
      )}

      {/* Manage Vendors Modal */}
      {showManageVendors && (
        <ManageVendorsModal 
          onClose={() => setShowManageVendors(false)} 
        />
      )}
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <ToastProvider>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<MainApp />} />
            <Route path="/po/:poId" element={<PODetailsPage />} />
            <Route path="/admin" element={<SuperAdminPanel />} />
            <Route path="/registration" element={<BusinessSignup />} />
            <Route path="/tools/epcis-filter" element={<EPCISFilterTool />} />
          </Routes>
        </AuthProvider>
      </ToastProvider>
    </Router>
  );
};

const MainApp = () => {
  const { user } = useAuth();

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-68px)]">
          <div className="max-w-md w-full">
            <LoginForm />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <Dashboard />
    </div>
  );
};

const PODetailsPage = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { poId } = useParams();
  const navigate = useNavigate();
  const [po, setPO] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCompleteEffect, setShowCompleteEffect] = useState(false);
  const [completePONumber, setCompletePONumber] = useState('');
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadingEPCIS, setUploadingEPCIS] = useState(false);
  const [uploadCompleted, setUploadCompleted] = useState(false);
  const [glnError, setGLNError] = useState(null); // Store GLN validation error details
  const [showReplaceUpload, setShowReplaceUpload] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  // States for PO number editing
  const [showEditPONumber, setShowEditPONumber] = useState(false);
  const [editingPONumber, setEditingPONumber] = useState('');
  const [editingVendor, setEditingVendor] = useState('');
  const [editVendors, setEditVendors] = useState([]);
  const [editVendorsLoading, setEditVendorsLoading] = useState(false);
  const [filteredEditVendors, setFilteredEditVendors] = useState([]);
  const [showEditVendorDropdown, setShowEditVendorDropdown] = useState(false);
  const [editVendorSearchQuery, setEditVendorSearchQuery] = useState('');
  const [savingPONumber, setSavingPONumber] = useState(false);

  // States for receiving workflow
  const [isReceiving, setIsReceiving] = useState(false);
  const [receivingSession, setReceivingSession] = useState(null);
  const [receivingStatus, setReceivingStatus] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const [scanSuccess, setScanSuccess] = useState('');
  const [webcamKey, setWebcamKey] = useState(Date.now());
  const webcamRef = useRef(null);
  const [isStartingReceiving, setIsStartingReceiving] = useState(false);

  // Modal close functionality
  const editPOModalRef = useModalClose(showEditPONumber, () => setShowEditPONumber(false));

  // Add state to prevent multiple animations
  const completionTriggered = useRef(false);

  // Only redirect if we're sure user is not authenticated (not just loading)
  useEffect(() => {
    // Add a small delay to prevent redirect during initial load
    const timer = setTimeout(() => {
      if (!user && !loading) {
        navigate('/');
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [user, navigate, loading]);

  // Helper function to get packaging summary from PO data
  const getPackagingSummary = (po) => {
    if (!po.epcis_parsed_data?.packaging_hierarchy) {
      return { simple: true, count: po.sscc_numbers?.length || 0 };
    }
    
    const hierarchy = po.epcis_parsed_data.packaging_hierarchy;
    const counts = {};
    
    // Count NUMBER of items at each level (not their contents)
    hierarchy.forEach(item => {
      const level = item.level || 'UNKNOWN';
      counts[level] = (counts[level] || 0) + 1; // Count the item itself, not its contents
    });
    
    // Build structured summary
    const summary = [];
    if (counts.SSCC) summary.push({ label: 'SSCC', count: counts.SSCC });
    if (counts.CASE) summary.push({ label: 'CASE', count: counts.CASE });
    if (counts.INNER_CASE) summary.push({ label: 'INNER', count: counts.INNER_CASE });
    if (counts.EACH) summary.push({ label: 'EACH', count: counts.EACH });
    
    return summary.length > 0 ? { simple: false, items: summary } : { simple: true, count: po.sscc_numbers?.length || 0 };
  };

  // Add ref to prevent duplicate calls during component mount
  const fetchPOCalledRef = useRef(false);

  useEffect(() => {
    if (poId && !fetchPOCalledRef.current) {
      fetchPOCalledRef.current = true;
      fetchPO();
    }
  }, [poId]);

  const fetchPO = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/pos/${poId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPO(response.data);
    } catch (error) {
      console.error('Error fetching PO:', error);
      setError('Failed to load purchase order details');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPONumber = async () => {
    setEditingPONumber(po.po_number);
    setEditingVendor(po.supplier_name);
    setEditVendorSearchQuery(po.supplier_name);
    
    // Fetch vendors for vendor editing
    setEditVendorsLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/vendors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditVendors(response.data.vendors || []);
      setFilteredEditVendors(response.data.vendors || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      showToast('Failed to load vendors', 'error');
    } finally {
      setEditVendorsLoading(false);
    }
    
    setShowEditPONumber(true);
  };

  const handleEditVendorSearchChange = (e) => {
    const value = e.target.value;
    setEditVendorSearchQuery(value);
    setEditingVendor(value);
    setShowEditVendorDropdown(value.length > 0);
    
    // Filter vendors based on search query
    const filtered = editVendors.filter(vendor =>
      vendor.company_name.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredEditVendors(filtered);
  };

  const handleEditVendorSelect = (vendor) => {
    setEditingVendor(vendor.company_name);
    setEditVendorSearchQuery(vendor.company_name);
    setShowEditVendorDropdown(false);
  };

  const handleSavePONumber = async () => {
    if (!editingPONumber.trim()) {
      showToast('PO number cannot be empty', 'error');
      return;
    }

    if (!editingVendor.trim() && po.status !== 'ready_to_receive') {
      showToast('Vendor cannot be empty', 'error');
      return;
    }

    // Validate that vendor matches an existing vendor (skip if status is ready_to_receive)
    if (po.status !== 'ready_to_receive') {
      const selectedVendor = editVendors.find(vendor => vendor.company_name === editingVendor);
      if (!selectedVendor) {
        showToast('Please select a valid vendor from the list', 'error');
        return;
      }
    }

    const hasChanges = editingPONumber.trim() !== po.po_number || 
                      (po.status !== 'ready_to_receive' && editingVendor.trim() !== po.supplier_name);
    
    if (!hasChanges) {
      setShowEditPONumber(false);
      return;
    }

    setSavingPONumber(true);
    
    try {
      const token = localStorage.getItem('session_token');
      let updateCount = 0;
      let lastResponse = null;
      
      // Update PO number if changed
      if (editingPONumber.trim() !== po.po_number) {
        try {
          const poNumberResponse = await axios.put(
            `${API}/pos/${poId}/po-number`,
            { po_number: editingPONumber.trim() },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          lastResponse = poNumberResponse.data;
          updateCount++;
        } catch (error) {
          console.error('Error updating PO number:', error);
          showToast('Failed to update PO number: ' + (error.response?.data?.detail || error.message), 'error');
          setSavingPONumber(false);
          return;
        }
      }

      // Update vendor if changed AND status allows vendor editing
      if (po.status !== 'ready_to_receive' && editingVendor.trim() !== po.supplier_name) {
        try {
          const vendorResponse = await axios.put(
            `${API}/pos/${poId}/vendor`,
            { supplier_name: editingVendor.trim() },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          lastResponse = vendorResponse.data;
          updateCount++;
        } catch (error) {
          console.error('Error updating vendor:', error);
          showToast('Failed to update vendor: ' + (error.response?.data?.detail || error.message), 'error');
          setSavingPONumber(false);
          return;
        }
      }

      // Update PO state with the latest response
      if (lastResponse) {
        setPO(lastResponse);
      }

      setShowEditPONumber(false);
      
      if (updateCount === 2) {
        showToast('PO number and vendor updated successfully', 'success');
      } else if (editingPONumber.trim() !== po.po_number) {
        showToast('PO number updated successfully', 'success');
      } else {
        showToast('Vendor updated successfully', 'success');
      }
      
    } catch (error) {
      console.error('Unexpected error updating PO:', error);
      showToast('Unexpected error occurred while updating PO details', 'error');
    } finally {
      setSavingPONumber(false);
    }
  };

  const handleEPCISUpload = async (file) => {
    if (!po) return;
    
    console.log('Starting EPCIS upload, clearing GLN error');
    setUploadingEPCIS(true);
    setGLNError(null); // Clear previous GLN error
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('session_token');
      
      // Check if token exists
      if (!token) {
        showToast('Session expired. Please log in again.', 'error');
        // Redirect to login or refresh page
        window.location.reload();
        return;
      }
      
      const response = await axios.post(`${API}/pos/${po.id}/upload-epcis`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      showToast('EPCIS file uploaded and parsed successfully!', 'success');
      
      // Display GLN cross-check results if available - removed toast, just process silently
      // GLN validation happens in background without user notification
      
      setUploadCompleted(true);
      setSelectedFile(null);
      setGLNError(null); // Clear any previous GLN errors
      await fetchPO(); // Refresh PO data
    } catch (error) {
      console.error('Upload error:', error);
      
      // Handle authentication errors specifically
      if (error.response?.status === 401) {
        showToast('Session expired. Please log in again.', 'error');
        // Clear invalid token
        localStorage.removeItem('session_token');
        // Redirect to login
        setTimeout(() => window.location.reload(), 2000);
      } 
      // Handle GLN validation errors (status 422)
      else if (error.response?.status === 422) {
        const errorData = error.response.data;
        if (errorData.detail && typeof errorData.detail === 'object') {
          // Store structured GLN error for inline display
          setGLNError({
            title: errorData.detail.error || 'GLN Validation Failed',
            message: errorData.detail.message,
            diagnostics: errorData.detail.diagnostics
          });
          
          console.log('GLN Error set:', {
            title: errorData.detail.error || 'GLN Validation Failed',
            message: errorData.detail.message,
            diagnostics: errorData.detail.diagnostics
          });
          
          showToast(errorData.detail.message || 'GLN validation failed', 'error');
        } else {
          showToast(errorData.detail || 'GLN validation failed', 'error');
        }
        // Don't set uploadCompleted or clear selectedFile - keep uploader available
        throw new Error('GLN validation failed'); // Throw error so handleUpload doesn't clear the file
      } 
      else {
        showToast(error.response?.data?.detail || 'Failed to upload EPCIS file', 'error');
        throw error; // Re-throw error so handleUpload doesn't clear the file
      }
    } finally {
      setUploadingEPCIS(false);
    }
  };

  const handleDownloadEPCIS = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/pos/${po.id}/download-epcis`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Debug: Log response headers
      console.log('Response headers:', response.headers);
      
      // Extract filename from Content-Disposition header (axios lowercases header names)
      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      
      let filename = `${po.po_number}_epcis.xml`; // fallback filename
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, ''); // Remove quotes if present
        }
      }

      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename; // Use original filename
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showToast('EPCIS file downloaded successfully!', 'success');
    } catch (error) {
      console.error('Download error:', error);
      showToast('Failed to download EPCIS file', 'error');
    }
  };

  const handleDeletePO = async () => {
    if (!window.confirm(`Are you sure you want to delete PO ${po.po_number}? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/pos/${po.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showToast('Purchase order deleted successfully!', 'success');
      // Navigate back to dashboard after successful deletion
      setTimeout(() => navigate('/'), 1000);
    } catch (error) {
      console.error('Delete error:', error);
      showToast('Failed to delete purchase order', 'error');
    }
  };

  const startReceivingSession = async () => {
    if (isStartingReceiving) return; // Prevent double-clicks
    
    try {
      setIsStartingReceiving(true);
      const token = localStorage.getItem('session_token');
      console.log('Starting receiving session for PO:', po.po_number);
      
      const response = await axios.post(`${API}/pos/${po.id}/start-receiving`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.session) {
        setReceivingSession(response.data.session);
        setIsReceiving(true);
        await getReceivingStatus();
        // Removed "Receiving session started!" toast - not needed
      }
    } catch (error) {
      console.error('Error starting receiving session:', error);
      showToast('Failed to start receiving session', 'error');
    } finally {
      setIsStartingReceiving(false);
    }
  };

  const getReceivingStatus = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/pos/${po.id}/receiving-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.has_session) {
        setReceivingStatus(response.data);
        setReceivingSession(response.data.session);
        setIsReceiving(true);
      }
    } catch (error) {
      console.error('Error getting receiving status:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !po) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center">
              <div className="text-red-600 text-lg font-medium mb-4">
                {error || 'Purchase order not found'}
              </div>
              <button
                onClick={() => navigate('/')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
              >
                ← Back to All Purchase Orders
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check authentication before rendering
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-full">
          <div className="max-w-md w-full">
            <LoginForm />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Back Button */}
        <div className="mb-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 font-medium transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            <span>Back to Purchase Orders</span>
          </button>
        </div>

        {/* PO Header - Simplified and more prominent */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex justify-between gap-4 flex-wrap">
            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-bold text-gray-900">
                      PO #{po.po_number}
                    </h1>
                    {po.status !== 'complete' && (
                      <button
                        onClick={handleEditPONumber}
                        className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                        title="Edit PO Details"
                      >
                        <Edit3 size={16} />
                      </button>
                    )}
                  </div>
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                    po.status === 'complete' ? 'bg-green-100 text-green-800' :
                    po.status === 'ready_to_receive' ? 'bg-blue-100 text-blue-800' :
                    po.status === 'awaiting_epcis' ? 'bg-amber-100 text-amber-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {po.status === 'awaiting_epcis' ? 'Awaiting EPCIS' :
                     po.status === 'ready_to_receive' ? 'Ready to Receive' :
                     po.status === 'complete' ? 'Complete' : po.status}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                  <span>Vendor: <span className="font-medium text-gray-900">{po.supplier_name}</span></span>
                  <span>•</span>
                  <span>Created: <span className="font-medium text-gray-900">{new Date(po.created_at).toLocaleDateString()}</span></span>
                  {(() => {
                    const summary = getPackagingSummary(po);
                    if (summary.simple && summary.count > 0) {
                      return (
                        <>
                          <span>•</span>
                          <span>{summary.count.toLocaleString()} Container{summary.count !== 1 ? 's' : ''}</span>
                        </>
                      );
                    } else if (!summary.simple) {
                      return (
                        <>
                          <span>•</span>
                          <span className="font-medium text-gray-900 flex items-center">
                            {summary.items.map((item, index) => (
                              <React.Fragment key={index}>
                                <span className="text-xs font-semibold px-1.5 py-0.5 rounded text-gray-600 bg-gray-100">
                                  {item.count.toLocaleString()} {item.label}
                                </span>
                                {index < summary.items.length - 1 && <ChevronRight className="inline-block w-4 h-4 text-gray-500" />}
                              </React.Fragment>
                            ))}
                          </span>
                        </>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            </div>
            
            {/* Primary Actions - Full width on mobile */}
            <div className="flex flex-wrap items-center gap-2">
              {po.status === 'ready_to_receive' && (
                <button
                  onClick={startReceivingSession}
                  disabled={isStartingReceiving}
                  className={`${
                    isStartingReceiving 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-green-600 hover:bg-green-700'
                  } text-white px-3.5 py-1.5 rounded-md shadow-md hover:shadow-lg flex items-center gap-2 transition-colors`}
                >

                  {isStartingReceiving ? 'Starting...' : 'Start Receiving'}
                </button>
              )}
              
              <div className="inline-flex overflow-hidden rounded-md border border-slate-500 shadow-md divide-x divide-slate-300">
                {(po.epcis_file_path || po.status === 'complete' || po.status === 'ready_to_receive') && (
                  <button
                    onClick={handleDownloadEPCIS}
                    className="bg-slate-800 hover:bg-slate-900 text-white px-3.5 py-1.5 flex items-center gap-2 transition-colors"
                  >
                    <IoCodeDownloadOutline className="w-5 h-5" />
                    EPCIS
                  </button>
                )}

                {(po.epcis_file_path || po.status === 'ready_to_receive') && po.status !== 'complete' && (
                  <button
                    onClick={() => setShowReplaceUpload(!showReplaceUpload)}
                    className="bg-white hover:bg-slate-100 text-slate-800 px-3.5 py-1.5 flex items-center gap-2 transition-colors"
                    title="Upload a new EPCIS file to replace the current one"
                  >
                    <LuUpload className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Status-Based Content */}
        {po.status === 'awaiting_epcis' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
                <Upload size={32} className="text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">EPCIS File Required</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Upload the EPCIS XML file provided by your vendor to proceed with receiving containers.
              </p>
              
              {/* EPCIS Upload Section */}
              <PODetailsModalContent
                po={po}
                selectedFile={selectedFile}
                setSelectedFile={setSelectedFile}
                dragActive={dragActive}
                setDragActive={setDragActive}
                uploadingEPCIS={uploadingEPCIS}
                uploadCompleted={uploadCompleted}
                setUploadCompleted={setUploadCompleted}
                showReplaceUpload={showReplaceUpload}
                setShowReplaceUpload={setShowReplaceUpload}
                onEPCISUpload={handleEPCISUpload}
                expandedNodes={expandedNodes}
                setExpandedNodes={setExpandedNodes}
                glnError={glnError}
                setGLNError={setGLNError}
              />
            </div>
          </div>
        )}

        {/* Replace EPCIS File Section - for POs that are not complete */}
        {showReplaceUpload && (po.epcis_file_path || po.status === 'ready_to_receive') && po.status !== 'complete' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Replace EPCIS File
              </h3>
              <button
                onClick={() => setShowReplaceUpload(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-amber-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div>
                  <p className="text-amber-800 font-medium">Warning</p>
                  <p className="text-amber-700 text-sm">
                    Replacing the EPCIS file will reprocess all data and may affect container receiving status.
                  </p>
                </div>
              </div>
            </div>

            <PODetailsModalContent
              po={po}
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
              dragActive={dragActive}
              setDragActive={setDragActive}
              uploadingEPCIS={uploadingEPCIS}
              uploadCompleted={uploadCompleted}
              setUploadCompleted={setUploadCompleted}
              showReplaceUpload={showReplaceUpload}
              setShowReplaceUpload={setShowReplaceUpload}
              onEPCISUpload={handleEPCISUpload}
              expandedNodes={expandedNodes}
              setExpandedNodes={setExpandedNodes}
              glnError={glnError}
              setGLNError={setGLNError}
            />
          </div>
        )}
        {(po.status === 'ready_to_receive' || po.status === 'complete') && po.epcis_parsed_data && (
          <div className="space-y-6">
            {/* Product Information - Most important for users */}
            {po.epcis_parsed_data.ndc_details && po.epcis_parsed_data.ndc_details.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <LuPill className="w-5 h-5 text-purple-600" />
                  NDCs in this Shipment ({po.epcis_parsed_data.ndc_details.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 md:[&>*:only-child]:col-span-2 gap-6">
                  {po.epcis_parsed_data.ndc_details.map((ndc, index) => {
                    // Helper function to extract NDC from GTIN (simplified and working)
                    const extractNDCFromGTIN = (gtin) => {
                      if (!gtin || gtin.length !== 14) return null;
                      
                      // For pharmaceutical GTINs starting with "003"
                      if (gtin.startsWith('003')) {
                        const core_digits = gtin.substring(3); // Remove "003"
                        
                        if (core_digits.length === 11) {
                          const labeler_code = core_digits.substring(0, 5);
                          const item_digits = core_digits.substring(5, 11); // 6 digits
                          
                          // Handle the known issue: need to get the correct last 2 digits
                          // The issue is the package code - it should be digits 8-9 from core_digits, not last 2
                          const product_code = core_digits.substring(5, 9); // positions 5-8 
                          const package_code = core_digits.substring(9, 11); // positions 9-10
                          
                          return `${labeler_code}-${product_code}-${package_code}`;
                        }
                      }
                      
                      return null;
                    };

                    // Get only SGTINs that belong to this specific NDC
                    const allSgtins = po.epcis_parsed_data?.sgtin_details || [];
                    const ndcSpecificSgtins = allSgtins.filter(sgtin => {
                      const sgtinNDC = extractNDCFromGTIN(sgtin.gtin14);
                      return sgtinNDC === ndc.ndc;
                    });

                    // Get only EACH items that belong to this specific NDC (filter by GTIN if available)
                    const eachItems = po.epcis_parsed_data?.packaging_hierarchy?.filter(item => {
                      if (item.level !== 'EACH') return false;
                      // If the item has a gtin, check if it matches this NDC
                      if (item.gtin14) {
                        const itemNDC = extractNDCFromGTIN(item.gtin14);
                        return itemNDC === ndc.ndc;
                      }
                      // If no gtin available, include it (fallback)
                      return true;
                    }) || [];
                    
                    // Use the new lot_expiration_combinations from backend
                    let uniqueLotExpirationData;
                    
                    if (ndc.lot_expiration_combinations && ndc.lot_expiration_combinations.length > 0) {
                      // Use the multiple combinations from backend commissioning events processing
                      uniqueLotExpirationData = ndc.lot_expiration_combinations.map(combo => ({
                        lot: combo.lot_number || 'Not specified',
                        expiry: combo.expiration_date || 'Not specified',
                        quantity: combo.quantity || 0
                      }));
                    } else {
                      // Fallback to single lot/expiration for backward compatibility
                      const ndcLot = ndc.lot_number || ndc.lot || ndc.lotNumber || ndc.batch_number || ndc.batch;
                      const ndcExpiry = ndc.expiration_date || ndc.expiry_date || ndc.expiryDate || ndc.exp_date || ndc.exp;
                      
                      if (ndcLot || ndcExpiry) {
                        uniqueLotExpirationData = [{
                          lot: ndcLot || 'Not specified',
                          expiry: ndcExpiry || 'Not specified',
                          quantity: 0  // No quantity info available in legacy data
                        }];
                      } else {
                        // Last resort: check SGTIN/EACH data (legacy support)
                        const lotExpirationPairs = new Set();
                        
                        // Check NDC-specific SGTINs
                        ndcSpecificSgtins.forEach(sgtin => {
                          const lot = sgtin.lot_number || sgtin.lot || sgtin.batch_number || sgtin.batch;
                          const expiry = sgtin.expiration_date || sgtin.expiry_date || sgtin.expiryDate || sgtin.exp_date || sgtin.exp;
                          if (lot || expiry) {
                            lotExpirationPairs.add(JSON.stringify({
                              lot: lot || 'Not specified',
                              expiry: expiry || 'Not specified',
                              quantity: 0  // No quantity info available in legacy data
                            }));
                          }
                        });
                        
                        // Check NDC-specific EACH items
                        eachItems.forEach(item => {
                          const lot = item.lot_number || item.lot || item.batch_number || item.batch;
                          const expiry = item.expiration_date || item.expiry_date || item.expiryDate || item.exp_date || item.exp;
                          if (lot || expiry) {
                            lotExpirationPairs.add(JSON.stringify({
                              lot: lot || 'Not specified',
                              expiry: expiry || 'Not specified',
                              quantity: 0  // No quantity info available in legacy data
                            }));
                          }
                        });
                        
                        uniqueLotExpirationData = Array.from(lotExpirationPairs).map(pair => JSON.parse(pair));
                        
                        if (uniqueLotExpirationData.length === 0) {
                          uniqueLotExpirationData = [{
                            lot: 'Not specified',
                            expiry: 'Not specified',
                            quantity: 0  // No quantity info available in legacy data
                          }];
                        }
                      }
                    }
                    
                    return (
                      <div key={index} className="w-full bg-gray-50 rounded-lg p-6 border border-gray-200">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="font-mono text-lg text-purple-600">
                                {ndc.ndc}
                              </div>
                            </div>
                            
                            <div className="font-semibold text-gray-900 mb-0 text-lg">
                              {ndc.product_name || ndc.regulatedProductName || ndc.description || `NDC ${ndc.ndc}`}
                            </div>
                            
                            {ndc.manufacturer && (
                              <div className="text-sm text-gray-600 mb-3">
                                 {ndc.manufacturer}
                              </div>
                            )}
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                              {ndc.strength && (
                                <div className="text-gray-600">
                                  <span className="font-medium text-gray-800">Strength:</span><br />
                                  {ndc.strength}
                                </div>
                              )}
                              {ndc.dosage_form && (
                                <div className="text-gray-600">
                                  <span className="font-medium text-gray-800">Form:</span><br />
                                  {ndc.dosage_form}
                                </div>
                              )}
                              {ndc.net_content && (
                                <div className="text-gray-600">
                                  <span className="font-medium text-gray-800">Net Content:</span><br />
                                  {ndc.net_content}
                                </div>
                              )}
                              {ndc.route_of_administration && (
                                <div className="text-gray-600">
                                  <span className="font-medium text-gray-800">Route:</span><br />
                                  {ndc.route_of_administration}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Lot & Expiration Information - Multiple combinations */}
                        <div className="pt-4 border-t border-gray-300">
                          <div className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-3">
                            Lot & Expiration ({uniqueLotExpirationData.length})
                          </div>
                          
                          {uniqueLotExpirationData.length === 1 ? (
                            // Single lot/expiration - display in grid format with quantity
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div className="flex items-center space-x-3">
                                <Tag className="w-5 h-5 text-purple-600 flex-shrink-0" />
                                <div>
                                  <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">Lot</div>
                                  <div className="font-mono text-gray-900">
                                    {uniqueLotExpirationData[0].lot}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-3">
                                <Clock className="w-5 h-5 text-purple-600 flex-shrink-0" />
                                <div>
                                  <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">Exp</div>
                                  <div className="font-mono text-gray-900">
                                    {uniqueLotExpirationData[0].expiry}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-3">
                                <LuPillBottle className="w-5 h-5 text-purple-600 flex-shrink-0" />
                                <div>
                                  <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">Qty</div>
                                  <div className="font-mono text-gray-900">
                                    {uniqueLotExpirationData[0].quantity || uniqueLotExpirationData[0].quantity === 0 ? 
                                      (uniqueLotExpirationData[0].quantity === 0 ? 'n/a' : uniqueLotExpirationData[0].quantity) : 
                                      'n/a'}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            // Multiple lot/expiration combinations - display as cards with quantities
                            <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-1 gap-0">
                              {uniqueLotExpirationData.map((data, idx) => (
                                <div key={idx} className="py-2 border-b border-gray-200 last:border-b-0 first:pt-0">
                                  <div className="flex items-center justify-between mb-2">
                                    {/* <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">
                                      #{idx + 1}
                                    </div> */}
                                    {/* <div className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                      {data.quantity} items
                                    </div> */}
                                  </div>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="flex items-center space-x-2">
                                      <Tag className="w-5 h-5 text-purple-600 flex-shrink-0" />
                                      <div>
                                        <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">Lot</div>
                                        <div className="font-mono text-gray-900">{data.lot}</div>
                                      </div>
                                    </div>
                                    
                                    <div className="flex items-center space-x-2">
                                      <Clock className="w-5 h-5 text-purple-600 flex-shrink-0" />
                                      <div>
                                        <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">Exp</div>
                                        <div className="font-mono text-gray-900">{data.expiry}</div>
                                      </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                <LuPillBottle className="w-5 h-5 text-purple-600 flex-shrink-0" />
                                <div>
                                  <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">Qty</div>
                                  <div className="font-mono text-gray-900">
                                    {data.quantity || data.quantity === 0 ? 
                                      (data.quantity === 0 ? 'n/a' : data.quantity) : 
                                      'n/a'}
                                  </div>
                                </div>
                              </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Fallback for legacy NDC data if ndc_details not available */}

            {/* Container Hierarchy - Simplified view */}
            {po.epcis_parsed_data.packaging_hierarchy && po.epcis_parsed_data.packaging_hierarchy.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <LuPackageSearch className="w-5 h-5 text-blue-600" />
                  Packaging Hierarchy
                </h3>
                
                
                
                {/* Full container breakdown - always visible */}
                <div className="">
                  <PODetailsModalContent
                    po={po}
                    selectedFile={selectedFile}
                    setSelectedFile={setSelectedFile}
                    dragActive={dragActive}
                    setDragActive={setDragActive}
                    uploadingEPCIS={uploadingEPCIS}
                    uploadCompleted={uploadCompleted}
                    setUploadCompleted={setUploadCompleted}
                    showReplaceUpload={showReplaceUpload}
                    setShowReplaceUpload={setShowReplaceUpload}
                    onEPCISUpload={handleEPCISUpload}
                    expandedNodes={expandedNodes}
                    setExpandedNodes={setExpandedNodes}
                    glnError={glnError}
                    setGLNError={setGLNError}
                  />
                </div>
                <div className="space-y-3">
                  {/* Show simplified hierarchy */}
                  {po.epcis_parsed_data.packaging_hierarchy
                    .filter(item => item.level === 'SSCC')
                    .map((sscc, index) => {
                      // Find the corresponding SSCC detail for HRI format
                      const ssccDetail = po.epcis_parsed_data?.sscc_details?.find(
                        detail => detail.epc === sscc.identifier || detail.hri === sscc.identifier
                      );
                      
                      return (
                        <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <div className="font-mono text-sm font-semibold text-gray-900 mb-1">
                                SSCC: {ssccDetail?.hri || sscc.identifier}
                              </div>
                              {ssccDetail?.hri && ssccDetail.hri !== sscc.identifier && (
                                <div className="font-mono text-xs text-gray-500">
                                  EPC: {sscc.identifier}
                                </div>
                              )}
                            </div>
                            <div className="text-sm text-gray-600">
                              {sscc.count} item{sscc.count !== 1 ? 's' : ''}
                            </div>
                          </div>
                          
                          {/* Show immediate children summary */}
                          {po.epcis_parsed_data.packaging_hierarchy
                            .filter(item => item.parent === sscc.identifier)
                            .length > 0 && (
                            <div className="text-sm text-gray-600 ml-4 border-l-2 border-gray-300 pl-3">
                              {(() => {
                                const children = po.epcis_parsed_data.packaging_hierarchy
                                  .filter(item => item.parent === sscc.identifier);
                                const childSummary = {};
                                children.forEach(child => {
                                  childSummary[child.level] = (childSummary[child.level] || 0) + 1;
                                });
                                return Object.entries(childSummary)
                                  .map(([level, count]) => `${count} ${level}${count > 1 ? 'S' : ''}`)
                                  .join(' • ');
                              })()}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            
          </div>
        )}
            <button
                onClick={handleDeletePO}
                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 mt-4 rounded-md font-small transition-colors"
              >
                Delete PO
              </button>
      </div>

      {/* Edit PO Number Modal */}
      {showEditPONumber && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div ref={editPOModalRef} className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edit PO Details</h3>
              <button
                onClick={() => setShowEditPONumber(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                PO Number
              </label>
              <input
                type="text"
                value={editingPONumber}
                onChange={(e) => setEditingPONumber(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter PO number"
                disabled={savingPONumber}
              />
              <p className="text-xs text-gray-500 mt-1">
                PO number must be unique within your pharmacy
              </p>
            </div>

            <div className="mb-4 relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vendor
              </label>
              <input
                type="text"
                value={editVendorSearchQuery}
                onChange={handleEditVendorSearchChange}
                onFocus={() => setShowEditVendorDropdown(editVendorSearchQuery.length > 0)}
                onBlur={(e) => {
                  // Delay closing to allow for clicks
                  const currentTarget = e.currentTarget;
                  setTimeout(() => {
                    if (currentTarget && document.activeElement && !currentTarget.contains(document.activeElement)) {
                      setShowEditVendorDropdown(false);
                    }
                  }, 150);
                }}
                className="w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder={
                  po.status === 'ready_to_receive' ? "Vendor cannot be changed after EPCIS upload" :
                  editVendorsLoading ? "Loading vendors..." : 
                  editVendors.length === 0 ? "No vendors available" : 
                  "Type to search vendors..."
                }
                disabled={savingPONumber || editVendorsLoading || editVendors.length === 0 || po.status === 'ready_to_receive'}
              />
              
              {/* Vendor Dropdown */}
              {showEditVendorDropdown && filteredEditVendors.length > 0 && (
                <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto mt-1">
                  {filteredEditVendors.map((vendor) => (
                    <div
                      key={vendor.id}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      onMouseDown={(e) => e.preventDefault()} // Prevent blur on click
                      onClick={() => handleEditVendorSelect(vendor)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{vendor.company_name}</div>
                          <div className="text-sm text-gray-500">
                            GLNs: {vendor.glns.join(', ')}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Helper text */}
              {editVendors.length === 0 && !editVendorsLoading && (
                <p className="text-xs text-red-600 mt-1">
                  No vendors available. Please add vendors in "Manage Vendors" first.
                </p>
              )}
              
              {editVendorSearchQuery && !filteredEditVendors.length && editVendors.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  No vendors match your search. Try a different term.
                </p>
              )}
              
              <p className="text-xs text-gray-500 mt-1">
                {po.status === 'ready_to_receive' ? 
                  "Vendor cannot be changed after EPCIS file upload to maintain GLN validation integrity" :
                  "Vendor must be selected from your vendor list"
                }
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowEditPONumber(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={savingPONumber}
              >
                Cancel
              </button>
              <button
                onClick={handleSavePONumber}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={
                  savingPONumber || 
                  !editingPONumber.trim() || 
                  (po.status !== 'ready_to_receive' && !editingVendor.trim()) // Only require vendor when not ready_to_receive
                }
              >
                {savingPONumber ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receiving Modal */}
      {isReceiving && (
        <ReceivingModal 
          po={po}
          receivingSession={receivingSession}
          receivingStatus={receivingStatus}
          isScanning={isScanning}
          scanError={scanError}
          scanSuccess={scanSuccess}
          webcamRef={webcamRef}
          webcamKey={webcamKey}
          onClose={() => {
            setIsReceiving(false);
            setReceivingSession(null);
            setReceivingStatus(null);
          }}
          onScan={async (scannedCode) => {
            if (!receivingSession || !scannedCode) return;
            
            try {
              setIsScanning(true);
              setScanError('');
              setScanSuccess('');
              
              const sessionToken = localStorage.getItem('session_token');
              if (!sessionToken) {
                setScanError('Not authenticated. Please login again.');
                // Let ReceivingModal handle toast via useEffect
                return;
              }
              
              const response = await axios.post(`${API}/pos/${poId}/scan-sscc`, {
                scanned_code: scannedCode // Backend expects 'scanned_code', not 'sscc'
              }, {
                headers: {
                  'Authorization': `Bearer ${sessionToken}`
                }
              });
              
              if (response.data.success) {
                // Removed toast notification for successful SSCC scans as requested
                // Let ReceivingModal handle other toasts via useEffect
                
                // Refresh receiving status
                const statusResponse = await axios.get(`${API}/pos/${poId}/receiving-status`, {
                  headers: {
                    'Authorization': `Bearer ${sessionToken}`
                  }
                });
                setReceivingStatus(statusResponse.data);
                
                // Check if all SSCCs scanned
                if (response.data.all_complete || response.data.session_complete) {
                  console.log('🎉 PO COMPLETION DETECTED!', { 
                    po_id: po?.id, 
                    completionTriggered: completionTriggered.current,
                    timestamp: new Date().toISOString()
                  });
                  
                  // Immediate check and set in one operation
                  const completionKey = `po_${po?.id}_complete`;
                  
                  if (!completionTriggered.current && !sessionStorage.getItem(completionKey)) {
                    console.log('🎉 TRIGGERING ANIMATION - Setting flags immediately');
                    
                    // Set both flags IMMEDIATELY before any async operations
                    completionTriggered.current = true;
                    sessionStorage.setItem(completionKey, 'shown');
                    
                    const poNum = po?.po_number || 'Unknown';
                    setCompletePONumber(poNum);
                    setShowCompleteEffect(true);
                    
                    // DO NOT CALL fetchPO here - it causes re-triggers
                    console.log('🎉 Animation triggered successfully');
                  } else {
                    console.log('🎉 SKIPPING - Already triggered:', { 
                      ref: completionTriggered.current, 
                      storage: !!sessionStorage.getItem(completionKey) 
                    });
                  }
                  
                  // Auto-close modal
                  setTimeout(() => {
                    setIsReceiving(false);
                    setReceivingSession(null);
                    setReceivingStatus(null);
                  }, 5000);
                } else {
                  const progress = statusResponse.data.progress;
                  // Only show progress toast if not the last SSCC
                  if (progress && progress.scanned_count < progress.expected_count) {
                    showToast(`📦 Progress: ${progress.scanned_count}/${progress.expected_count} SSCCs received (${progress.percentage}%)`, 'info');
                  }
                }
              } else {
                // Handle different error types - removed duplicate toasts
                const errorMessage = response.data.error || 'Failed to process SSCC';
                setScanError(errorMessage);
                // Let ReceivingModal handle toast via useEffect
              }
            } catch (error) {
              const errorMessage = error.response?.data?.detail || 'Error processing SSCC';
              setScanError(errorMessage);
              // Let ReceivingModal handle toast via useEffect
            } finally {
              setIsScanning(false);
            }
          }}
          onStartReceiving={startReceivingSession}
        />
      )}

      {/* PO Completion Effect - PODetailsPage */}
      <POCompletionEffect 
        isVisible={showCompleteEffect}
        poNumber={completePONumber}
        onComplete={() => {
          console.log('🎯 Completion effect finished, refreshing PO status');
          setShowCompleteEffect(false);
          
          // Now it's safe to refresh PO data after animation completes
          fetchPO();
        }}
      />
    </div>
  );
};

// Login Form Component - FINAL FIX
function LoginForm() {
  const { login } = useAuth();
  const { showToast } = useToast();
  
  // Helper function to generate username suggestions from full name
  const generateUsernameFromName = (fullName) => {
    if (!fullName) return '';
    
    // Convert to lowercase and remove special characters except spaces
    const cleanName = fullName.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '')  // Remove all spaces
      .substring(0, 20);    // Limit to 20 characters
    
    return cleanName;
  };
  
  // Use a simple number state instead of boolean - sometimes React has boolean issues
  const [formMode, setFormMode] = useState(1); // 1 = login, 2 = register
  const isLogin = formMode === 1;
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    pharmacy_name: '', // Changed from business_name to match backend
    npi_number: '',    // Changed from npi to match backend
    contact_name: '',  // Added required field
    contact_email: '', // Added required field
    address_line1: '', // Changed from address_line_1 to match backend
    address_line2: '', // Changed from address_line_2 to match backend
    city: '',
    state: '',
    zip_code: '',
    country: 'United States',
    phone: ''
  });
  const [isNPIValidating, setIsNPIValidating] = useState(false);
  const [npiValidationResult, setNpiValidationResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateNPI = async (npi) => {
    if (npi.length === 10) {
      setIsNPIValidating(true);
      try {
        const response = await axios.get(`${API}/npi/validate/${npi}`);
        if (response.data.valid) {
          const npiData = response.data.data;
          
          setNpiValidationResult({
            valid: true,
            message: `✅ Valid NPI: ${npiData.organization_name}`
          });
          
          // Get suggested username from the backend
          let suggestedUsername = generateUsernameFromName(npiData.authorized_official);
          
          try {
            const usernameResponse = await axios.get(`${API}/username/suggest`, {
              params: { full_name: npiData.authorized_official }
            });
            if (usernameResponse.data.suggested_username) {
              suggestedUsername = usernameResponse.data.suggested_username;
            }
          } catch (error) {
            console.log('Username suggestion failed, using local generation:', error);
            // Fallback to local generation if API fails
          }
          
          // Auto-populate Primary Practice Address fields
          setFormData(prevData => ({
            ...prevData,
            pharmacy_name: npiData.organization_name || prevData.pharmacy_name,
            contact_name: npiData.authorized_official || prevData.contact_name,
            username: suggestedUsername || prevData.username,
            address_line1: npiData.address_line1 || prevData.address_line1,
            address_line2: npiData.address_line2 || prevData.address_line2,
            city: npiData.city || prevData.city,
            state: npiData.state || prevData.state,
            zip_code: npiData.zip_code ? npiData.zip_code.substring(0, 5) : prevData.zip_code, // Truncate to 5 digits
            phone: npiData.phone || prevData.phone
          }));
          
          showToast(`✅ Primary Practice Address populated from NPI registry`, 'success');
        } else {
          setNpiValidationResult({
            valid: false,
            message: '❌ NPI not found in registry'
          });
        }
      } catch (error) {
        setNpiValidationResult({
          valid: false,
          message: '❌ NPI validation failed'
        });
      } finally {
        setIsNPIValidating(false);
      }
    } else {
      setNpiValidationResult(null);
    }
  };

  const handleNPIChange = (e) => {
    const npi = e.target.value;
    handleInputChange(e);
    
    if (npi.length === 10) {
      validateNPI(npi);
    } else {
      setNpiValidationResult(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (isLogin) {
        const response = await axios.post(`${API}/auth/login`, {
          username: formData.username,
          password: formData.password
        });
        
        if (response.data.session_token) {
          login(
            response.data.session_token,
            response.data.user,
            response.data.business
          );
          showToast('Login successful!', 'success');
        }
      } else {
        // Remove fields that backend doesn't expect
        const { confirmPassword, country, ...registrationData } = formData;
        console.log('Registration payload:', registrationData);
        
        const response = await axios.post(`${API}/business/signup`, registrationData);
        console.log('Registration response:', response.data);
        showToast('Registration submitted! Wait for admin approval.', 'success');
        setFormMode(1); // Go back to login
      }
    } catch (error) {
      console.error('Registration error:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      let errorMsg = 'An error occurred during registration';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMsg = error.response.data;
        } else if (error.response.data.detail) {
          if (Array.isArray(error.response.data.detail)) {
            // Handle validation errors array
            const validationErrors = error.response.data.detail.map(err => 
              `${err.loc?.join('.')} - ${err.msg}`
            ).join(', ');
            errorMsg = `Validation errors: ${validationErrors}`;
          } else {
            errorMsg = error.response.data.detail;
          }
        } else if (error.response.data.message) {
          errorMsg = error.response.data.message;
        } else {
          console.log('Unknown error format:', error.response.data);
          errorMsg = JSON.stringify(error.response.data, null, 2);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      showToast(`Registration failed: ${errorMsg}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMode = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setFormMode(isLogin ? 2 : 1);
  };

  return (
    <div className="bg-transparent min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className={`w-full space-y-8 ${!isLogin ? 'max-w-2xl' : 'max-w-md'}`}>
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {isLogin ? 'Sign in to your account' : 'Register your pharmacy'}
          </h2>
          {!isLogin && (
            <p className="mt-2 text-center text-sm text-gray-600">
              Enter your NPI to auto-populate your Primary Practice Address
            </p>
          )}
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {!isLogin ? (
            // Registration Form with Organized Sections
            <div className="space-y-8">
              
              {/* Business Information Section */}
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200">
                  Business Information
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      NPI Number *
                    </label>
                    <input
                      name="npi_number"
                      type="text"
                      maxLength="10"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Enter 10-digit NPI number"
                      value={formData.npi_number}
                      onChange={handleNPIChange}
                    />
                    {isNPIValidating && (
                      <p className="mt-1 text-sm text-blue-600 flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Validating NPI and fetching Primary Practice Address...
                      </p>
                    )}
                    {npiValidationResult && (
                      <p className={`mt-1 text-sm ${npiValidationResult.valid ? 'text-green-600' : 'text-red-600'}`}>
                        {npiValidationResult.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pharmacy Name *
                    </label>
                    <input
                      name="pharmacy_name"
                      type="text"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Pharmacy name (auto-populated from NPI)"
                      value={formData.pharmacy_name}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Name *
                    </label>
                    <input
                      name="contact_name"
                      type="text"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Primary contact name"
                      value={formData.contact_name}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Email *
                    </label>
                    <input
                      name="contact_email"
                      type="email"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="contact@pharmacy.com"
                      value={formData.contact_email}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>

              {/* Primary Practice Address Section */}
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200">
                  Primary Practice Address
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address Line 1 *
                    </label>
                    <input
                      name="address_line1"
                      type="text"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Street address (auto-populated from NPI)"
                      value={formData.address_line1}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address Line 2
                    </label>
                    <input
                      name="address_line2"
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Apartment, suite, etc. (optional)"
                      value={formData.address_line2}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City *
                      </label>
                      <input
                        name="city"
                        type="text"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="City"
                        value={formData.city}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State *
                      </label>
                      <input
                        name="state"
                        type="text"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="State"
                        value={formData.state}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ZIP Code *
                      </label>
                      <input
                        name="zip_code"
                        type="text"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="ZIP Code"
                        value={formData.zip_code}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Country *
                      </label>
                      <input
                        name="country"
                        type="text"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Country"
                        value={formData.country}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      name="phone"
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Business phone number (auto-populated from NPI)"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>

              {/* Account Credentials Section */}
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200">
                  Account Credentials
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Username *
                    </label>
                    <input
                      name="username"
                      type="text"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Choose a username"
                      value={formData.username}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password *
                    </label>
                    <input
                      name="password"
                      type="password"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Create a secure password"
                      value={formData.password}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm Password *
                    </label>
                    <input
                      name="confirmPassword"
                      type="password"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Confirm your password"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Login Form (Simple)
            <div className="space-y-4">
              <div>
                <input
                  name="username"
                  type="text"
                  required
                  className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Username"
                  value={formData.username}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <input
                  name="password"
                  type="password"
                  required
                  className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isSubmitting ? 'Please wait...' : (isLogin ? 'Sign in' : 'Register')}
            </button>
          </div>

          <div className="text-center">
          <a href="/registration">Don't have an account? Register</a>
            {/* <button
              type="button"
              onClick={toggleMode}
              className="text-blue-600 hover:text-blue-500"
            >
              {isLogin ? "Don't have an account? Register" : 'Already have an account? Sign in'}
            </button> */}
          </div>
        </form>
      </div>
    </div>
  );
}

const PODetailsModalContent = ({ 
  po, 
  selectedFile, 
  setSelectedFile, 
  dragActive, 
  setDragActive, 
  uploadingEPCIS, 
  uploadCompleted,
  setUploadCompleted, 
  showReplaceUpload, 
  setShowReplaceUpload, 
  onEPCISUpload,
  expandedNodes,
  setExpandedNodes,
  glnError,
  setGLNError
}) => {
  const [allExpanded, setAllExpanded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [searchFilter, setSearchFilter] = useState('');

  // Tree structure functions - MUST be defined before use
  const buildHierarchyTree = (hierarchy) => {
    if (!hierarchy || hierarchy.length === 0) return [];
    
    // Create a map of all nodes
    const nodeMap = new Map();
    const rootNodes = [];
    
    // First pass: create all nodes
    hierarchy.forEach(item => {
      nodeMap.set(item.identifier, {
        ...item,
        children: []
      });
    });
    
    // Second pass: build parent-child relationships and find roots
    hierarchy.forEach(item => {
      const node = nodeMap.get(item.identifier);
      
      if (!item.parent || item.level === 'SSCC') {
        // This is a root node
        rootNodes.push(node);
      } else {
        // This node has a parent
        const parent = nodeMap.get(item.parent);
        if (parent) {
          parent.children.push(node);
        } else {
          // Parent not found, treat as root
          rootNodes.push(node);
        }
      }
    });
    
    return rootNodes;
  };

  // Build hierarchy tree from PO data
  const hierarchyTree = po?.epcis_parsed_data?.packaging_hierarchy ? 
    buildHierarchyTree(po.epcis_parsed_data.packaging_hierarchy) : [];

  // Initialize expanded nodes when PO data changes - Start collapsed
  React.useEffect(() => {
    if (po?.epcis_parsed_data?.packaging_hierarchy) {
      // Start with all nodes collapsed
      setExpandedNodes(new Set()); 
      setAllExpanded(false);
    }
  }, [po?.epcis_parsed_data?.packaging_hierarchy]);

  // Helper function to search in child nodes
  const hasMatchingChild = (node, searchTerm) => {
    if (!node.children || node.children.length === 0) return false;
    
    return node.children.some(child => 
      child.identifier?.toLowerCase().includes(searchTerm) ||
      child.ndc?.toLowerCase().includes(searchTerm) ||
      child.lot_number?.toLowerCase().includes(searchTerm) ||
      hasMatchingChild(child, searchTerm)
    );
  };

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    if (!po.epcis_parsed_data?.packaging_hierarchy) return;
    
    const allNodeIds = po.epcis_parsed_data.packaging_hierarchy.map(item => item.identifier);
    setExpandedNodes(new Set(allNodeIds));
    setAllExpanded(true);
  };

  const collapseAll = () => {
    setExpandedNodes(new Set());
    setAllExpanded(false);
  };

  const renderTreeNode = (node, depth = 0) => {
    if (!node) return null;
    
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.identifier);
    
    return (
      <div key={node.identifier} className="mb-1">
        <div 
          className="flex items-center space-x-2 p-3 bg-white rounded border hover:bg-gray-50" 
          style={{ marginLeft: `${depth * 24}px` }}
        >
          {/* Expand/Collapse Button */}
          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
            {hasChildren ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleNode(node.identifier);
                }}
                className="w-5 h-5 flex items-center justify-center text-gray-600 hover:text-blue-600 hover:bg-blue-100 rounded transition-colors"
                title={isExpanded ? "Collapse" : "Expand"}
              >
                {isExpanded ? (
                  <ChevronDown size={16} />
                ) : (
                  <ChevronRight size={16} />
                )}
              </button>
            ) : (
              <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
            )}
          </div>
          
          {/* Level Badge */}
          <div className="flex-shrink-0">
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
              node.level === 'SSCC' ? 'bg-blue-100 text-blue-800' :
              node.level === 'CASE' ? 'bg-green-100 text-green-800' :
              node.level === 'INNER_CASE' ? 'bg-yellow-100 text-yellow-800' :
              'bg-purple-100 text-purple-800'
            }`}>
              {node.level}
            </span>
          </div>
          
          {/* Identifier */}
          <div className="flex-1 min-w-0">
            <div className="font-mono text-sm text-gray-900 truncate" title={node.display_name || node.identifier}>
              {(() => {
                const displayText = node.display_name || node.identifier;
                return displayText.length > 45 ? `${displayText.substring(0, 45)}...` : displayText;
              })()}
            </div>
          </div>
          
          {/* Count */}
          <div className="flex-shrink-0 text-right">
            <div className="font-semibold text-gray-900">{node.count || 0}</div>
            <div className="text-xs text-gray-500">
              {node.level === 'SSCC' ? 'Items' :
               node.level === 'CASE' ? 'Items' :
               node.level === 'INNER_CASE' ? 'Items' :
               'Item'}
            </div>
          </div>
        </div>
        
        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="mt-1">
            {node.children.map(child => renderTreeNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.name.toLowerCase().endsWith('.xml')) {
      setSelectedFile(file);
    } else {
      console.error('Please select a valid XML file');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const file = e.dataTransfer.files[0];
    if (file && file.name.toLowerCase().endsWith('.xml')) {
      setSelectedFile(file);
    } else {
      console.error('Please select a valid XML file');
    }
  };

  const handleUpload = async () => {
    if (selectedFile) {
      try {
        await onEPCISUpload(selectedFile);
        // Only clear file and mark completed on successful upload
        setSelectedFile(null);
        setUploadCompleted(true); // Mark upload as completed
        setShowReplaceUpload(false); // Hide replace upload section
        // The parent component will handle refreshing the PO data
        // and updating the modal content through the onEPCISUpload callback
      } catch (error) {
        // Don't clear selectedFile or set uploadCompleted on error
        // This keeps the uploader available for retry
        console.error('Upload failed in handleUpload:', error);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'awaiting_epcis': return 'bg-yellow-100 text-yellow-800';
      case 'ready_to_receive': return 'bg-blue-100 text-blue-800';
      case 'complete': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* EPCIS Upload Section - Only show if awaiting EPCIS and no file uploaded yet OR if GLN error occurred */}
      {((po.status === 'awaiting_epcis' && !po.epcis_file_name && !uploadCompleted) || showReplaceUpload || glnError || uploadingEPCIS) && (
        <div className="rounded-lg p-2 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold text-gray-900">
              {showReplaceUpload ? 'Replace EPCIS File' : 'Upload EPCIS File'}
            </h4>
            {showReplaceUpload && (
              <button
                onClick={() => {
                  setShowReplaceUpload(false);
                  setSelectedFile(null);
                }}
                className="text-gray-400 hover:text-gray-600 text-xl"
              >
                ×
              </button>
            )}
          </div>
          <div 
            className={`border-2 border-dashed rounded-lg p-8 text-center ${
              dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {selectedFile ? (
              <div className="space-y-4">
                <div className="text-sm text-gray-600">
                  Selected file: <span className="font-medium">{selectedFile.name}</span>
                </div>
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={handleUpload}
                    disabled={uploadingEPCIS}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg font-medium flex items-center min-w-[200px] justify-center"
                  >
                    {uploadingEPCIS ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Processing EPCIS...
                      </>
                    ) : (
                      'Upload & Parse EPCIS'
                    )}
                  </button>
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-2 rounded-lg font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                  <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                </svg>
                <div>
                  <p className="text-gray-600">Drop your EPCIS XML file here, or</p>
                  <label className="cursor-pointer">
                    <span className="text-blue-600 hover:text-blue-800 font-medium">browse to upload</span>
                    <input
                      type="file"
                      className="hidden"
                      accept=".xml"
                      onChange={handleFileSelect}
                    />
                  </label>
                </div>
                <p className="text-xs text-gray-500">XML files only. GS1 Rx EPCIS 1.2 format required.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* GLN Validation Error Display */}
      {glnError && (
        <div className="mb-6 p-4 border border-red-200 rounded-lg bg-red-50">
          {console.log('Rendering GLN Error:', glnError)}
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-red-800">
                {glnError.title}
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{glnError.message}</p>
              </div>
              
              {glnError.diagnostics && (
                <div className="mt-4 space-y-3">
                  <div className="text-sm font-medium text-red-800">Diagnostics:</div>
                  
                  {glnError.diagnostics.vendor_glns && glnError.diagnostics.vendor_glns.length > 0 && (
                    <div className="bg-white p-3 rounded border border-red-200">
                      <div className="text-xs font-medium text-gray-700 mb-1">Your Vendor's GLNs:</div>
                      <div className="flex flex-wrap gap-2">
                        {glnError.diagnostics.vendor_glns.map((gln, index) => (
                          <span key={index} className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                            {gln}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {glnError.diagnostics.epcis_glns && glnError.diagnostics.epcis_glns.length > 0 ? (
                    <div className="bg-white p-3 rounded border border-red-200">
                      <div className="text-xs font-medium text-gray-700 mb-1">
                        Sample GLNs Found in EPCIS File ({glnError.diagnostics.total_epcis_glns_found} total):
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {glnError.diagnostics.epcis_glns.map((gln, index) => (
                          <span key={index} className="inline-block bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded">
                            {gln}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : glnError.diagnostics.total_epcis_glns_found === 0 && (
                    <div className="bg-white p-3 rounded border border-red-200">
                      <div className="text-xs font-medium text-gray-700 mb-1">GLNs Found in EPCIS File:</div>
                      <div className="text-sm text-gray-600">
                        No GLNs found. Check if the file contains valid GS1 identifiers (SGLN URNs, PGLN URNs, or Digital Link URLs with /414/).
                      </div>
                    </div>
                  )}
                  
                  <div className="bg-white p-3 rounded border border-red-200">
                    <div className="text-xs font-medium text-gray-700 mb-2">Troubleshooting:</div>
                    <div className="text-xs text-gray-600 space-y-1">
                      <p>• Ensure the correct vendor is selected for this PO</p>
                      <p>• Check that your vendor's GLNs are correctly configured</p>
                      <p>• Contact support if you believe this is an error</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="mt-4">
                <button
                  onClick={() => setGLNError(null)}
                  className="text-sm bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded border border-red-300"
                >
                  Dismiss Error
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EPCIS Data Display */}
      {po.epcis_parsed_data && (
        <div className="">

          {/* Packaging Hierarchy */}
          {po.epcis_parsed_data?.packaging_hierarchy && po.epcis_parsed_data.packaging_hierarchy.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex space-x-2">
                  <button
                    onClick={expandAll}
                    className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Expand All
                  </button>
                  <button
                    onClick={collapseAll}
                    className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 12H6" />
                    </svg>
                    Collapse All
                  </button>
                </div>
              </div>
              
              <div className="border border-gray-200 rounded-lg bg-gray-50 p-3">
                <div>
                  {(() => {
                    const tree = buildHierarchyTree(po.epcis_parsed_data.packaging_hierarchy);
                    return tree.map(rootNode => renderTreeNode(rootNode));
                  })()}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


const ReceivingModal = ({ 
  po, 
  receivingSession, 
  receivingStatus, 
  isScanning, 
  scanError, 
  scanSuccess, 
  webcamRef, 
  webcamKey,
  onClose, 
  onScan, 
  onStartReceiving 
}) => {
  const { showToast } = useToast();
  const modalRef = useModalClose(true, onClose);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [manualSSCC, setManualSSCC] = useState('');
  const [processingManual, setProcessingManual] = useState(false);
  const [cameraInitializing, setCameraInitializing] = useState(true);
  const [localIsScanning, setLocalIsScanning] = useState(false);
  const [localScanError, setLocalScanError] = useState('');
  const [localScanSuccess, setLocalScanSuccess] = useState('');

  if (!po) return null;

  // Watch for scan state changes and show toasts
  React.useEffect(() => {
    if (scanSuccess && scanSuccess.trim()) {
      showToast(scanSuccess, 'success');
    }
  }, [scanSuccess]);

  React.useEffect(() => {
    if (scanError && scanError.trim()) {
      showToast(scanError, 'error');
    }
  }, [scanError]);

  const handleUserMedia = (stream) => {
    console.log('Camera ready - stream active:', stream.active);
    setCameraReady(true);
    setCameraError('');
    setCameraInitializing(false);
    
    // Validate stream tracks
    const videoTracks = stream.getVideoTracks();
    if (videoTracks.length > 0) {
      console.log('Video track ready:', videoTracks[0].readyState, videoTracks[0].getSettings());
    }
  };

  const handleUserMediaError = (error) => {
    console.error('Camera initialization error:', error);
    setCameraReady(false);
    setCameraInitializing(false);
    
    let errorMessage = 'Failed to access camera. ';
    
    if (error.name === 'NotAllowedError') {
      errorMessage += 'Camera permission denied. Please allow camera access and refresh the page.';
    } else if (error.name === 'NotFoundError') {
      errorMessage += 'No camera found on this device.';
    } else if (error.name === 'NotReadableError') {
      errorMessage += 'Camera is already in use by another application.';
    } else if (error.name === 'OverconstrainedError') {
      errorMessage += 'Camera settings not supported. Please try a different device.';
    } else {
      errorMessage += 'Please check camera permissions and try refreshing the page.';
    }
    
    setCameraError(errorMessage);
  };

  // Reset ALL camera states when webcamKey changes (complete reset)
  React.useEffect(() => {
    console.log('Webcam key changed - COMPLETE camera state reset:', webcamKey);
    setCameraReady(false);
    setCameraError('');
    setCameraInitializing(true);
    setManualSSCC(''); // Also reset manual input
  }, [webcamKey]);

  // Manual SSCC entry that integrates with parent component
  const handleManualEntry = async () => {
    if (!manualSSCC.trim() || processingManual) {
      console.log('Manual entry validation failed:', { manualSSCC: manualSSCC.trim(), processingManual, receivingSession });
      return;
    }
    
    setProcessingManual(true);
    try {
      console.log('Manual SSCC entry:', manualSSCC.trim());
      
      // Call the parent onScan function with the manual code
      await onScan(manualSSCC.trim());
      setManualSSCC('');
    } catch (error) {
      console.error('Manual entry error:', error);
    } finally {
      setProcessingManual(false);
    }
  };

  // Camera scanning with mobile-compatible barcode detection
  const handleCameraScan = async () => {
    if (!webcamRef.current || !receivingSession || !cameraReady) return;
    
    try {
      console.log('Starting mobile-compatible camera barcode scan');
      setLocalIsScanning(true);
      setLocalScanError('');
      setLocalScanSuccess('');
      
      // Capture image from webcam
      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) {
        setLocalScanError('Failed to capture camera image');
        return;
      }

      console.log('Image captured, trying mobile-friendly barcode detection...');

      // Method 1: Try ZXing library (excellent mobile support)
      try {
        const codeReader = new BrowserMultiFormatReader();
        const img = new Image();
        
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = imageSrc;
        });

        // Create canvas to process image
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const result = await codeReader.decodeFromImageData(imageData);
        
        if (result) {
          const detectedCode = result.getText();
          console.log('✅ ZXing detected barcode:', detectedCode);
          await onScan(detectedCode);
          setLocalScanSuccess(`✅ Scanned: ${detectedCode}`);
          return;
        }
      } catch (zxingError) {
        console.log('ZXing detection failed, trying Quagga...', zxingError.message);
      }

      // Method 2: Try Quagga (good mobile support)
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = imageSrc;
        });

        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        await new Promise((resolve, reject) => {
          Quagga.decodeSingle({
            decoder: {
              readers: ['code_128_reader', 'code_39_reader', 'ean_reader', 'ean_8_reader']
            },
            locate: true,
            src: imageSrc
          }, (result) => {
            if (result && result.codeResult) {
              console.log('✅ Quagga detected barcode:', result.codeResult.code);
              onScan(result.codeResult.code);
              setLocalScanSuccess(`✅ Scanned: ${result.codeResult.code}`);
              resolve(true);
            } else {
              reject(new Error('No barcode detected by Quagga'));
            }
          });
        });
        return;
      } catch (quaggaError) {
        console.log('Quagga detection failed:', quaggaError.message);
      }

      // Method 3: Native BarcodeDetector (fallback for supported browsers)
      if ('BarcodeDetector' in window) {
        try {
          const img = new Image();
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = imageSrc;
          });

          const barcodeDetector = new window.BarcodeDetector({
            formats: ['code_128', 'code_39', 'ean_13', 'ean_8', 'codabar']
          });

          const barcodes = await barcodeDetector.detect(img);
          
          if (barcodes && barcodes.length > 0) {
            const detectedCode = barcodes[0].rawValue;
            console.log('✅ Native BarcodeDetector detected:', detectedCode);
            await onScan(detectedCode);
            setLocalScanSuccess(`✅ Scanned: ${detectedCode}`);
            return;
          }
        } catch (detectionError) {
          console.log('Native BarcodeDetector failed:', detectionError.message);
        }
      }

      // If all methods fail, show helpful error
      setLocalScanError('Could not detect barcode. Try better lighting, closer distance, or use manual entry below.');
      
    } catch (error) {
      console.error('Camera scan error:', error);
      setLocalScanError('Camera scan error: ' + error.message);
    } finally {
      setLocalIsScanning(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleManualEntry();
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div ref={modalRef} className="relative top-4 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Modal Header */}
          <div className="flex justify-between items-center mb-1">
            <h3 className="text-xl font-medium text-gray-900">
              Receiving: PO {po.po_number}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          {/* Progress Section */}
          {receivingStatus && receivingStatus.has_session && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              {/* <h4 className="font-semibold text-blue-900 mb-2">Receiving Progress</h4> */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-700">
                  Scanned: {receivingStatus.progress?.scanned_count || 0} / {receivingStatus.progress?.expected_count || 0} SSCCs
                </span>
                <span className="text-sm text-blue-600">
                  {receivingStatus.progress?.percentage || 0}% Complete
                </span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                  style={{ width: `${receivingStatus.progress?.percentage || 0}%` }}
                ></div>
              </div>
              
              {/* Expected SSCCs Checklist */}
              <div className="mt-4">
                <h5 className="text-sm font-medium text-blue-900 mb-2">Expected SSCCs:</h5>
                <div className="grid grid-cols-1 gap-1 h-fit overflow-y-auto">
                  {(receivingStatus.checklist || []).map((item, index) => {
                    return (
                      <div key={index} className={`flex items-center space-x-2 px-2 py-1 rounded text-sm ${
                        item.scanned ? 'bg-green-100 text-green-800' : 'bg-white text-gray-700'
                      }`}>
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                          item.scanned ? 'bg-green-500 border-green-500' : 'border-gray-300'
                        }`}>
                          {item.scanned && (
                            <Check size={12} className="text-white" />
                          )}
                        </div>
                        <span className="font-mono text-sm">{item.sscc_hri}</span>
                        {item.scanned && (
                          <span className="text-xs text-green-600">
                            ✓ Received {item.scanned_at ? new Date(item.scanned_at).toLocaleTimeString() : ''}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          

          {/* Camera Section */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-medium text-gray-900">Camera Barcode Scanner</h4>
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${
                  cameraReady ? 'bg-green-500' : 
                  cameraInitializing ? 'bg-yellow-500' : 
                  'bg-red-500'
                }`}></div>
                <span className="text-sm text-gray-600">
                  {cameraReady ? 'Camera Ready' : 
                   cameraInitializing ? 'Camera Initializing...' : 
                   'Camera Error'}
                </span>
              </div>
            </div>
            
            
            {/* Camera Error */}
            {cameraError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <span className="text-red-800 text-sm">{cameraError}</span>
              </div>
            )}
            
            {/* Camera */}
            <div className="relative bg-black rounded-lg overflow-hidden mb-4" style={{ aspectRatio: '4/3' }}>
              <Webcam
                key={webcamKey} // Force remount when key changes
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                screenshotQuality={0.8}
                className="w-full h-full object-cover"
                onUserMedia={handleUserMedia}
                onUserMediaError={handleUserMediaError}
                videoConstraints={{
                  facingMode: 'environment', // Use back camera on mobile
                  width: { ideal: 1280, max: 1920 },
                  height: { ideal: 720, max: 1080 },
                  frameRate: { ideal: 30, max: 60 }
                }}
              />
              
              {/* Camera Loading Overlay */}
              {cameraInitializing && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                  <div className="text-white text-sm font-medium bg-black bg-opacity-50 px-4 py-2 rounded">
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Initializing camera...</span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Scanning Overlay - only show when camera is ready */}
              {cameraReady && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="border-2 border-green-400 w-64 h-40 rounded-lg relative">
                    <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-green-400"></div>
                    <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-green-400"></div>
                    <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-green-400"></div>
                    <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-green-400"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-green-400 text-xs font-medium bg-black bg-opacity-50 px-2 py-1 rounded text-center">
                        Position SSCC-18 barcode here<br />
                        <span className="text-xs opacity-75">Sample: (00)123456789012345678</span>
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Scan Button */}
            <div className="flex flex-col items-center space-y-2">
              <button
                onClick={handleCameraScan}
                disabled={localIsScanning || !receivingSession || !cameraReady}
                className={`px-8 py-3 rounded-lg font-medium transition-all ${
                  localIsScanning || !receivingSession || !cameraReady
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl'
                }`}
              >
                {localIsScanning ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Scanning...</span>
                  </div>
                ) : (
                  'Scan SSCC-18 Barcode'
                )}
              </button>
              
              {!cameraReady && !cameraError && (
                <p className="text-xs text-gray-500 text-center">
                  Waiting for camera to initialize...
                </p>
              )}
              
              {cameraReady && (
                <p className="text-xs text-green-600 text-center">
                  ✓ Camera ready - Position barcode in green box above
                </p>
              )}
            </div>
          </div>

          {/* Manual Entry Section (Priority - Easy Testing) */}
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h5 className="font-medium text-blue-900 mb-2">Manual SSCC Entry</h5>
            <div className="flex space-x-2">
              <input
                type="text"
                placeholder="Enter SSCC-18 (e.g., 001975814411583661)"
                value={manualSSCC}
                onChange={(e) => setManualSSCC(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1 px-3 py-2 border border-blue-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                maxLength="22"
                disabled={processingManual}
              />
              <button
                onClick={handleManualEntry}
                disabled={!manualSSCC.trim() || processingManual}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  !manualSSCC.trim() || processingManual
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {processingManual ? 'Processing...' : 'Submit'}
              </button>
            </div>
          </div>

          {/* Messages */}
          {(scanError || localScanError) && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-red-800 text-sm">{scanError || localScanError}</span>
              </div>
            </div>
          )}

          {(scanSuccess || localScanSuccess) && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-green-800 text-sm">{scanSuccess || localScanSuccess}</span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;